<template>
  <div class="cand-page">
    <div class="label">
      <div class="onboarding-heading">
        <span class="eyebrow">Catalog automation</span>
        <h4>Onboard new parts</h4>
      </div>
      <div class="label-actions">
        <div class="page-views" role="tablist" aria-label="Page view">
          <button
            v-for="view in pageViews"
            :key="view.key"
            type="button"
            role="tab"
            :class="{ active: pageView === view.key }"
            :aria-selected="pageView === view.key ? 'true' : 'false'"
            :title="view.hint"
            @click="setPageView(view.key)">{{ view.label }}</button>
        </div>
        <button type="button" class="btn btn-outline-primary page-refresh" :disabled="refreshingPage" @click="refreshPage()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5"/><path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9"/></svg>
          {{ refreshingPage ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>
    <p class="intro">From discovery to your catalog. Track every part on the board, or open Stages to work through each step.</p>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <onboarding-board v-if="pageView === 'board'" ref="onboardingBoard" @open-candidate="openCandidateById" />

    <!-- Stages: the working view. Kept mounted (v-show) so its runs and scope stay live. -->
    <div v-show="pageView === 'stages'">
    <section class="stage-tabs">
      <button
        v-for="stage in stages"
        :key="stage.key"
        type="button"
        class="stage-tab"
        :class="{ active: stageKey === stage.key }"
        @click="setStage(stage.key)">
        <span class="stage-number">{{ stage.number }}</span>
        <span class="stage-tab-copy">
          <strong>{{ stage.shortTitle }}</strong>
          <small>{{ stageStatusLine(stage) }}</small>
        </span>
        <span class="stage-tab-count">{{ stageCount(stage) }}</span>
      </button>
    </section>

    <section class="stage-workbench">
      <div class="stage-workbench-head">
        <div class="stage-workbench-main">
          <span class="stage-kicker">Stage {{ activeStage.number }}</span>
          <h5>{{ activeStage.title }}</h5>
          <p>{{ activeStage.description }}</p>
        </div>
        <div class="stage-actions-row">
          <button
            v-for="action in headerStageActions"
            :key="stageActionKey(action)"
            class="btn-ghost btn-ghost-primary stage-run-btn"
            :disabled="stageBusy || !stageActionRunnable(activeStage, action)"
            :title="stageActionBlockedReason(action)"
            @click="runStageAction(action)">
            {{ stageBusy === stageActionKey(action) ? 'Starting...' : action.cta }}
          </button>
          <button
            v-if="canBulkRefreshPrices"
            class="btn-ghost price-refresh-bulk"
            :disabled="bulkPriceBusy"
            @click="refreshMissingPrices()">
            {{ bulkPriceBusy ? 'Starting...' : 'Refresh missing prices' }}
          </button>
        </div>
      </div>
      <div v-if="stageMissingNotes.length" class="stage-note-list">
        <div v-for="note in stageMissingNotes" :key="note" class="stage-note">{{ note }}</div>
      </div>
      <div v-else-if="!activeStageActions.length && !canBulkRefreshPrices" class="stage-note">{{ stageJobMissingNote(activeStage) }}</div>
      <div v-if="activeStageScopeAction && activeStage.key !== 'onboarding'" class="stage-scope-card" :class="{ loading: stageScopeLoading }">
        <div class="stage-scope-main">
          <span class="stage-scope-kicker">Run scope</span>
          <strong>{{ stageScopeTitle }}</strong>
          <small>{{ stageScopeDescription }}</small>
        </div>
        <div class="stage-scope-stat">
          <span>{{ stageScopeLoading ? '...' : stageScopeCount }}</span>
          <small>{{ stageScopeCountLabel }}</small>
        </div>
        <div v-if="stageScopeCounts.length" class="stage-scope-counts">
          <span v-for="row in stageScopeCounts" :key="row.key">{{ row.label }} {{ row.value }}</span>
        </div>
        <div v-if="stageScopeError" class="stage-scope-error">{{ stageScopeError }}</div>
      </div>

      <!-- Stage 1: one intake panel — three ways a part gets in — over the request queue -->
      <div v-if="activeStage.key === 'onboarding'" class="intake">
        <div class="intake-head">
          <select
            v-model="typeFilter"
            class="intake-type"
            :class="{ 'needs-type': intakeNeedsType }"
            aria-label="Part type"
            @change="refreshCandidates()">
            <option value="all">All types</option>
            <option value="cpu">CPU</option>
            <option value="gpu">GPU</option>
            <option value="motherboard">Motherboard</option>
            <option value="ram">RAM</option>
            <option value="storage">Storage</option>
            <option value="case">Case</option>
            <option value="psu">PSU</option>
          </select>
          <div class="intake-tabs" role="tablist" aria-label="Ways to add parts">
            <button
              v-for="mode in intakeModes"
              :key="mode.key"
              type="button"
              role="tab"
              class="intake-tab"
              :class="{ active: intakeMode === mode.key }"
              :aria-selected="intakeMode === mode.key ? 'true' : 'false'"
              :title="mode.hint"
              @click="setIntakeMode(mode.key)">{{ mode.label }}</button>
          </div>
        </div>

        <!-- Add a part: names, UPCs, ASINs and Amazon links in one box -->
        <div v-if="intakeMode === 'add'" class="intake-pane">
          <div class="intake-row">
            <textarea
              v-model="requests.text"
              class="intake-input"
              :rows="intakeRows"
              :disabled="requests.saving"
              placeholder="Part name, UPC, ASIN or Amazon link"
              @keydown.enter.exact.prevent="submitRequests(true)"></textarea>
            <button
              type="button"
              class="btn-ghost btn-ghost-primary intake-btn"
              :disabled="!canAddRequests"
              title="Search Amazon for it (an ASIN skips the search), then scrape it into Review"
              @click="submitRequests(true)">
              {{ requests.saving && requests.onboarding ? 'Starting…' : 'Onboard now' }}
            </button>
            <button
              type="button"
              class="btn-ghost intake-btn"
              :disabled="!canAddRequests"
              title="Put it in the queue for later"
              @click="submitRequests(false)">
              {{ requests.saving && !requests.onboarding ? 'Adding…' : 'Add to queue' }}
            </button>
          </div>
          <p class="intake-line">
            <template v-if="intakePreview.chips.length && !intakeNeedsType">
              <span
                v-for="(chip, i) in intakePreview.chips"
                :key="'det' + i"
                class="intake-chip"
                :class="'kind-' + chip.kind">{{ chip.label }}</span>
              <span v-if="intakePreview.more" class="intake-chip">+{{ intakePreview.more }} more</span>
            </template>
            <span v-else :class="{ 'intake-warn': intakeNeedsType }">{{ intakeHint }}</span>
          </p>
          <p v-if="requests.addError" class="intake-error">{{ requests.addError }}</p>
          <ul v-if="requests.skipped.length" class="intake-skipped">
            <li v-for="(item, i) in requests.skipped" :key="'sk' + i"><em>{{ item.line }}</em> — {{ item.reason }}</li>
          </ul>
        </div>

        <!-- Suggest with AI: popular or important parts the catalog lacks -->
        <div v-else-if="intakeMode === 'suggest'" class="intake-pane">
          <div class="intake-row">
            <input
              v-model="suggest.note"
              class="intake-input"
              type="text"
              maxlength="300"
              placeholder="Focus (optional) — e.g. budget builds, white builds, PCIe 5.0"
              @keydown.enter.prevent="runSuggest()">
            <select v-model.number="suggest.count" class="intake-count" aria-label="How many suggestions">
              <option :value="5">5 ideas</option>
              <option :value="10">10 ideas</option>
              <option :value="20">20 ideas</option>
            </select>
            <button
              type="button"
              class="btn-ghost btn-ghost-primary intake-btn"
              :disabled="!canSuggest"
              @click="runSuggest()">
              {{ suggest.running ? 'Suggesting…' : 'Suggest' }}
            </button>
          </div>
          <!-- Live while it runs; afterwards one line, with the story behind a toggle.
               The picks themselves land in the queue below — one list. -->
          <p v-if="suggest.error" class="intake-error">{{ suggest.error }}</p>
          <p v-if="suggest.running && !suggest.activity.length" class="intake-line">
            <span class="discover-spinner" aria-hidden="true"></span>
            Starting — looking for popular {{ typePlural(suggest.type) }} we do not carry.
          </p>
          <p v-else-if="suggest.loaded" class="intake-line">
            <strong class="intake-summary">{{ suggestHeadline }}</strong>
            <span>{{ suggestSubline }}</span>
            <button
              v-if="suggest.activity.length"
              type="button"
              class="intake-link"
              @click="suggest.feedOpen = !suggest.feedOpen">{{ suggest.feedOpen ? 'Hide how it got there' : 'How it got there' }}</button>
          </p>
          <job-activity-feed
            v-if="suggest.activity.length && (suggest.running || suggest.feedOpen)"
            class="intake-feed"
            :items="suggest.activity" />
          <p v-if="!suggest.running && !suggest.loaded && !suggest.error" class="intake-line" :class="{ 'intake-warn': intakeNeedsType }">{{ suggestIdleHint }}</p>
        </div>

        <!-- Coverage gaps: the compatibility-graph planner (Discover -> review -> Onboard) -->
        <div v-else class="intake-pane">
          <div class="intake-row intake-row-fields">
            <label class="stage-field stage-field-quantity">
              <span>Max directives</span>
              <input v-model.number="stageOptions.count" type="number" min="1" max="50" step="1">
            </label>
            <label class="stage-field">
              <span>Scope</span>
              <select v-model="stageOptions.onboardScope" @change="scheduleScopeFetch()">
                <option value="active">Active coverage</option>
                <option value="all">All targets</option>
              </select>
            </label>
            <label class="stage-field stage-field-quantity">
              <span>Min count</span>
              <input
                v-model.number="stageOptions.minCount"
                type="number"
                min="0"
                step="1"
                placeholder="default"
                @change="scheduleScopeFetch()">
            </label>
            <button
              type="button"
              class="btn-ghost btn-ghost-primary intake-btn discover-btn"
              :disabled="discover.running || discover.committing"
              @click="runDiscover()">
              {{ discover.running ? 'Discovering…' : 'Discover' }}
            </button>
          </div>
          <div v-if="discover.running" class="discover-live">
            <div class="discover-status">
              <span class="discover-spinner" aria-hidden="true"></span>
              <div>
                <strong>Discovering onboarding candidates…</strong>
                <small>Running targeted Amazon searches. Every proposal is saved to the search ledger as it arrives.</small>
              </div>
            </div>
            <div class="discover-metrics">
              <span><strong>{{ discoverMetric('proposed') }}</strong> proposed</span>
              <span><strong>{{ discoverMetric('checked') }}</strong> checked</span>
              <span><strong>{{ discoverMetric('available') }}</strong> available</span>
              <span><strong>{{ discoverMetric('rejected') }}</strong> rejected</span>
              <span><strong>{{ discover.candidates.length }}</strong> previews ready</span>
            </div>
            <small class="discover-live-copy">{{ discoverProgressText() }}</small>
            <button v-if="discover.ledger.rounds.length || discover.ledger.results.length" type="button" class="discover-plan-toggle" @click="discover.resultsOpen = !discover.resultsOpen">
              {{ discover.resultsOpen ? 'Hide search ledger' : 'Show search ledger' }}
            </button>
            <ul v-if="discover.resultsOpen" class="discover-ledger-list">
              <template v-if="!discover.ledger.results.length">
                <li v-for="round in discover.ledger.rounds" :key="'running-search-round-' + round.id" class="discover-ledger-row">
                  <span class="discover-ledger-status" :class="'is-' + round.status">{{ statusLabel(round.status) }}</span>
                  <span class="discover-ledger-product">
                    <strong>{{ onboardTypeLabel(round.partType) }} · round {{ round.roundNumber }}</strong>
                    <small>{{ round.proposedCount || 0 }} proposed · {{ round.checkedCount || 0 }} checked<template v-if="round.error"> · {{ round.error }}</template></small>
                  </span>
                </li>
              </template>
              <li v-for="result in discover.ledger.results" :key="'search-result-' + result.id" class="discover-ledger-row">
                <span class="discover-ledger-status" :class="'is-' + result.status">{{ statusLabel(result.status) }}</span>
                <span class="discover-ledger-product">
                  <strong>{{ result.title || result.model || result.asin || 'Unstructured result' }}</strong>
                  <small>{{ result.asin || 'No valid ASIN' }}<template v-if="result.reason"> · {{ result.reason }}</template></small>
                </span>
              </li>
            </ul>
          </div>
          <div v-else-if="discover.error" class="discover-error-state">
            <div class="discover-error">{{ discover.error }}</div>
            <div v-if="discover.ledger.rounds.length || discover.ledger.results.length" class="discover-ledger-summary">
              <div class="discover-metrics">
                <span><strong>{{ discoverMetric('proposed') }}</strong> proposed</span>
                <span><strong>{{ discoverMetric('checked') }}</strong> checked</span>
                <span><strong>{{ discoverMetric('available') }}</strong> available</span>
                <span><strong>{{ discoverMetric('rejected') }}</strong> rejected</span>
              </div>
              <button type="button" class="discover-plan-toggle" @click="discover.resultsOpen = !discover.resultsOpen">
                {{ discover.resultsOpen ? 'Hide search ledger' : 'Show search ledger' }}
              </button>
              <ul v-if="discover.resultsOpen" class="discover-ledger-list">
                <li v-for="round in discover.ledger.rounds" :key="'failed-search-round-' + round.id" class="discover-ledger-row">
                  <span class="discover-ledger-status" :class="'is-' + round.status">{{ statusLabel(round.status) }}</span>
                  <span class="discover-ledger-product">
                    <strong>{{ onboardTypeLabel(round.partType) }} · round {{ round.roundNumber }}</strong>
                    <small>{{ round.proposedCount || 0 }} proposed · {{ round.checkedCount || 0 }} checked<template v-if="round.error"> · {{ round.error }}</template></small>
                  </span>
                </li>
                <li v-for="result in discover.ledger.results" :key="'failed-search-result-' + result.id" class="discover-ledger-row">
                  <span class="discover-ledger-status" :class="'is-' + result.status">{{ statusLabel(result.status) }}</span>
                  <span class="discover-ledger-product">
                    <strong>{{ result.title || result.model || result.asin || 'Unstructured result' }}</strong>
                    <small>{{ result.asin || 'No valid ASIN' }}<template v-if="result.reason"> · {{ result.reason }}</template></small>
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <template v-else-if="discover.loaded">
            <!-- Scope summary on top -->
            <div class="discover-scope">
              <div class="discover-scope-main">
                <span class="discover-scope-kicker">Onboarding scope</span>
                <strong>{{ discover.candidates.length }} candidate{{ discover.candidates.length === 1 ? '' : 's' }} to review</strong>
                <small>{{ discoverScopeNum('onboardGaps') }} gap{{ discoverScopeNum('onboardGaps') === 1 ? '' : 's' }} to onboard · {{ discoverScopeNum('retireCount') }} flagged to retire</small>
              </div>
              <button v-if="discover.directives.length" type="button" class="discover-plan-toggle" @click="discover.planOpen = !discover.planOpen">
                {{ discover.planOpen ? 'Hide plan' : 'Show plan' }}
              </button>
            </div>
            <div v-if="discover.warning" class="discover-warning">{{ discover.warning }}</div>
            <div v-if="discover.ledger.rounds.length || discover.ledger.results.length" class="discover-ledger-summary">
              <div class="discover-metrics">
                <span><strong>{{ discoverMetric('proposed') }}</strong> proposed</span>
                <span><strong>{{ discoverMetric('checked') }}</strong> checked</span>
                <span><strong>{{ discoverMetric('available') }}</strong> available</span>
                <span><strong>{{ discoverMetric('rejected') }}</strong> rejected</span>
              </div>
              <button type="button" class="discover-plan-toggle" @click="discover.resultsOpen = !discover.resultsOpen">
                {{ discover.resultsOpen ? 'Hide search ledger' : 'Show search ledger' }}
              </button>
              <ul v-if="discover.resultsOpen" class="discover-ledger-list">
                <template v-if="!discover.ledger.results.length">
                  <li v-for="round in discover.ledger.rounds" :key="'finished-search-round-' + round.id" class="discover-ledger-row">
                    <span class="discover-ledger-status" :class="'is-' + round.status">{{ statusLabel(round.status) }}</span>
                    <span class="discover-ledger-product">
                      <strong>{{ onboardTypeLabel(round.partType) }} · round {{ round.roundNumber }}</strong>
                      <small>{{ round.proposedCount || 0 }} proposed · {{ round.checkedCount || 0 }} checked<template v-if="round.error"> · {{ round.error }}</template></small>
                    </span>
                  </li>
                </template>
                <li v-for="result in discover.ledger.results" :key="'finished-search-result-' + result.id" class="discover-ledger-row">
                  <span class="discover-ledger-status" :class="'is-' + result.status">{{ statusLabel(result.status) }}</span>
                  <span class="discover-ledger-product">
                    <strong>{{ result.title || result.model || result.asin || 'Unstructured result' }}</strong>
                    <small>{{ result.asin || 'No valid ASIN' }}<template v-if="result.reason"> · {{ result.reason }}</template></small>
                  </span>
                </li>
              </ul>
            </div>
            <ul v-if="discover.planOpen && discover.directives.length" class="onboard-plan-list">
              <li v-for="(dir, i) in discover.directives" :key="'dir' + i" class="onboard-plan-row">
                <span class="onboard-plan-tier" :class="'tier-' + onboardPlanRow(dir).tier">{{ onboardPlanRow(dir).tierLabel }}</span>
                <span class="onboard-plan-gap">
                  <strong>{{ onboardPlanRow(dir).gap }}</strong>
                  <small v-if="onboardPlanRow(dir).reason">{{ onboardPlanRow(dir).reason }}</small>
                </span>
                <span v-if="onboardPlanRow(dir).count !== null" class="onboard-plan-count">{{ onboardPlanRow(dir).count }}</span>
              </li>
            </ul>

            <!-- Cleanup group (retire directives) -->
            <div v-if="discover.cleanup.length" class="cleanup-group">
              <button type="button" class="cleanup-toggle" @click="discover.cleanupOpen = !discover.cleanupOpen">
                <span class="cleanup-caret" :class="{ open: discover.cleanupOpen }">▸</span>
                Cleanup — {{ discover.cleanup.length }} flagged to retire
              </button>
              <ul v-if="discover.cleanupOpen" class="cleanup-list">
                <li v-for="(r, i) in discover.cleanup" :key="'cl' + i" class="cleanup-row">
                  <span class="cleanup-type">{{ onboardTypeLabel(r.part_type) }}</span>
                  <span class="cleanup-reason">{{ r.reason }}</span>
                  <span v-if="r.count != null" class="cleanup-count">{{ r.count }}</span>
                </li>
              </ul>
            </div>

            <!-- One-by-one review deck -->
            <div v-if="discover.candidates.length" class="review-deck">
              <div class="review-deck-head">
                <span>Review {{ discover.index + 1 }} of {{ discover.candidates.length }}</span>
                <span class="review-progress"><span :style="{ width: ((discover.index + 1) / discover.candidates.length * 100) + '%' }"></span></span>
              </div>
              <div
                v-if="currentPreview"
                class="review-card"
                :class="{ 'is-duplicate': currentPreview.duplicate, 'is-accept': previewDecision(currentPreview) === 'accept', 'is-reject': previewDecision(currentPreview) === 'reject' }">
                <div class="review-thumb" v-if="currentPreview.image" :style="{ backgroundImage: 'url(' + currentPreview.image + ')' }"></div>
                <div class="review-thumb review-thumb--empty" v-else><span class="icon icon-tools"></span></div>
                <div class="review-body">
                  <div class="review-name-row">
                    <span class="type-badge">{{ onboardTypeLabel(currentPreview.part_type) }}</span>
                    <strong>{{ currentPreview.name }}</strong>
                    <span v-if="currentPreview.duplicate" class="review-dup-badge">Already in catalog</span>
                  </div>
                  <div class="review-meta">
                    <span v-if="currentPreview.gap" class="review-gap">Fills: {{ currentPreview.gap }}</span>
                    <span v-if="currentPreview.price != null" class="review-price">{{ currentPreview.currency || '$' }}{{ currentPreview.price }}</span>
                    <span v-else class="review-price muted">No price yet</span>
                    <a v-if="currentPreview.sourceUrl" :href="currentPreview.sourceUrl" target="_blank" rel="noopener" class="review-source">Source ↗</a>
                    <span v-if="currentPreview.asin" class="review-asin">ASIN {{ currentPreview.asin }}</span>
                  </div>
                  <span v-if="previewDecision(currentPreview)" class="review-decision" :class="previewDecision(currentPreview)">
                    {{ previewDecision(currentPreview) === 'accept' ? 'Marked to onboard' : 'Rejected' }}
                  </span>
                </div>
              </div>
              <div class="review-actions">
                <button type="button" class="btn-ghost" :disabled="discover.index === 0" @click="prevPreview()">‹ Prev</button>
                <button type="button" class="review-reject" @click="decidePreview('reject')">Reject</button>
                <button type="button" class="btn-ghost" @click="skipPreview()">Skip ›</button>
                <button
                  type="button"
                  class="review-accept"
                  :disabled="currentPreview && currentPreview.duplicate"
                  @click="decidePreview('accept')">Onboard ✓</button>
              </div>
            </div>
            <div v-else class="onboard-plan-empty">
              <strong>Nothing to onboard</strong>
              <small>No candidates were found for this scope. Lower <em>Min count</em> or set <em>Scope</em> to “All targets”, then Discover again.</small>
            </div>

            <div v-if="discover.candidates.length" class="review-commit">
              <span>{{ acceptedPreviewIds.length }} selected to onboard</span>
              <button
                type="button"
                class="btn-ghost btn-ghost-primary"
                :disabled="!acceptedPreviewIds.length || discover.committing"
                @click="commitOnboard()">
                {{ discover.committing ? 'Onboarding…' : 'Onboard ' + acceptedPreviewIds.length + ' & scrape' }}
              </button>
            </div>
          </template>
          <p v-else class="intake-line">Finds the parts missing for valid, varied builds, then lets you review each one before it is onboarded.</p>
        </div>
      </div>

      <!-- Request queue: everything asked for, whichever way it came in -->
      <section v-if="activeStage.key === 'onboarding' && showRequestQueue" class="queue">
        <div class="queue-head">
          <strong>Queue</strong>
          <small>{{ requestSummaryLine }}</small>
          <div class="requests-filter" role="group" aria-label="Which requests to show">
            <button
              v-for="filter in requestFilters"
              :key="filter.key"
              type="button"
              :class="{ active: requests.filter === filter.key }"
              @click="setRequestFilter(filter.key)">{{ filter.label }}</button>
          </div>
          <button
            v-if="triageIds.length"
            type="button"
            class="btn-ghost"
            :disabled="requests.bulkBusy"
            title="Move every request waiting for a decision into the queue"
            @click="queueTriage()">
            Queue {{ triageIds.length }} to triage
          </button>
          <button
            v-if="requestsAction"
            type="button"
            class="btn-ghost btn-ghost-primary"
            :disabled="!!stageBusy || !stageActionRunnable(activeStage, requestsAction)"
            :title="stageActionBlockedReason(requestsAction) || 'Search Amazon for the queued parts'"
            @click="runStageAction(requestsAction)">
            {{ stageBusy === stageActionKey(requestsAction) ? 'Starting…' : requestsActionLabel }}
          </button>
        </div>
        <p v-if="requests.error" class="intake-error">{{ requests.error }}</p>
        <ul v-if="requests.rows.length" class="requests-list">
          <part-request-row
            v-for="row in requests.rows"
            :key="row.id"
            :row="row"
            :busy="requests.busyId === row.id || requests.bulkBusy"
            :live="rowLive(row.id)"
            :fresh="!!requests.fresh[row.id]"
            @onboard="onboardRequests([row.id])"
            @status="(status) => setRequestStatus(row, status)" />
        </ul>
        <p v-else-if="!requests.loading" class="intake-line">
          Nothing waiting.
          <button v-if="requests.filter === 'open'" type="button" class="intake-link" @click="setRequestFilter('all')">Show all requests</button>
        </p>
      </section>

      <div v-if="stageRun" class="run-card" :class="'run-' + runStatus(stageRun)">
        <span class="run-dot"></span>
        <div>
          <strong>{{ stageRun.label }}</strong>
          <small>Run #{{ runId(stageRun) || 'pending' }} - {{ runStatusLabel(stageRun) }}</small>
          <small v-if="runScopeLabel(stageRun)" class="run-scope">{{ runScopeLabel(stageRun) }}</small>
          <small v-if="runAiOutcome(stageRun).label" class="run-ai-outcome" :class="'tone-' + runAiOutcome(stageRun).tone">
            {{ runAiOutcome(stageRun).label }}
          </small>
          <small v-if="runAiOutcome(stageRun).preview" class="run-ai-preview">
            {{ runAiOutcome(stageRun).preview }}
          </small>
        </div>
        <span v-if="runSummary(stageRun)" class="run-summary">{{ runSummary(stageRun) }}</span>
        <span v-if="onboardRunProgress(stageRun).length" class="run-progress">
          <span v-for="p in onboardRunProgress(stageRun)" :key="p.label" class="run-progress-chip">
            <small>{{ p.label }}</small>{{ p.value }}
          </span>
        </span>
        <button
          v-if="stageRunEmptyAmazonSearch(stageRun)"
          type="button"
          class="run-fallback-btn"
          :disabled="stageBusy"
          @click="runDiscoverFallback()">
          Try Discover new parts
        </button>
      </div>
    </section>

    <!-- Candidate list -->
    <div class="candidate-table-shell">
      <LoadingOverlay v-if="loading && candidates.length" compact />
      <div class="candidate-table-head">
        <span>Candidate</span>
        <span>Status</span>
        <span>Price</span>
        <span>Source</span>
        <span>Added</span>
        <span>Attempts</span>
        <span>Missing / Issues</span>
      </div>
      <article
        v-for="c in candidates"
        :key="c.id"
        class="candidate-table-row"
        :class="{ 'missing-price': !candidatePrice(c).ok, 'missing-source': !sourceUrl(c), 'has-blockers': blockerCount(c) }"
        role="button"
        tabindex="0"
        @click="openDetail(c)"
        @keydown.enter="openDetail(c)"
        @keydown.space.prevent="openDetail(c)">
        <div class="candidate-product-cell">
          <div class="cand-thumb" v-if="c.raw_product && c.raw_product.mainImg"
               :style="{ backgroundImage: 'url(' + c.raw_product.mainImg + ')' }"></div>
          <div class="cand-thumb cand-thumb--empty" v-else><span class="icon icon-tools"></span></div>
          <div class="candidate-product-copy">
            <div class="candidate-name-row">
              <span class="type-badge">{{ c.part_type }}</span>
              <strong>{{ candidateName(c) }}</strong>
            </div>
            <span
              v-if="onboardBadge(c)"
              class="onboard-badge"
              :class="'tier-' + onboardBadge(c).tier"
              :title="onboardBadge(c).intent || onboardBadge(c).text">
              <span class="onboard-spark">◆</span>
              {{ onboardBadge(c).text }}
            </span>
            <p>{{ c.reason || c.error || 'No review note yet.' }}</p>
            <small v-if="c.asin">ASIN {{ c.asin }}</small>
          </div>
        </div>

        <div class="candidate-table-cell candidate-status-cell">
          <span class="status-pill" :class="'st-' + c.status">{{ statusLabel(c.status) }}</span>
        </div>

        <div class="candidate-table-cell candidate-price-cell">
          <span class="cand-price" :class="{ 'price-missing': !candidatePrice(c).ok }" :title="candidatePrice(c).title">
            {{ candidatePrice(c).label }}
          </span>
          <small v-if="!candidatePrice(c).ok">Needs price review</small>
        </div>

        <div class="candidate-table-cell candidate-source-cell">
          <a v-if="sourceUrl(c)" :href="sourceUrl(c)" target="_blank" rel="noopener noreferrer" @click.stop title="Verified Amazon product page">
            <span class="source-check" aria-hidden="true"></span>
            Amazon
          </a>
          <span v-else class="missing-field">Missing source</span>
        </div>

        <div class="candidate-table-cell candidate-time-cell">
          <span v-if="addedAt(c)" :title="fmtTime(addedAt(c))">{{ fmtRelative(addedAt(c)) }}</span>
          <span v-else class="missing-field">Unknown</span>
          <small v-if="addedAt(c)">{{ fmtDateShort(addedAt(c)) }}</small>
        </div>

        <div class="candidate-table-cell candidate-attempt-cell">
          <strong>{{ c.attempts || 0 }}</strong>
        </div>

        <div class="candidate-table-cell candidate-issue-cell">
          <span
            v-if="candidateMissingFieldCount(c)"
            class="issue-pill issue-pill-missing"
            :title="candidateMissingFieldTitle(c)"
          >
            {{ candidateMissingFieldCount(c) }} missing
          </span>
          <span v-else class="issue-pill issue-pill-clean">Complete</span>
          <small v-if="blockerCount(c)" class="candidate-issue-note note-blocker">
            {{ blockerCount(c) }} blocker{{ blockerCount(c) > 1 ? 's' : '' }}
          </small>
          <small v-else-if="issueCounts(c).total" class="candidate-issue-note">
            {{ issueCounts(c).total }} finding{{ issueCounts(c).total > 1 ? 's' : '' }}
          </small>
        </div>
      </article>
      <div v-if="!loading && !candidates.length" class="empty">No candidates in this stage.</div>
      <LoadingOverlay v-if="loading && !candidates.length" mode="block" compact clear min-height="220px" />
    </div>
    </div>

    <!-- Detail drawer -->
    <div v-if="selected" class="cand-drawer" @click.self="closeDetail()">
      <div class="cand-panel">
        <div class="panel-head">
          <div class="panel-title-wrap">
            <div class="panel-kicker-row">
              <span class="type-badge">{{ selected.part_type }}</span>
              <span class="status-pill" :class="'st-' + selected.status">{{ statusLabel(selected.status) }}</span>
              <span class="candidate-id">#{{ selected.id }}</span>
              <span v-if="selected.asin" class="candidate-id">ASIN {{ selected.asin }}</span>
            </div>
            <h3 class="panel-title">{{ candidateName(selected) }}</h3>
            <div v-if="onboardBadge(selected)" class="onboard-banner" :class="'tier-' + onboardBadge(selected).tier">
              <span class="onboard-spark">◆</span>
              <span class="onboard-banner-main">
                <strong>{{ onboardBadge(selected).text }}</strong>
                <span v-if="onboardBadge(selected).intent">{{ onboardBadge(selected).intent }}</span>
              </span>
            </div>
          </div>
          <a class="close" @click="closeDetail()">✕</a>
        </div>

        <div class="panel-body">
          <!-- Amazon scrape summary -->
          <section class="block candidate-overview-block">
            <div class="candidate-overview">
              <div class="candidate-media">
                <div
                  v-if="selected.raw_product && selected.raw_product.mainImg"
                  class="scrape-thumb"
                  :style="{ backgroundImage: 'url(' + selected.raw_product.mainImg + ')' }"></div>
                <div class="scrape-thumb scrape-thumb--empty" v-else>
                  <span class="icon icon-tools"></span>
                </div>
              </div>

              <div class="candidate-overview-main">
                <span class="section-kicker">Amazon scrape</span>
                <h4>{{ selected.raw_product && selected.raw_product.title ? selected.raw_product.title : candidateName(selected) }}</h4>
                <div class="candidate-meta-line">
                  <span v-if="selected.raw_product && selected.raw_product.brand">{{ selected.raw_product.brand }}</span>
                  <span v-if="selected.asin">ASIN {{ selected.asin }}</span>
                  <span v-if="selected.raw_product && selected.raw_product.availabilityRaw" class="availability-note">{{ selected.raw_product.availabilityRaw }}</span>
                </div>
                <details v-if="scrapeOutcome(selected).show" class="scrape-outcome-card" :class="'tone-' + scrapeOutcome(selected).tone">
                  <summary>
                    <span class="scrape-outcome-label">{{ scrapeOutcome(selected).label }}</span>
                    <small>{{ scrapeOutcome(selected).action }}</small>
                  </summary>
                  <p>{{ scrapeOutcome(selected).message }}</p>
                </details>

                <div v-if="primarySpecChips(selected).length" class="candidate-chip-row">
                  <span v-for="chip in primarySpecChips(selected)" :key="chip.key" class="candidate-chip">
                    <small>{{ chip.label }}</small>
                    <strong>{{ chip.value }}</strong>
                  </span>
                </div>

                <details class="scrape-details" v-if="candidateBullets(selected).length">
                  <summary>Amazon listing highlights <span>{{ candidateBullets(selected).length }}</span></summary>
                  <ul class="bullets">
                    <li v-for="(b, i) in candidateBullets(selected)" :key="i">{{ b }}</li>
                  </ul>
                </details>
              </div>

            </div>
          </section>

          <div class="candidate-review-workspace">
            <aside class="candidate-review-sidebar">
              <section class="candidate-price-card">
                <span class="price-card-label">Current price</span>
                <strong class="price-card-value" :class="{ 'price-missing': !candidatePrice(selected).ok }">
                  {{ candidatePrice(selected).label }}
                </strong>
                <span class="price-card-note">{{ candidatePrice(selected).title }}</span>
                <form class="price-manual-form" @submit.prevent="saveManualPrice">
                  <label>
                    <span>Manual price</span>
                    <input
                      v-model.trim="priceEditValue"
                      type="text"
                      inputmode="decimal"
                      placeholder="$0.00"
                      :disabled="busy">
                  </label>
                  <button
                    type="submit"
                    class="price-save-btn"
                    :disabled="busy || !priceEditValue">
                    {{ busy === 'priceSave' ? 'Saving...' : 'Save price' }}
                  </button>
                </form>
                <div class="price-card-actions">
                  <button
                    type="button"
                    class="price-refresh-btn"
                    :disabled="busy"
                    @click="refreshCandidatePrice(false)">
                    {{ busy === 'price' ? 'Scraping...' : 'Scrape price' }}
                  </button>
                  <button
                    v-if="shouldShowPriceReopen(selected)"
                    type="button"
                    class="price-refresh-btn price-refresh-reopen"
                    :disabled="busy"
                    @click="refreshCandidatePrice(true)">
                    {{ busy === 'priceReopen' ? 'Refreshing...' : 'Refresh & reopen' }}
                  </button>
                  <a v-if="amazonLink(selected.asin)" :href="amazonLink(selected.asin)" target="_blank" rel="noopener noreferrer" class="amzn-link">View on Amazon</a>
                </div>
                <div v-if="priceEditMessage" class="price-refresh-message" :class="'price-refresh-message--' + priceEditKind">
                  {{ priceEditMessage }}
                </div>
                <div v-if="priceRefreshMessage" class="price-refresh-message" :class="'price-refresh-message--' + priceRefreshKind">
                  {{ priceRefreshMessage }}
                </div>
              </section>

          <div class="candidate-summary-grid">
          <section class="block" v-if="sourceUrl(selected)">
            <h5>Amazon source</h5>
            <div class="source-card">
              <span class="source-kicker">Verified product page</span>
              <a :href="sourceUrl(selected)" target="_blank" rel="noopener noreferrer">
                {{ sourceTitle(selected) }}
              </a>
              <p v-if="sourceMetaText(selected)">{{ sourceMetaText(selected) }}</p>
            </div>
          </section>

          <!-- Pipeline / trace -->
          <section class="block">
            <h5>Pipeline</h5>
            <div class="pipeline-meta">
              <span class="pl-item"><span class="pl-k">Status</span><span class="status-pill" :class="'st-' + selected.status">{{ statusLabel(selected.status) }}</span></span>
              <span class="pl-item"><span class="pl-k">Attempts</span><span class="pl-v">{{ selected.attempts != null ? selected.attempts : '—' }}</span></span>
              <span class="pl-item" v-if="addedAt(selected)"><span class="pl-k">Added</span><span class="pl-v" :title="fmtTime(addedAt(selected))">{{ fmtTime(addedAt(selected)) }}</span></span>
              <span class="pl-item" v-if="selected.last_scraped_at"><span class="pl-k">Last scraped</span><span class="pl-v">{{ fmtTime(selected.last_scraped_at) }}</span></span>
              <a v-if="selected.source_job_run_id" class="pl-runlink" @click="viewSourceRun(selected)">
                View source run #{{ selected.source_job_run_id }} &rsaquo;
              </a>
            </div>
          </section>
          </div>

          <section class="block action-block">
            <h5>Part actions</h5>
            <div class="single-stage-actions">
              <button
                class="single-stage-btn"
                :disabled="busy || !canRunSingleStage('scrape')"
                :title="singleStageNote('scrape')"
                @click="runSingleStage('scrape')">
                <span class="single-stage-index">01</span>
                <span class="single-stage-copy"><strong>Scrape</strong><small>{{ singleStageNote('scrape') }}</small></span>
              </button>
              <button
                class="single-stage-btn"
                :disabled="busy || !canRunSingleStage('research')"
                :title="singleStageNote('research')"
                @click="runSingleStage('research')">
                <span class="single-stage-index">02</span>
                <span class="single-stage-copy"><strong>Research</strong><small>{{ singleStageNote('research') }}</small></span>
              </button>
              <button
                class="single-stage-btn"
                :disabled="busy || !canRunSingleStage('validate')"
                :title="singleStageNote('validate')"
                @click="runSingleStage('validate')">
                <span class="single-stage-index">03</span>
                <span class="single-stage-copy"><strong>Validate</strong><small>{{ singleStageNote('validate') }}</small></span>
              </button>
              <button
                class="single-stage-btn single-stage-import"
                :disabled="busy || !canRunSingleStage('import')"
                :title="singleStageNote('import')"
                @click="importCandidate(false)">
                <span class="single-stage-index">04</span>
                <span class="single-stage-copy"><strong>Import</strong><small>{{ singleStageNote('import') }}</small></span>
              </button>
              <button
                v-if="showUnavailableImport"
                class="single-stage-btn single-stage-unavailable"
                :disabled="busy || !canImportUnavailable"
                :title="unavailableImportNote"
                @click="importCandidate(true)">
                <span class="single-stage-index">04B</span>
                <span class="single-stage-copy">
                  <strong>Save as unavailable</strong>
                  <small>{{ canImportUnavailable ? 'Hidden from builder until price returns' : unavailableImportNote }}</small>
                </span>
              </button>
            </div>
            <div v-if="candidateRun" class="run-card run-card-compact" :class="'run-' + runStatus(candidateRun)">
              <span class="run-dot"></span>
              <div>
                <strong>{{ candidateRun.label }}</strong>
                <small>Run #{{ runId(candidateRun) || 'pending' }} - {{ runStatusLabel(candidateRun) }}</small>
              </div>
              <span v-if="runSummary(candidateRun)" class="run-summary">{{ runSummary(candidateRun) }}</span>
            </div>
          </section>

            </aside>

            <main class="candidate-review-main">

          <SpecEvaluationPanel
            :item="selected"
            health-title="Candidate health"
            health-description="Typed evaluation from catalog rules, AI research, and source provenance."
            :show-research="true"
            :show-research-all="true"
            :research-state="findingResearchState"
            :research-succeeded="findingResearchSucceeded"
            :can-research="canResearchFinding"
            :is-research-busy="isFindingResearchBusy"
            @research-all="researchCandidateIssue(null, 'all')"
            @research-finding="researchFinding"
            @retry-finding="researchFinding"
            @clear-finding="clearFindingResearchState"
            @scroll-field="scrollToSpecField"
          />

          <!-- Enrichment error -->
          <section class="block enrichment-error-block" v-if="selected.error">
            <h5>Enrichment error</h5>
            <div class="enrichment-error-card">
              <span class="icon icon-warning"></span>
              <div>
                <strong>{{ enrichmentErrorTitle(selected.error) }}</strong>
                <p>{{ selected.error }}</p>
              </div>
            </div>
          </section>

          <!-- Final spec -->
          <section class="block generated-block" v-if="selected.final_spec">
            <div class="block-title-row generated-title-row">
              <div>
                <h5>Generated catalog entry</h5>
                <p>Review and correct the catalog fields before validation/import.</p>
              </div>
              <span v-if="finalSpecDirty" class="dirty-pill">Unsaved edits</span>
            </div>
            <div class="spec-form-groups">
              <div
                class="spec-form-group"
                :class="'spec-form-group-' + grp.key"
                v-for="grp in editableSpecGroups"
                :key="grp.key">
                <div class="spec-group-label">
                  <span>{{ grp.label }}</span>
                  <small>
                    <template v-if="specGroupProblemCount(grp)">{{ specGroupProblemCount(grp) }} need attention &middot; </template>{{ grp.rows.length }} fields
                  </small>
                </div>
                <div class="spec-field-list">
                  <div
                    v-for="row in grp.rows"
                    :key="grp.key + ':' + row.k"
                    class="spec-field"
                    :data-field-path="specFieldPath(grp.key, row.k).toLowerCase()"
                    :class="['field-status-' + fieldStatusClass(row.status), { missing: row.missing, 'is-multiline': row.multiline }]">
                  <div class="spec-field-head">
                    <span class="spec-field-name" :title="row.description">{{ row.k }}</span>
                    <div class="field-meta-bar">
                      <span
                        v-if="row.status"
                        class="field-status-chip"
                        :class="'field-status-chip-' + fieldStatusClass(row.status)"
                        :title="row.description">
                        {{ fieldStatusLabel(row.status) }}
                      </span>
                      <span
                        v-if="row.meta && fieldMetaTierLabel(fieldMetaTier(row.meta))"
                        class="field-tier-badge"
                        :class="'field-tier-' + fieldMetaTrustClass(row.meta)"
                        :title="fieldMetaTooltip(row.meta)">
                        {{ fieldMetaTierLabel(fieldMetaTier(row.meta)) }}
                      </span>
                      <a
                        v-if="fieldMetaUrl(row.meta)"
                        class="field-meta-link"
                        :href="fieldMetaUrl(row.meta)"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop>
                        source
                      </a>
                      <span
                        v-if="fieldMetaSummary(row.meta)"
                        class="field-meta-summary"
                        :title="fieldMetaTooltip(row.meta)">
                        {{ fieldMetaSummary(row.meta) }}
                      </span>
                      <button
                        type="button"
                        class="field-research-action"
                        :disabled="issueBusy[fieldResearchKey(grp.key, row.k)]"
                        @click.stop="researchSpecField(grp.key, row.k)">
                        {{ issueBusy[fieldResearchKey(grp.key, row.k)] ? 'Researching...' : 'Research field' }}
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-if="row.multiline"
                    v-model="editableFinalSpec[grp.key][row.k]"
                    rows="3"
                    @input="markFinalSpecEdited"></textarea>
                  <input
                    v-else
                    v-model="editableFinalSpec[grp.key][row.k]"
                    type="text"
                    @input="markFinalSpecEdited">
                    <div
                      v-if="issueResearchStateForField(grp.key, row.k).message || issueResearchStateForField(grp.key, row.k).suggestion"
                      class="issue-research-state field-research-state"
                      :class="'state-' + issueResearchStateForField(grp.key, row.k).tone">
                      <strong>{{ issueResearchStateForField(grp.key, row.k).label }}</strong>
                      <span v-if="issueResearchStateForField(grp.key, row.k).message">{{ issueResearchStateForField(grp.key, row.k).message }}</span>
                      <code v-if="issueResearchStateForField(grp.key, row.k).suggestion">{{ issueResearchStateForField(grp.key, row.k).suggestion }}</code>
                      <div v-if="issueResearchStateForField(grp.key, row.k).errors && issueResearchStateForField(grp.key, row.k).errors.length" class="resolver-error-list">
                        <span v-for="(err, errIndex) in issueResearchStateForField(grp.key, row.k).errors" :key="'field-err-' + errIndex">{{ err }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="generated-actions">
              <div v-if="finalSpecMessage" class="spec-save-state" :class="'state-' + finalSpecMessageKind">
                {{ finalSpecMessage }}
              </div>
              <button class="act act-notes" :disabled="busy === 'specSave' || !finalSpecDirty" @click="saveGeneratedSpec()">
                {{ busy === 'specSave' ? 'Saving...' : 'Save catalog fields' }}
              </button>
              <button class="act act-import" :disabled="busy || !selected.id" @click="validateGeneratedSpec()">
                {{ busy === 'validate' ? 'Validating...' : 'Save & validate' }}
              </button>
            </div>
          </section>
          <section class="block" v-else>
            <h5>Generated catalog entry</h5>
            <div class="muted">Not enriched yet — no final spec.</div>
          </section>

            </main>
          </div>

          <div v-if="candidateImages(selected).length || selected.reason" class="candidate-evidence-grid">
            <!-- Images -->
            <details class="block image-block evidence-disclosure" v-if="candidateImages(selected).length">
              <summary><span>Images <span class="img-count">{{ candidateImages(selected).length }}</span></span><small>View source media</small></summary>
              <div class="img-gallery">
                <a v-for="(img, i) in candidateImages(selected)" :key="i"
                   :href="img" target="_blank" rel="noopener noreferrer" class="img-cell"
                   :title="img">
                  <span class="img-preview" :style="{ backgroundImage: 'url(' + img + ')' }"></span>
                  <img class="img-probe" :src="img" alt="" @load="rememberImageResolution(img, $event)" @error="rememberImageResolutionError(img)" />
                  <small>{{ imageResolution(img) }}</small>
                </a>
              </div>
            </details>

            <!-- Research reason -->
            <details class="block reason-block evidence-disclosure" v-if="selected.reason">
              <summary><span>Why it was picked</span><small>View onboarding rationale</small></summary>
              <p class="reason">{{ selected.reason }}</p>
            </details>
          </div>

          <!-- Review notes -->
          <section class="block review-notes-block">
            <h5>Review notes</h5>
            <textarea v-model="reviewNotes" class="notes" rows="2" placeholder="Optional notes for this decision..."></textarea>
            <span v-if="selected.imported_part_id" class="muted imported-ref">
              Imported as {{ selected.imported_part_type || 'part' }} #{{ selected.imported_part_id }}
            </span>
          </section>
        </div>

        <div class="panel-foot">
          <button class="act act-import" :disabled="busy || !canRunSingleStage('import') || selected.status === 'imported'" :title="singleStageNote('import')" @click="importCandidate(false)">
            {{ busy === 'import' ? 'Importing...' : 'Approve & import' }}
          </button>
          <button
            v-if="showUnavailableImport"
            class="act act-unavailable"
            :disabled="busy || !canImportUnavailable"
            :title="unavailableImportNote"
            @click="importCandidate(true)">
            {{ busy === 'importUnavailable' ? 'Saving...' : 'Save as unavailable' }}
          </button>
          <div class="stage-send-control">
            <select v-model="sendStageTarget" :disabled="busy">
              <option v-for="target in sendStageOptions" :key="target.status" :value="target.status">
                {{ target.label }}
              </option>
            </select>
            <button class="act act-retry" :disabled="busy || !selected || selected.status === sendStageTarget" @click="sendToSelectedStage()">
              {{ busy === sendStageTarget ? 'Moving...' : 'Move' }}
            </button>
          </div>
          <button class="act act-dup" :disabled="busy" @click="setStatus('duplicate')">
            {{ busy === 'duplicate' ? '...' : 'Duplicate' }}
          </button>
          <button class="act act-reject" :disabled="busy" @click="setStatus('rejected')">
            {{ busy === 'rejected' ? '...' : 'Reject' }}
          </button>
          <button class="act act-notes" :disabled="busy" @click="saveNotes()">Save notes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, amazonProductUrl } from '../../config'
import LoadingOverlay from '../widgets/LoadingOverlay.vue'
import SpecEvaluationPanel from './shared/SpecEvaluationPanel.vue'
import PartRequestRow from './shared/PartRequestRow.vue'
import JobActivityFeed from './shared/JobActivityFeed.vue'
import OnboardingBoard from './shared/OnboardingBoard.vue'
import realtime from '../../services/realtime'
import { mergeActivity, requestActivity } from '../../services/jobActivity'
import { clearPageJobRun, loadPageJobRun, savePageJobRun } from '../../services/pageJobTracker'

const PAGE_JOB_KEY = 'candidate-review'
import { normalizeBulletList } from '../../utils/bullets'

const INTAKE_MODE_KEY = 'gixcore.onboarding.intakeMode'
const PAGE_VIEW_KEY = 'gixcore.onboarding.pageView'
/** Request statuses that still need a decision or a run — the queue's default view. */
const OPEN_REQUEST_STATUSES = ['new', 'queued', 'searching', 'not_found']
const TYPE_PLURALS = {
  cpu: 'CPUs',
  gpu: 'graphics cards',
  motherboard: 'motherboards',
  ram: 'RAM kits',
  storage: 'drives',
  psu: 'power supplies',
  case: 'cases'
}

/**
 * What a pasted line looks like, for the live preview under the input. The
 * server (PartInputParser) makes the real call, including the barcode check
 * digit; this only has to label the line helpfully while typing.
 */
function detectRequestInput (line) {
  const text = String(line || '').replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim()
  if (/^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*(?:amazon\.[a-z.]{2,6}|amzn\.to|amzn\.com|amzn\.eu|a\.co)(?=[/?#:]|$)/i.test(text)) {
    const asin = /\/(?:dp|gp\/product|gp\/aw\/d|product)\/([A-Z0-9]{10})(?=[/?#]|$)/i.exec(text) ||
      /[?&]asin=([A-Z0-9]{10})(?=[&#]|$)/i.exec(text)
    return asin
      ? { kind: 'asin', label: `Amazon link · ${asin[1].toUpperCase()}` }
      : { kind: 'bad', label: 'Amazon link with no product in it' }
  }
  if (/^(?:https?:\/\/|www\.)/i.test(text)) return { kind: 'bad', label: 'Only Amazon links work' }
  const code = text.replace(/^(?:asin|upc|ean|gtin|barcode)\s*[:#-]?\s*/i, '')
  if (/^B0[A-Z0-9]{8}$/i.test(code)) return { kind: 'asin', label: `ASIN · ${code.toUpperCase()}` }
  const digits = code.replace(/[\s-]+/g, '')
  if (/^[\d\s-]+$/.test(code) && /^\d{11,13}$/.test(digits)) return { kind: 'upc', label: `UPC · ${digits}` }
  return { kind: 'name', label: `Name · ${text.length > 40 ? text.slice(0, 38) + '…' : text}` }
}

export default {
  name: 'CandidateReview',
  components: {
    LoadingOverlay,
    SpecEvaluationPanel,
    PartRequestRow,
    JobActivityFeed,
    OnboardingBoard
  },
  data () {
    return {
      candidates: [],
      statusFilter: '',
      selected: null,
      reviewNotes: '',
      loading: false,
      busy: null,        // which action is in flight ('import' | a status string)
      stageKey: 'onboarding',
      typeFilter: 'all',
      stageBusy: null,
      bulkPriceBusy: false,
      priceRefreshMessage: '',
      priceRefreshKind: 'neutral',
      priceEditValue: '',
      priceEditMessage: '',
      priceEditKind: 'neutral',
      stageOptions: {
        count: 5,
        limit: 20,
        delayMs: 1500,
        maxAttempts: 0,
        researchNote: '',
        // Onboarding scope levers. onboardScope 'active' = only currently-live coverage;
        // 'all' widens to the full target. minCount overrides the per-segment target that
        // defines a coverage gap (empty = registry default).
        onboardScope: 'active',
        minCount: ''
      },
      // Discover -> review -> Onboard flow. Discover is async; partial previews
      // land in stats.discover while every raw result is kept in the search ledger.
      discover: {
        running: false,
        loaded: false,
        runId: null,
        error: '',
        scope: null,
        directives: [],
        cleanup: [],
        candidates: [],
        progress: {},
        ledger: { summary: {}, rounds: [], results: [] },
        warning: '',
        resultsOpen: false,
        index: 0,
        decisions: {},
        committing: false,
        planOpen: false,
        cleanupOpen: false
      },
      // Stage 1 intake. Coverage gaps say what the catalog needs; requests say what
      // people asked for (by hand, by AI suggestion, by searching the site). All
      // of them end up as candidates through the same search -> scrape -> validate
      // chain.
      pageView: 'board',
      refreshingPage: false,
      pageViews: [
        { key: 'board', label: 'Board', hint: 'Where every part is right now' },
        { key: 'stages', label: 'Stages', hint: 'Add parts and work on candidates' }
      ],
      intakeMode: 'add',
      intakeModes: [
        { key: 'add', label: 'Add a part', hint: 'Name, UPC, ASIN or Amazon link' },
        { key: 'suggest', label: 'Suggest with AI', hint: 'Popular parts we do not carry' },
        { key: 'gaps', label: 'Coverage gaps', hint: 'What valid builds are missing' }
      ],
      requestFilters: [
        { key: 'open', label: 'Open' },
        { key: 'all', label: 'All' }
      ],
      requests: {
        filter: 'open',
        loading: false,
        refetch: false,
        saving: false,
        onboarding: false,
        bulkBusy: false,
        busyId: null,
        error: '',
        addError: '',
        text: '',
        rows: [],
        counts: {},
        skipped: [],
        // Rows being watched stay on screen after they leave the "open" filter,
        // so a finished "Onboard now" shows its result instead of vanishing.
        pinned: {},
        // Rows the last AI suggestion run added — marked "New".
        fresh: {}
      },
      // AI suggestions: an async job run followed live over the jobs:<runId>
      // socket (polling as a fallback). Picks are filed as `new` requests, so
      // they appear in the request queue — the one list.
      suggest: {
        count: 10,
        note: '',
        type: null,
        running: false,
        runId: null,
        error: '',
        loaded: false,
        activity: [],
        feedOpen: false,
        filed: 0,
        skipped: 0,
        returned: 0,
        model: '',
        webSearch: false
      },
      suggestPollTimer: null,
      suggestUnwatch: null,
      // Live activity of the dispatch run working through requests ("Onboard
      // now" / "Onboard N queued"), shown on each request's row.
      dispatchFeed: { runId: null, items: [] },
      dispatchUnwatch: null,
      requestRefreshTimer: null,
      discoverPollTimer: null,
      jobsLoaded: false,
      jobMap: {},
      jobRuns: {},
      runPollTimers: {},
      scopeTimer: null,
      stageScope: null,
      stageActionScopes: {},
      stageScopeLoading: false,
      stageScopeError: '',
      imageDimensions: {},
      issueBusy: {},
      issueResearchResults: {},
      issuePanelOpen: false,
      editableFinalSpec: null,
      finalSpecSnapshot: '',
      finalSpecEdited: false,
      finalSpecMessage: '',
      finalSpecMessageKind: 'neutral',
      sendStageTarget: 'discovered',
      candidateCounts: {},
      loadingCounts: false,
      error: null,
      notice: null,
      stages: [
        {
          key: 'onboarding',
          number: 1,
          shortTitle: 'Onboarding',
          title: 'Add parts',
          description: 'Name a part, let AI suggest what we are missing, or fill coverage gaps. New parts are scraped into Review.',
          job: 'catalog-auto-onboard',
          cta: 'Smart auto-onboard',
          statuses: ['discovered', 'scrape_failed'],
          actions: [
            {
              key: 'scrape',
              job: 'enrich-candidates',
              cta: 'Scrape candidates',
              kind: 'scrape'
            },
            {
              key: 'requests',
              job: 'onboard-part-requests',
              cta: 'Onboard requested parts',
              kind: 'requests',
              // Lives on the request queue, not in the stage header, and the
              // queue owns its scope preview.
              scope: false,
              inline: true
            }
          ]
        },
        {
          key: 'reviewValidate',
          number: 2,
          shortTitle: 'Review & Validate',
          title: 'Research and Validate Candidates',
          description: 'Research missing or N/A fields, then run deterministic catalog checks before import.',
          job: 'enrich-candidate-specs',
          cta: 'Research missing fields',
          statuses: ['needs_review'],
          usesLimit: true,
          actions: [
            {
              key: 'research',
              job: 'enrich-candidate-specs',
              cta: 'Research missing fields',
              kind: 'research',
              scope: true
            },
            {
              key: 'validate',
              job: 'validate-candidates',
              cta: 'Validate candidates',
              kind: 'validate'
            }
          ]
        },
        {
          key: 'import',
          number: 3,
          shortTitle: 'Import',
          title: 'Review and Import',
          description: 'Inspect validated candidates and import approved parts into the catalog.',
          job: null,
          cta: '',
          statuses: ['validated']
        }
      ],
      tabs: [
        { key: 'needs_review', label: 'Needs review' },
        { key: 'validated', label: 'Validated' },
        { key: 'discovered', label: 'Discovered' },
        { key: 'scrape_failed', label: 'Scrape failed' },
        { key: 'duplicate', label: 'Duplicate' },
        { key: 'imported', label: 'Imported' },
        { key: 'rejected', label: 'Rejected' }
      ]
    }
  },
  computed: {
    activeStage () {
      return this.stages.find((stage) => stage.key === this.stageKey) || this.stages[0]
    },
    activeStageActions () {
      return this.stageActions(this.activeStage)
    },
    activeStageScopeAction () {
      return this.activeStageActions.find((action) => action.scope !== false) || this.activeStageActions[0] || null
    },
    /** Actions shown in the stage header; inline ones render where they act. */
    headerStageActions () {
      return this.activeStageActions.filter((action) => !action.inline)
    },
    requestsAction () {
      return this.activeStageActions.find((action) => this.stageActionKind(action) === 'requests') || null
    },
    requestSummaryLine () {
      const counts = this.requests.counts || {}
      const queued = Number(counts.queued || 0)
      const triage = Number(counts.new || 0)
      if (!queued && !triage) return 'Nothing waiting'
      const parts = []
      if (queued) parts.push(`${queued} queued`)
      if (triage) parts.push(`${triage} to triage`)
      return parts.join(' · ')
    },
    requestsActionLabel () {
      const queued = Number((this.requests.counts || {}).queued || 0)
      return queued ? `Onboard ${queued} queued` : 'Onboard queued'
    },
    /** The queue appears once anything has ever been requested. */
    showRequestQueue () {
      const counts = this.requests.counts || {}
      return this.requests.rows.length > 0 || Object.keys(counts).some((key) => Number(counts[key]) > 0)
    },
    /** Add and Suggest both need a concrete part type. */
    intakeNeedsType () {
      return this.intakeMode !== 'gaps' && this.normalizedType() === 'all'
    },
    suggestIdleHint () {
      if (this.intakeNeedsType) return 'Pick a type first — suggestions are made one type at a time.'
      return `Finds popular ${this.typePlural(this.normalizedType())} we do not carry yet, each with a reason. Nothing is onboarded until you say so.`
    },
    intakeLines () {
      return String(this.requests.text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    },
    intakeRows () {
      return Math.min(Math.max(this.intakeLines.length, 1), 6)
    },
    intakePreview () {
      const lines = this.intakeLines
      return {
        chips: lines.slice(0, 5).map((line) => detectRequestInput(line)),
        more: Math.max(lines.length - 5, 0)
      }
    },
    intakeHint () {
      if (this.intakeNeedsType) return 'Pick a type first — it decides which catalog the part goes into.'
      return 'e.g. Samsung 990 Pro 2TB, a UPC, an ASIN or an amazon.com link. Out-of-stock parts are onboarded too. Shift+Enter for another line.'
    },
    canAddRequests () {
      return !this.requests.saving && !!this.intakeLines.length && this.normalizedType() !== 'all'
    },
    canSuggest () {
      return !this.suggest.running && this.normalizedType() !== 'all'
    },
    /** Requests on screen still waiting for a decision (and not being worked on). */
    triageIds () {
      return this.requests.rows
        .filter((row) => row.status === 'new' && !this.rowLive(row.id).length)
        .map((row) => row.id)
    },
    suggestHeadline () {
      const filed = this.suggest.filed
      if (filed) return `${filed} new pick${filed === 1 ? '' : 's'} added to the queue below.`
      if (!this.suggest.returned) return 'The AI returned no usable suggestions.'
      return 'Nothing new — we already have or asked for every suggestion.'
    },
    suggestSubline () {
      if (!this.suggest.returned) return 'Try again, or give it a focus.'
      const bits = [`${this.suggest.returned} proposed${this.suggest.model ? ' by ' + this.suggest.model : ''}`]
      if (this.suggest.skipped) bits.push(`${this.suggest.skipped} skipped as already covered`)
      return bits.join(' · ')
    },
    stageRun () {
      return this.jobRuns.stage || null
    },
    candidateRun () {
      const run = this.jobRuns.candidate || null
      if (!run || !this.selected) return null
      if (run.candidateId && String(run.candidateId) !== String(this.selected.id)) return null
      return run
    },
    activeStatuses () {
      return this.activeStage.statuses
    },
    /**
     * Notes for real problems only (a job the backend does not know). "No
     * eligible work" is the disabled button's tooltip, not a banner.
     */
    stageMissingNotes () {
      if (!this.activeStageActions.length || !this.jobsLoaded) return []
      const notes = this.activeStageActions
        .filter((action) => action.job && !this.jobMap[action.job])
        .map((action) => this.stageJobMissingNote(this.activeStage, action))
        .filter(Boolean)
      return notes.filter((note, index) => notes.indexOf(note) === index)
    },
    canBulkRefreshPrices () {
      const refreshStatuses = ['rejected', 'scrape_failed', 'needs_review']
      return this.activeStatuses.some((status) => refreshStatuses.indexOf(status) >= 0)
    },
    canImportCandidate () {
      return !!(this.selected &&
        this.candidatePrice(this.selected).ok &&
        this.candidateImportReadiness(this.selected).ready)
    },
    showUnavailableImport () {
      return !!(this.selected &&
        this.candidateImportStatusAccepted(this.selected) &&
        !this.candidatePrice(this.selected).ok)
    },
    canImportUnavailable () {
      return !!(this.showUnavailableImport && this.candidateImportReadiness(this.selected).ready)
    },
    unavailableImportNote () {
      if (!this.selected) return ''
      const readiness = this.candidateImportReadiness(this.selected)
      if (!readiness.ready) return readiness.reason
      return 'Import as a hidden catalog part and monitor until Amazon price returns'
    },
    sendStageOptions () {
      return [
        { status: 'discovered', label: 'Stage 1: Onboarding queue' },
        { status: 'needs_review', label: 'Stage 2: Review & validate' },
        { status: 'validated', label: 'Stage 3: Ready to import' },
        { status: 'duplicate', label: 'Mark duplicate' },
        { status: 'rejected', label: 'Reject' }
      ]
    },
    stageScopeTitle () {
      if (this.stageScopeLoading && !this.stageScope) return 'Checking eligible work...'
      const descriptor = (this.stageScope && this.stageScope.descriptor) || {}
      return descriptor.title || this.stageActionLabel(this.activeStageScopeAction) || 'Eligible candidates'
    },
    stageScopeDescription () {
      if (this.stageScopeError) return 'Scope preview is unavailable; the run can still be started if the job exists.'
      const descriptor = (this.stageScope && this.stageScope.descriptor) || {}
      if (descriptor.description) return descriptor.description
      if (this.stageScope && this.stageScope.canRun === false) return 'No eligible work for the current stage options.'
      return 'Showing eligible work and counts for this run.'
    },
    stageScopeCount () {
      const scope = this.stageScope || {}
      const values = [
        scope.total,
        scope.eligible,
        scope.selected,
        scope.count,
        scope.previewCount
      ]
      for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return Number(value) || 0
      }
      if (Array.isArray(scope.items)) return scope.items.length
      if (scope.counts && typeof scope.counts === 'object') {
        return Object.keys(scope.counts).reduce((sum, key) => sum + (Number(scope.counts[key]) || 0), 0)
      }
      if (this.stageActionKind(this.activeStageScopeAction) === 'amazonSearch' || this.stageActionKind(this.activeStageScopeAction) === 'smartAutoOnboard') {
        return Number(this.stageOptions.count) || 0
      }
      return this.stageCount(this.activeStage)
    },
    stageScopeCountLabel () {
      const count = this.stageScopeCount
      if (this.stageActionKind(this.activeStageScopeAction) === 'amazonSearch') return count === 1 ? 'requested product' : 'requested products'
      if (this.stageActionKind(this.activeStageScopeAction) === 'smartAutoOnboard') return count === 1 ? 'directive' : 'directives'
      return count === 1 ? 'eligible row' : 'eligible rows'
    },
    stageScopeCounts () {
      const counts = this.stageScope && this.stageScope.counts
      if (!counts || typeof counts !== 'object') return []
      return Object.keys(counts).map((key) => ({
        key,
        label: this.prettyStatus(key),
        value: counts[key]
      })).filter((row) => row.value !== undefined && row.value !== null)
    },
    currentPreview () {
      return this.discover.candidates[this.discover.index] || null
    },
    acceptedPreviewIds () {
      return this.discover.candidates
        .filter((c) => c && this.discover.decisions[c.previewId] === 'accept')
        .map((c) => c.previewId)
    },
    editableSpecGroups () {
      if (!this.editableFinalSpec) return []
      const priority = (row) => {
        const status = String(row.status || '').toLowerCase()
        if (status === 'blocker') return 0
        if (row.missing) return 1
        if (status === 'warning') return 2
        if (status === 'estimated') return 3
        return 4
      }
      const sortRows = (rows) => rows.sort((a, b) => {
        const rank = priority(a) - priority(b)
        return rank || String(a.k || '').localeCompare(String(b.k || ''))
      })
      const evaluatedRows = this.evaluationFields(this.selected)
      if (evaluatedRows.length) {
        const used = {}
        const groups = [
          { key: 'general', label: 'General' },
          { key: 'specs', label: 'Specs' },
          { key: 'compats', label: 'Compatibility' }
        ].map((group) => {
          const values = this.editableFinalSpec[group.key] || {}
          const rows = evaluatedRows
            .filter((row) => row.group === group.key)
            .map((row) => {
              used[row.path.toLowerCase()] = true
              const value = values[row.key] !== undefined ? values[row.key] : row.value
              return {
                k: row.key,
                missing: row.missing || this.isMissingSpecValue(value),
                multiline: this.isLongSpecValue(value),
                meta: row.provenance || this.fieldMetaForPath(row.path),
                status: row.status,
                description: row.description,
                evaluation: row
              }
            })
          return Object.assign(group, { rows })
        })

        ;['general', 'specs', 'compats'].forEach((groupKey) => {
          const group = groups.find((entry) => entry.key === groupKey)
          const values = this.editableFinalSpec[groupKey] || {}
          Object.keys(values).sort((a, b) => a.localeCompare(b)).forEach((key) => {
            const path = this.specFieldPath(groupKey, key).toLowerCase()
            if (used[path]) return
            group.rows.push({
              k: key,
              missing: this.isMissingSpecValue(values[key]),
              multiline: this.isLongSpecValue(values[key]),
              meta: this.fieldMetaForPath(this.specFieldPath(groupKey, key)),
              status: 'ok',
              description: 'Field was not included in the latest typed evaluation.'
            })
          })
        })
        groups.forEach((group) => sortRows(group.rows))
        return groups.filter((group) => group.rows.length)
      }
      return [
        { key: 'general', label: 'General' },
        { key: 'specs', label: 'Specs' },
        { key: 'compats', label: 'Compatibility' }
      ].map((group) => {
        const values = this.editableFinalSpec[group.key] || {}
        return sortRows(Object.keys(values).map((key) => ({
          k: key,
          missing: this.isMissingSpecValue(values[key]),
          multiline: this.isLongSpecValue(values[key]),
          meta: this.fieldMetaForPath(this.specFieldPath(group.key, key))
        })))
          .reduce((acc, row) => {
            acc.rows.push(row)
            return acc
          }, Object.assign(group, { rows: [] }))
      })
    },
    finalSpecDirty () {
      return this.finalSpecEdited || this.finalSpecSnapshot !== this.stringifyFinalSpec(this.editableFinalSpec)
    }
  },
  watch: {
    stageKey () {
      this.scheduleScopeFetch()
    },
    typeFilter () {
      this.scheduleScopeFetch()
    },
    statusFilter () {
      this.scheduleScopeFetch()
    },
    stageOptions: {
      deep: true,
      handler () {
        this.scheduleScopeFetch()
      }
    }
  },
  mounted () {
    this.restorePageView()
    this.restoreIntakeMode()
    this.fetchJobs()
    this.fetchList()
    this.fetchCounts()
    this.fetchRequests()
    this.scheduleScopeFetch()
    this.restoreJobRun('stage')
    this.restoreJobRun('candidate')
  },
  beforeDestroy () {
    Object.keys(this.runPollTimers).forEach((key) => {
      clearTimeout(this.runPollTimers[key])
    })
    if (this.scopeTimer) clearTimeout(this.scopeTimer)
    if (this.discoverPollTimer) clearTimeout(this.discoverPollTimer)
    if (this.suggestPollTimer) clearTimeout(this.suggestPollTimer)
    if (this.requestRefreshTimer) clearTimeout(this.requestRefreshTimer)
    this.stopSuggestWatch()
    this.stopDispatchWatch()
  },
  methods: {
    amazonLink (asin) {
      return amazonProductUrl(asin)
    },
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    async fetchJobs () {
      try {
        const { data } = await axios.get(`${apiDomain}jobs?includeHidden=true`, { headers: this.authHeaders() })
        const jobs = this.candidateListFromKeys(data, ['jobs', 'data', 'items', 'rows'])
        const map = {}
        jobs.forEach((job) => {
          if (job && job.key) map[job.key] = job
        })
        this.jobMap = map
      } catch (e) {
        this.jobMap = {}
      } finally {
        this.jobsLoaded = true
        this.scheduleScopeFetch()
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const statusRows = await Promise.all(this.activeStatuses.map((status) => {
          return axios.get(
            `${apiDomain}part-candidates?${this.candidateQuery({ status, limit: 200 })}`,
            { headers: this.authHeaders() }
          )
        }))
        const candidates = []
        const seen = {}
        statusRows.forEach(({ data }) => {
          this.applyCandidateCounts(data)
          this.candidateListFromResponse(data).forEach((candidate) => {
            const key = candidate.id || `${candidate.part_type || ''}:${candidate.asin || candidate.model || candidates.length}`
            if (seen[key]) return
            seen[key] = true
            candidates.push(candidate)
          })
        })
        this.candidates = candidates
        this.activeStatuses.forEach((status) => {
          const count = candidates.filter((candidate) => candidate.status === status).length
          this.$set(this.candidateCounts, status, count)
        })
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    async refreshPage () {
      this.refreshingPage = true
      try {
        if (this.pageView === 'board' && this.$refs.onboardingBoard) {
          await this.$refs.onboardingBoard.refresh()
        } else {
          await this.refreshCandidates()
        }
      } finally {
        this.refreshingPage = false
      }
    },
    async refreshCandidates () {
      this.error = null
      await this.fetchJobs()
      await Promise.all([
        this.fetchList(),
        this.fetchCounts(),
        this.fetchRequests()
      ])
      if (this.selected && this.selected.id) {
        try {
          await this.refreshSelectedDetail()
        } catch (e) {
          this.error = this.errMsg(e)
        }
      }
      await this.fetchStageScope()
    },
    async fetchCounts () {
      this.loadingCounts = true
      try {
        // The list endpoint returns per-status totals: one small request
        // instead of downloading every status tab just to count its rows.
        try {
          const { data } = await axios.get(
            `${apiDomain}part-candidates?${this.candidateQuery({ limit: 1 })}`,
            { headers: this.authHeaders() })
          if (data && data.counts && typeof data.counts === 'object') {
            this.tabs.forEach((tab) => this.$set(this.candidateCounts, tab.key, Number(data.counts[tab.key]) || 0))
            return
          }
        } catch (e) {
          // Fall through to the per-status requests (older backend).
        }
        const results = await Promise.all(this.tabs.map((tab) => {
          return axios.get(
            `${apiDomain}part-candidates?${this.candidateQuery({ status: tab.key, limit: 200 })}`,
            { headers: this.authHeaders() }
          ).then(({ data }) => ({ key: tab.key, data })).catch(() => ({ key: tab.key, data: null }))
        }))
        const next = {}
        results.forEach((row) => {
          if (!row.data) return
          const responseCounts = this.extractCandidateCounts(row.data)
          if (responseCounts) {
            Object.assign(next, responseCounts)
            return
          }
          next[row.key] = this.candidateListFromResponse(row.data).length
        })
        Object.keys(next).forEach((key) => {
          this.$set(this.candidateCounts, key, next[key])
        })
      } finally {
        this.loadingCounts = false
      }
    },
    candidateListFromResponse (data) {
      if (!data) return []
      return Array.isArray(data)
        ? data
        : (data.candidates || data.partCandidates || data.data || data.rows || [])
    },
    candidateListFromKeys (data, keys) {
      if (Array.isArray(data)) return data
      for (const key of keys) {
        if (data && Array.isArray(data[key])) return data[key]
      }
      return []
    },
    candidateQuery (params) {
      const query = new URLSearchParams()
      Object.keys(params || {}).forEach((key) => {
        const value = params[key]
        if (value !== undefined && value !== null && value !== '') query.set(key, value)
      })
      if (this.typeFilter && this.typeFilter !== 'all') query.set('type', this.typeFilter)
      return query.toString()
    },
    extractCandidateCounts (data) {
      if (!data || Array.isArray(data)) return null
      const meta = data.meta || {}
      const counts = data.counts || data.statusCounts || data.status_counts ||
        meta.counts || meta.statusCounts || meta.status_counts
      if (!counts || typeof counts !== 'object') return null

      const normalized = {}
      this.tabs.forEach((tab) => {
        const keys = [tab.key, tab.label, this.statusLabel(tab.key)]
        let value
        keys.some((countKey) => {
          if (Object.prototype.hasOwnProperty.call(counts, countKey)) {
            value = counts[countKey]
            return true
          }
          return false
        })
        if (value !== undefined && value !== null && value !== '') {
          normalized[tab.key] = Number(value) || 0
        }
      })
      return Object.keys(normalized).length ? normalized : null
    },
    applyCandidateCounts (data) {
      const counts = this.extractCandidateCounts(data)
      if (!counts) return
      Object.keys(counts).forEach((key) => {
        this.$set(this.candidateCounts, key, counts[key])
      })
    },
    tabCount (key) {
      if (this.candidateCounts[key] !== undefined) return this.candidateCounts[key]
      return this.loadingCounts ? '...' : 0
    },
    setStage (key) {
      this.stageKey = key
      this.statusFilter = ''
      if (this.runPollTimers.stage) clearTimeout(this.runPollTimers.stage)
      this.$delete(this.jobRuns, 'stage')
      this.fetchList()
    },
    setStageForStatus (status) {
      const stage = this.stages.find((item) => item.statuses.indexOf(status) >= 0)
      this.stageKey = stage ? stage.key : (this.stages[0] && this.stages[0].key)
      this.statusFilter = status
      if (this.runPollTimers.stage) clearTimeout(this.runPollTimers.stage)
      this.$delete(this.jobRuns, 'stage')
      this.fetchList()
    },
    stageIncludesStatus (status) {
      return this.activeStatuses.indexOf(status) >= 0
    },
    stageCount (stage) {
      return stage.statuses.reduce((sum, status) => sum + Number(this.candidateCounts[status] || 0), 0)
    },
    stageStatusLine (stage) {
      return stage.statuses.map((status) => {
        return `${this.statusLabel(status)} ${this.tabCount(status)}`
      }).join(' / ')
    },
    stageActions (stage) {
      if (!stage) return []
      if (Array.isArray(stage.actions) && stage.actions.length) return stage.actions
      return stage.job ? [{
        key: stage.key,
        job: stage.job,
        cta: stage.cta,
        kind: stage.key,
        scope: true
      }] : []
    },
    stageActionKey (action) {
      return action && (action.key || action.job || action.cta) ? String(action.key || action.job || action.cta) : ''
    },
    stageActionKind (action) {
      return action && (action.kind || action.key) ? String(action.kind || action.key) : ''
    },
    stageActionLabel (action) {
      return action && (action.cta || action.label || action.title || action.job) ? String(action.cta || action.label || action.title || action.job) : ''
    },
    scheduleScopeFetch () {
      if (this.scopeTimer) clearTimeout(this.scopeTimer)
      this.scopeTimer = setTimeout(() => {
        this.scopeTimer = null
        this.fetchStageScope()
      }, 260)
    },

    /* ---- Stage 1 intake -------------------------------------------------- */

    setPageView (view) {
      this.pageView = view
      try { localStorage.setItem(PAGE_VIEW_KEY, view) } catch (e) { /* storage blocked: the choice just is not remembered */ }
      if (view === 'stages') this.refreshCandidates()
    },
    restorePageView () {
      let saved = null
      try { saved = localStorage.getItem(PAGE_VIEW_KEY) } catch (e) { /* storage blocked: start on the board */ }
      if (this.pageViews.some((view) => view.key === saved)) this.pageView = saved
    },
    /** A board card was clicked: open that candidate in the detail drawer. */
    async openCandidateById (id) {
      try {
        const { data } = await axios.get(`${apiDomain}part-candidates/${id}`, { headers: this.authHeaders() })
        const candidate = data && (data.candidate || data.partCandidate || data.data || data)
        if (candidate && candidate.id) await this.openDetail(candidate)
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    setIntakeMode (mode) {
      this.intakeMode = mode
      try { localStorage.setItem(INTAKE_MODE_KEY, mode) } catch (e) { /* storage blocked: the choice just is not remembered */ }
    },
    restoreIntakeMode () {
      let saved = null
      try { saved = localStorage.getItem(INTAKE_MODE_KEY) } catch (e) { /* storage blocked: start on the default mode */ }
      if (this.intakeModes.some((mode) => mode.key === saved)) this.intakeMode = saved
    },
    typePlural (type) {
      return TYPE_PLURALS[type] || 'parts'
    },

    /* ---- Request queue ---------------------------------------------------- */

    setRequestFilter (filter) {
      this.requests.filter = filter
      this.requests.rows = []
      this.fetchRequests()
    },
    async fetchRequests () {
      // A refresh asked for mid-flight runs once more afterwards, never dropped.
      if (this.requests.loading) { this.requests.refetch = true; return }
      this.requests.loading = true
      try {
        const type = this.normalizedType()
        const query = new URLSearchParams({ limit: 60 })
        if (this.requests.filter === 'open') query.set('status', OPEN_REQUEST_STATUSES.join(','))
        if (type !== 'all') query.set('type', type)
        const calls = [axios.get(`${apiDomain}admin/part-requests?${query.toString()}`, { headers: this.authHeaders() })]

        const pinned = Object.keys(this.requests.pinned)
        if (this.requests.filter === 'open' && pinned.length) {
          const pinnedQuery = new URLSearchParams({ ids: pinned.join(','), limit: 200 })
          if (type !== 'all') pinnedQuery.set('type', type)
          calls.push(axios.get(`${apiDomain}admin/part-requests?${pinnedQuery.toString()}`, { headers: this.authHeaders() }))
        }

        const [main, extra] = await Promise.all(calls)
        const fetched = (main.data && main.data.requests) || []
        const pinnedRows = extra ? ((extra.data && extra.data.requests) || []) : []
        this.requests.rows = this.steadyRows(this.requests.rows, fetched, pinnedRows)
        this.requests.counts = (main.data && main.data.counts) || {}
        this.requests.error = ''
      } catch (e) {
        // A 404 just means the backend predates part requests — stay quiet and
        // leave the panel empty rather than shouting on every page load.
        const status = e && e.response && e.response.status
        if (status && status !== 404) this.requests.error = this.errMsg(e)
      } finally {
        this.requests.loading = false
        if (this.requests.refetch) {
          this.requests.refetch = false
          this.fetchRequests()
        }
      }
    },
    /**
     * Keep the list steady while it updates: rows already on screen keep their
     * place (pinned ones even after leaving the filter), and rows that were not
     * on screen before — fresh AI picks, say — go on top.
     */
    steadyRows (previous, fetched, pinnedRows) {
      const byId = new Map(fetched.map((row) => [row.id, row]))
      pinnedRows.forEach((row) => { if (!byId.has(row.id)) byId.set(row.id, row) })
      const kept = []
      const seen = {}
      previous.forEach((row) => {
        const next = byId.get(row.id)
        if (next) { kept.push(next); seen[row.id] = true }
      })
      const added = []
      byId.forEach((row, id) => { if (!seen[id]) added.push(row) })
      return added.concat(kept)
    },
    /** Debounced re-read of the queue, e.g. each time a dispatched request finishes. */
    scheduleRequestRefresh (delay = 400) {
      if (this.requestRefreshTimer) clearTimeout(this.requestRefreshTimer)
      this.requestRefreshTimer = setTimeout(() => {
        this.requestRefreshTimer = null
        this.fetchRequests()
      }, delay)
    },
    pinRequests (ids) {
      const list = ids || []
      list.forEach((id) => { if (id) this.$set(this.requests.pinned, id, true) })
    },
    /** Live activity of the dispatch run for one request's row. */
    rowLive (id) {
      return requestActivity(this.dispatchFeed.items, id)
    },
    /**
     * File what was typed as requests; with `onboard`, dispatch them right away.
     * Lines the server could not use stay in the box so they can be fixed.
     */
    async submitRequests (onboard = false) {
      if (!this.canAddRequests) return
      const text = (this.requests.text || '').trim()
      this.requests.saving = true
      this.requests.onboarding = onboard
      this.requests.addError = ''
      this.requests.skipped = []
      try {
        const { data } = await axios.post(`${apiDomain}admin/part-requests/bulk`,
          { text, type: this.normalizedType() },
          { headers: this.authHeaders() })
        const created = (data && data.created) || []
        const bumped = (data && data.bumped) || []
        const filed = created.concat(bumped)
        this.requests.skipped = (data && data.skipped) || []
        this.requests.counts = (data && data.counts) || this.requests.counts
        if (filed.length) this.requests.text = this.requests.skipped.map((item) => item.line).join('\n')
        this.pinRequests(filed.map((row) => row.id))

        if (onboard && filed.length) {
          await this.startOnboard(filed.map((row) => row.id))
        } else if (filed.length) {
          this.notice = `Filed ${created.length} new request${created.length === 1 ? '' : 's'}` +
            (bumped.length ? `, ${bumped.length} already in the queue` : '') + '.'
        }
        await this.fetchRequests()
      } catch (e) {
        this.requests.addError = this.errMsg(e)
      } finally {
        this.requests.saving = false
        this.requests.onboarding = false
      }
    },
    /** "Onboard now" for rows already in the queue. */
    async onboardRequests (ids) {
      if (!ids.length || this.requests.onboarding) return
      this.requests.onboarding = true
      if (ids.length === 1) this.requests.busyId = ids[0]
      try {
        await this.startOnboard(ids)
      } finally {
        this.requests.onboarding = false
        this.requests.busyId = null
        await this.fetchRequests()
      }
    },
    /**
     * Dispatch exactly these requests (search, then scrape) and follow the run
     * live: each row shows what is being done for it. A 409 means another
     * dispatch run holds the lock — the rows stay queued for it, so that is a
     * notice, not an error.
     */
    async startOnboard (ids) {
      let data = null
      try {
        const res = await axios.post(`${apiDomain}admin/part-requests/onboard`, { ids }, { headers: this.authHeaders() })
        data = res.data
      } catch (e) {
        const body = e && e.response && e.response.data
        if (e && e.response && e.response.status === 409 && body) {
          this.notice = body.message || 'A dispatch run is already in progress; these parts are queued.'
          return
        }
        this.error = this.errMsg(e)
        return
      }
      const held = (data && data.held) || []
      const heldText = held.map((row) => `${row.raw_text} (${row.reason})`).join('; ')
      if (!data || !data.started || !data.runId) {
        this.notice = held.length ? `Nothing to onboard: ${heldText}` : 'Nothing to onboard.'
        return
      }
      const started = data.requestIds || ids
      this.pinRequests(started)
      this.$set(this.jobRuns, 'stage', {
        id: data.runId,
        status: 'running',
        label: `Onboarding ${started.length} requested part${started.length === 1 ? '' : 's'}`,
        jobKey: 'onboard-part-requests',
        startedAt: new Date().toISOString()
      })
      this.persistJobRun('stage')
      this.pollRun('stage')
      this.watchDispatchRun(data.runId)
      this.error = null
      // Progress shows on the rows themselves; only say what was left out.
      if (held.length) this.notice = `Not onboarded: ${heldText}`
    },
    async setRequestStatus (row, status) {
      if (!row || this.requests.busyId) return
      this.requests.busyId = row.id
      try {
        await axios.post(`${apiDomain}admin/part-requests/${row.id}/status`,
          { status }, { headers: this.authHeaders() })
        this.pinRequests([row.id])
        await this.fetchRequests()
      } catch (e) {
        this.requests.error = this.errMsg(e)
      } finally {
        this.requests.busyId = null
      }
    },
    /** Move everything waiting for a decision into the queue in one go. */
    async queueTriage () {
      const ids = this.triageIds
      if (!ids.length || this.requests.bulkBusy) return
      this.requests.bulkBusy = true
      try {
        await axios.post(`${apiDomain}admin/part-requests/bulk-status`,
          { ids, status: 'queued' }, { headers: this.authHeaders() })
        this.pinRequests(ids)
        await this.fetchRequests()
      } catch (e) {
        this.requests.error = this.errMsg(e)
      } finally {
        this.requests.bulkBusy = false
      }
    },

    /* ---- Live runs (job:activity over the jobs:<runId> socket) ------------ */

    /** Subscribe to one run's topic; the server answers with a snapshot. */
    watchRun (runId, handlers) {
      const topic = `jobs:${runId}`
      const unsubscribe = realtime.subscribe(topic, handlers)
      if (realtime.isConnected()) realtime.refresh(topic)
      return unsubscribe
    },
    /** Follow a dispatch run so each request's row shows its progress. */
    watchDispatchRun (runId) {
      this.stopDispatchWatch()
      this.dispatchFeed = { runId, items: [] }
      this.dispatchUnwatch = this.watchRun(runId, {
        'job:snapshot': (data) => {
          if (data && Array.isArray(data.activity) && data.activity.length) {
            this.dispatchFeed.items = data.activity
            this.pinRequests(data.activity.map((item) => item.request).filter(Boolean))
          }
        },
        'job:activity': (data) => this.onDispatchActivity(data && data.item),
        'job:finished': () => this.scheduleRequestRefresh(0)
      })
    },
    onDispatchActivity (item) {
      if (!item) return
      this.dispatchFeed.items = mergeActivity(this.dispatchFeed.items, item)
      if (item.request) this.pinRequests([item.request])
      // A request finished: re-read the queue so its status comes from the database.
      if (item.kind === 'step' && /^req\.\d+$/.test(item.id) && (item.status === 'done' || item.status === 'failed')) {
        this.scheduleRequestRefresh()
      }
    },
    stopDispatchWatch () {
      if (this.dispatchUnwatch) this.dispatchUnwatch()
      this.dispatchUnwatch = null
    },

    /* ---- AI suggestions --------------------------------------------------- */

    async runSuggest () {
      if (!this.canSuggest) return
      this.resetSuggest()
      this.suggest.type = this.normalizedType()
      this.suggest.running = true
      try {
        const { data } = await axios.post(`${apiDomain}admin/part-requests/suggest`, {
          type: this.suggest.type,
          count: Math.min(Math.max(Number(this.suggest.count) || 10, 1), 25),
          note: (this.suggest.note || '').trim()
        }, { headers: this.authHeaders() })
        const runId = (data && data.runId) || null
        if (!runId) {
          this.suggest.running = false
          this.suggest.error = 'The suggestion run did not return a run id.'
          return
        }
        this.suggest.runId = runId
        this.suggestUnwatch = this.watchRun(runId, {
          'job:snapshot': (payload) => {
            if (payload && Array.isArray(payload.activity) && payload.activity.length) this.suggest.activity = payload.activity
            if (payload && payload.run && this.isTerminalRun(payload.run)) this.finishSuggest(payload.run)
          },
          'job:activity': (payload) => {
            if (payload && payload.item) this.suggest.activity = mergeActivity(this.suggest.activity, payload.item)
          },
          'job:finished': (payload) => this.finishSuggest(payload)
        })
        this.pollSuggest()
      } catch (e) {
        this.suggest.running = false
        this.suggest.error = this.errMsg(e)
      }
    },
    /** Fallback for when the socket is down: slow while live updates flow. */
    pollSuggest () {
      const id = this.suggest.runId
      if (!id || !this.suggest.running) return
      if (this.suggestPollTimer) clearTimeout(this.suggestPollTimer)
      this.suggestPollTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get(`${apiDomain}jobs/runs/${id}`, { headers: this.authHeaders() })
          const run = this.unwrapRun(data)
          if (this.isTerminalRun(run)) this.finishSuggest(run)
          else this.pollSuggest()
        } catch (e) {
          this.pollSuggest()
        }
      }, realtime.isConnected() ? 8000 : 2500)
    },
    /** One exit for the socket and the poll alike. */
    finishSuggest (run) {
      if (!this.suggest.running || !run) return
      this.suggest.running = false
      this.stopSuggestWatch()
      if (this.suggestPollTimer) clearTimeout(this.suggestPollTimer)
      this.suggestPollTimer = null
      if (['failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0) {
        this.suggest.error = run.error || 'The suggestion run failed.'
        this.suggest.feedOpen = true
        return
      }
      const result = this.runStats(run).suggest || {}
      const filed = Array.isArray(result.filed) ? result.filed : []
      filed.forEach((row) => this.$set(this.requests.fresh, row.id, true))
      this.pinRequests(filed.map((row) => row.id))
      this.suggest.filed = filed.length
      this.suggest.skipped = Array.isArray(result.skipped) ? result.skipped.length : 0
      this.suggest.returned = Number(result.returned) || 0
      this.suggest.model = [result.provider, result.model].filter(Boolean).join('/')
      this.suggest.webSearch = !!result.webSearch
      this.suggest.loaded = true
      this.fetchRequests()
    },
    stopSuggestWatch () {
      if (this.suggestUnwatch) this.suggestUnwatch()
      this.suggestUnwatch = null
    },
    resetSuggest () {
      if (this.suggestPollTimer) clearTimeout(this.suggestPollTimer)
      this.suggestPollTimer = null
      this.stopSuggestWatch()
      Object.assign(this.suggest, {
        type: null,
        running: false,
        runId: null,
        error: '',
        loaded: false,
        activity: [],
        feedOpen: false,
        filed: 0,
        skipped: 0,
        returned: 0,
        model: '',
        webSearch: false
      })
    },
    scopeQuery (options) {
      const params = new URLSearchParams()
      Object.keys(options || {}).forEach((key) => {
        const value = options[key]
        if (value === undefined || value === null || value === '') return
        if (Array.isArray(value)) {
          value.forEach((entry) => params.append(key, entry))
          return
        }
        params.set(key, value)
      })
      params.set('previewLimit', 8)
      return params.toString()
    },
    async fetchStageScope () {
      const actions = this.activeStageActions.filter((action) => action && action.job)
      const primaryKey = this.stageActionKey(this.activeStageScopeAction)
      if (!actions.length) {
        this.stageScope = null
        this.stageActionScopes = {}
        this.stageScopeError = ''
        this.stageScopeLoading = false
        return
      }
      const availableActions = actions.filter((action) => !this.jobsLoaded || this.jobMap[action.job])
      if (!availableActions.length) {
        this.stageScope = null
        this.stageActionScopes = {}
        this.stageScopeError = ''
        this.stageScopeLoading = false
        return
      }
      this.stageScopeLoading = true
      this.stageScopeError = ''
      try {
        const results = await Promise.all(availableActions.map(async (action) => {
          const query = this.scopeQuery(this.activeStageOptions(action))
          try {
            const { data } = await axios.get(
              `${apiDomain}jobs/${action.job}/scope${query ? '?' + query : ''}`,
              { headers: this.authHeaders() }
            )
            return { key: this.stageActionKey(action), scope: this.unwrapScope(data) }
          } catch (e) {
            return { key: this.stageActionKey(action), error: e }
          }
        }))
        const scopes = {}
        let primaryScope = null
        let primaryError = null
        results.forEach((result) => {
          if (!result || !result.key) return
          if (result.scope) scopes[result.key] = result.scope
          if (result.key === primaryKey) {
            primaryScope = result.scope || null
            primaryError = result.error || null
          }
        })
        this.stageActionScopes = scopes
        this.stageScope = primaryScope
        this.stageScopeError = primaryError ? this.errMsg(primaryError) : ''
      } finally {
        this.stageScopeLoading = false
      }
    },
    unwrapScope (data) {
      return (data && (data.scope || data.data)) || data || null
    },
    stageActionScope (action) {
      const key = this.stageActionKey(action)
      return key ? this.stageActionScopes[key] || null : null
    },
    stageActionRunnable (stage, action) {
      if (!action || !action.job) return false
      if (this.jobsLoaded && !this.jobMap[action.job]) return false
      const actionScope = this.stageActionScope(action)
      if (actionScope && actionScope.canRun === false) return false
      if (
        this.stageScope &&
        stage &&
        stage.key === this.activeStage.key &&
        this.stageActionKey(action) === this.stageActionKey(this.activeStageScopeAction) &&
        this.stageScope.canRun === false
      ) return false
      return true
    },
    stageJobRunnable (stage) {
      return this.stageActionRunnable(stage, this.stageActions(stage)[0])
    },
    /** Why a stage button is disabled, for its tooltip; '' when it can run. */
    stageActionBlockedReason (action) {
      if (!action || this.stageActionRunnable(this.activeStage, action)) return ''
      return this.stageJobMissingNote(this.activeStage, action)
    },
    stageJobMissingNote (stage, action) {
      const target = action || this.stageActions(stage)[0]
      if (!target || !target.job) return 'This stage uses the candidate import endpoint from the detail drawer.'
      const actionScope = this.stageActionScope(target)
      if (actionScope && actionScope.canRun === false) {
        return `No eligible work for "${this.stageActionLabel(target)}" with the current options.`
      }
      if (
        this.stageScope &&
        stage &&
        stage.key === this.activeStage.key &&
        this.stageActionKey(target) === this.stageActionKey(this.activeStageScopeAction) &&
        this.stageScope.canRun === false
      ) {
        return 'No eligible work for the current options.'
      }
      return `Job "${target.job}" was not returned by /api/jobs?includeHidden=true.`
    },
    normalizedType () {
      return this.typeFilter && this.typeFilter !== 'all' ? this.typeFilter : 'all'
    },
    activeStageOptions (action = this.activeStageScopeAction) {
      const type = this.normalizedType()
      const kind = this.stageActionKind(action) || this.activeStage.key
      if (kind === 'requests') {
        // "Onboard N queued" works through what is queued, capped per run so
        // one click cannot start an hour of searches.
        const queued = Number((this.requests.counts || {}).queued || 0)
        return {
          type,
          limit: Math.min(Math.max(queued, 1), 10),
          varietyCount: 2
        }
      }
      if (kind === 'smartAutoOnboard') {
        const maxDirectives = Math.max(1, Number(this.stageOptions.count) || 5)
        const options = {
          mode: 'dispatch',
          maxDirectives,
          runPipeline: true
        }
        // Levers flow into both the scope preview query and the dispatch run.
        if (this.stageOptions.onboardScope && this.stageOptions.onboardScope !== 'active') {
          options.scope = this.stageOptions.onboardScope
        }
        const minCount = Number(this.stageOptions.minCount)
        if (Number.isFinite(minCount) && minCount > 0) options.minCount = minCount
        if (type !== 'all') {
          options.type = type
          options.types = [type]
        }
        return options
      }
      if (kind === 'amazonSearch') {
        return {
          type,
          count: Number(this.stageOptions.count) || 5,
          researchNote: this.stageOptions.researchNote || ''
        }
      }
      if (kind === 'scrape') {
        return {
          type,
          retryFailed: true,
          limit: Number(this.stageOptions.limit) || 0
        }
      }
      if (kind === 'research') {
        const candidateIds = this.researchCandidateIdsInList()
        const options = {
          type,
          limit: candidateIds.length || Number(this.stageOptions.limit) || 0,
          delayMs: Number(this.stageOptions.delayMs) || 0
        }
        if (candidateIds.length) options.candidateIds = candidateIds
        return options
      }
      if (kind === 'validate') {
        return {
          type,
          limit: Number(this.stageOptions.limit) || 0,
          maxAttempts: Number(this.stageOptions.maxAttempts) || 0
        }
      }
      return { type }
    },
    async runActiveStage () {
      await this.runStageAction(this.activeStageScopeAction || this.activeStageActions[0])
    },
    async runStageAction (action) {
      if (!action || !action.job || !this.stageActionRunnable(this.activeStage, action)) return
      const busyKey = this.stageActionKey(action)
      const options = this.activeStageOptions(action)
      const candidateIds = Array.isArray(options.candidateIds) ? options.candidateIds : []
      const label = this.stageActionKind(action) === 'research' && candidateIds.length
        ? `Research ${candidateIds.length} candidate${candidateIds.length === 1 ? '' : 's'}`
        : this.stageActionLabel(action)
      this.stageBusy = busyKey
      try {
        const scoped = busyKey === this.stageActionKey(this.activeStageScopeAction)
        const scopeSnapshot = this.stageActionScope(action)
        await this.startJobRun(action.job, options, label, 'stage', {
          scopeSnapshot: scopeSnapshot || (scoped ? this.stageScope : null),
          stageAction: busyKey,
          candidateIds,
          targetedCandidates: candidateIds.length
        })
        // Dispatching the queue: follow it live on the rows.
        const started = this.jobRuns.stage
        if (action.job === 'onboard-part-requests' && this.runId(started)) this.watchDispatchRun(this.runId(started))
      } finally {
        this.stageBusy = null
      }
    },
    researchCandidatesInList () {
      const type = this.normalizedType()
      const activeStatuses = this.activeStatuses || []
      const eligible = (this.candidates || []).filter((candidate) => {
        if (!candidate || candidate.id === undefined || candidate.id === null) return false
        if (activeStatuses.length && activeStatuses.indexOf(candidate.status) < 0) return false
        if (type !== 'all' && String(candidate.part_type || '').toLowerCase() !== String(type).toLowerCase()) return false
        return true
      })
      const missing = eligible.filter((candidate) => this.candidateMissingFieldCount(candidate) > 0)
      return missing.length ? missing : eligible
    },
    researchCandidateIdsInList () {
      const seen = {}
      return this.researchCandidatesInList().reduce((ids, candidate) => {
        const key = String(candidate.id)
        if (!seen[key]) {
          seen[key] = true
          ids.push(candidate.id)
        }
        return ids
      }, [])
    },
    candidateImportStatusAccepted (candidate) {
      return !!(candidate && ['needs_review', 'validated'].indexOf(candidate.status) >= 0)
    },
    candidateHasImportSpec (candidate) {
      const spec = candidate && candidate.final_spec
      if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return false
      return ['general', 'specs', 'compats'].every((group) => {
        const values = spec[group]
        return values && typeof values === 'object' && !Array.isArray(values) && Object.keys(values).length > 0
      })
    },
    candidateImportReadiness (candidate) {
      if (!this.candidateImportStatusAccepted(candidate)) {
        return { ready: false, reason: 'Candidate must be in needs review or validated' }
      }
      if (!/^[A-Z0-9]{10}$/i.test(String(candidate.asin || '').trim())) {
        return { ready: false, reason: 'A valid 10-character ASIN is required before import' }
      }
      if (!this.candidateHasImportSpec(candidate)) {
        return { ready: false, reason: 'Complete general, specs, and compatibility fields before import' }
      }
      const blockers = this.blockerCount(candidate)
      if (blockers > 0) {
        return {
          ready: false,
          reason: `Resolve ${blockers} validation blocker${blockers === 1 ? '' : 's'} before import`
        }
      }
      return { ready: true, reason: 'Ready to import' }
    },
    canRunSingleStage (stageKey) {
      if (!this.selected) return false
      if (stageKey === 'scrape') {
        return !!this.selected.id && ['discovered', 'scrape_failed', 'needs_review', 'validated'].includes(this.selected.status)
      }
      if (stageKey === 'research') return !!this.selected.id
      if (stageKey === 'validate') return !!this.selected.id
      if (stageKey === 'import') return this.canImportCandidate
      return false
    },
    singleStageNote (stageKey) {
      if (!this.selected) return ''
      if (stageKey === 'scrape') {
        if (!this.canRunSingleStage(stageKey)) {
          return `Cannot rescrape a candidate in ${this.statusLabel(this.selected.status)} status`
        }
        return this.selected.asin ? `Run Amazon scrape for ASIN ${this.selected.asin}` : 'Run Amazon scrape for this candidate'
      }
      if (stageKey === 'research') {
        return 'Run AI missing-spec research for this part'
      }
      if (stageKey === 'validate') {
        return 'Refresh catalog-rule findings for this part'
      }
      if (stageKey === 'import') {
        if (this.canRunSingleStage(stageKey)) return 'Import candidate into the catalog'
        if (!this.candidateImportStatusAccepted(this.selected)) return 'Candidate must be in needs review or validated'
        if (!this.candidatePrice(this.selected).ok) return 'No price found - use Save as unavailable'
        return this.candidateImportReadiness(this.selected).reason
      }
      return ''
    },
    async runSingleStage (stageKey) {
      if (!this.selected || !this.canRunSingleStage(stageKey)) return
      const id = this.selected.id
      const type = this.selected.part_type || this.normalizedType()
      const asin = this.selected.asin || undefined
      const jobs = {
        scrape: {
          key: 'enrich-candidates',
          label: 'Scrape',
          options: { type, retryFailed: true, candidateIds: [id], asin }
        },
        research: {
          key: 'enrich-candidate-specs',
          label: 'Research',
          options: { type, candidateIds: [id], asin, delayMs: Number(this.stageOptions.delayMs) || 0 }
        },
        validate: {
          key: 'validate-candidates',
          label: 'Validate',
          options: { type, candidateIds: [id], asin, maxAttempts: 0 }
        }
      }
      const job = jobs[stageKey]
      if (!job) return
      this.busy = stageKey
      try {
        await this.startJobRun(job.key, job.options, job.label, 'candidate', { candidateId: id })
      } finally {
        this.busy = null
      }
    },
    async refreshCandidatePrice (reopen) {
      if (!this.selected) return
      const id = this.selected.id
      const label = reopen ? 'Refresh price and reopen' : 'Refresh Amazon price'
      this.busy = reopen ? 'priceReopen' : 'price'
      this.priceRefreshMessage = ''
      this.priceRefreshKind = 'neutral'
      try {
        await this.startJobRun('refresh-candidate-prices', {
          candidateIds: [id],
          asin: this.selected.asin || undefined,
          type: this.selected.part_type || this.normalizedType(),
          missingOnly: false,
          reopen: !!reopen,
          delayMs: 4000
        }, label, 'candidate', { intent: 'priceRefresh', candidateId: id, reopen: !!reopen })
      } finally {
        this.busy = null
      }
    },
    async refreshMissingPrices () {
      this.bulkPriceBusy = true
      const statuses = this.activeStatuses.slice()
      try {
        await this.startJobRun('refresh-candidate-prices', {
          type: this.normalizedType(),
          status: statuses.length === 1 ? statuses[0] : 'all',
          statuses,
          missingOnly: true,
          limit: Number(this.stageOptions.limit) || 20,
          reopen: false
        }, 'Refresh missing prices', 'stage', { intent: 'bulkPriceRefresh' })
      } finally {
        this.bulkPriceBusy = false
      }
    },
    async startJobRun (jobKey, options, label, contextKey, meta = {}) {
      const optimisticRun = Object.assign({}, meta, {
        id: '',
        label,
        jobKey,
        status: 'queued',
        startedAt: new Date().toISOString()
      })
      this.$set(this.jobRuns, contextKey, optimisticRun)
      try {
        const requestBody = {
          options: Object.assign({}, options || {})
        }
        const { data } = await axios.post(`${apiDomain}jobs/${jobKey}/run`,
          requestBody,
          { headers: this.authHeaders() })
        const run = this.unwrapRun(data)
        const trackedRun = Object.assign({}, run, meta, { label, jobKey })
        this.$set(this.jobRuns, contextKey, trackedRun)
        this.persistJobRun(contextKey)
        this.notice = `${label} started${this.runId(trackedRun) ? ` as run #${this.runId(trackedRun)}` : ''}.`
        this.error = null
        this.pollRun(contextKey)
      } catch (e) {
        this.error = this.errMsg(e)
        if (e && e.response && e.response.status >= 400 && e.response.status < 500) {
          this.$set(this.jobRuns, contextKey, null)
          clearPageJobRun(PAGE_JOB_KEY, contextKey)
        }
      }
    },
    unwrapRun (data) {
      return (data && (data.run || data.jobRun || data.job_run || data.data)) || data || {}
    },
    pollRun (contextKey) {
      const run = this.jobRuns[contextKey]
      const id = this.runId(run)
      if (!id) return
      if (this.runPollTimers[contextKey]) clearTimeout(this.runPollTimers[contextKey])
      this.runPollTimers[contextKey] = setTimeout(async () => {
        try {
          const { data } = await axios.get(`${apiDomain}jobs/runs/${id}`, { headers: this.authHeaders() })
          const nextRun = Object.assign({}, this.jobRuns[contextKey], this.unwrapRun(data))
          this.$set(this.jobRuns, contextKey, nextRun)
          this.persistJobRun(contextKey)
          if (this.isTerminalRun(nextRun)) {
            await this.refreshCandidates()
            if (this.selected && contextKey === 'candidate') {
              await this.refreshSelectedDetail()
              if (nextRun.intent === 'priceRefresh') this.applyPriceRefreshResult(nextRun)
            }
            return
          }
          this.pollRun(contextKey)
        } catch (e) {
          this.error = this.errMsg(e)
        }
      }, 2500)
    },
    persistJobRun (contextKey) {
      const run = this.jobRuns[contextKey]
      if (run) savePageJobRun(PAGE_JOB_KEY, contextKey, run)
    },
    async restoreJobRun (contextKey) {
      const saved = loadPageJobRun(PAGE_JOB_KEY, contextKey)
      if (!saved || !saved.runId) return
      this.$set(this.jobRuns, contextKey, Object.assign({ stats: {} }, saved, { id: saved.runId }))
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${saved.runId}`, { headers: this.authHeaders() })
        const run = Object.assign({}, saved, this.unwrapRun(data))
        this.$set(this.jobRuns, contextKey, run)
        this.persistJobRun(contextKey)
        if (!this.isTerminalRun(run)) this.pollRun(contextKey)
        // Reloaded mid-dispatch: pick the live row progress back up.
        if (contextKey === 'stage' && !this.isTerminalRun(run) &&
            (run.jobKey || run.job_key) === 'onboard-part-requests') this.watchDispatchRun(saved.runId)
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          this.$set(this.jobRuns, contextKey, null)
          clearPageJobRun(PAGE_JOB_KEY, contextKey)
        } else {
          this.pollRun(contextKey)
        }
      }
    },
    runId (run) {
      return run && (run.id || run.runId || run.run_id)
    },
    runStatus (run) {
      return String((run && (run.status || run.state)) || 'running').toLowerCase()
    },
    runStatusLabel (run) {
      const status = this.runStatus(run)
      const label = this.statusLabel(status)
      return label === status ? this.prettyStatus(status) : label
    },
    isTerminalRun (run) {
      return ['success', 'succeeded', 'completed', 'complete', 'partial', 'failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0
    },
    runSummary (run) {
      const stats = this.runStats(run)
      if (!stats || typeof stats !== 'object') return ''
      const parts = []
      ;['found', 'stored', 'processed', 'enriched', 'updated', 'priceUpdated', 'reopened', 'unavailable', 'failed', 'skipped'].forEach((key) => {
        if (stats[key] !== undefined && stats[key] !== null) parts.push(`${this.prettyStatus(key)} ${stats[key]}`)
      })
      return parts.slice(0, 3).join(' / ')
    },
    runScopeLabel (run) {
      const scope = run && run.scopeSnapshot
      if (!scope) return ''
      const total = scope.total || scope.eligible || scope.selected || scope.count ||
        (Array.isArray(scope.items) ? scope.items.length : null)
      if (total === null || total === undefined || total === '') return ''
      return `${total} scoped ${Number(total) === 1 ? 'row' : 'rows'}`
    },
    flattenRunStats (stats, prefix) {
      const out = []
      Object.keys(stats || {}).forEach((key) => {
        const value = stats[key]
        const path = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          out.push(...this.flattenRunStats(value, path))
        } else {
          out.push({ key: path, value })
        }
      })
      return out
    },
    runStatValue (run, names) {
      const stats = this.runStats(run)
      const flat = this.flattenRunStats(stats)
      const normalizedNames = names.map((name) => String(name).toLowerCase())
      for (const item of flat) {
        const key = String(item.key || '').toLowerCase()
        const leaf = key.split('.').pop()
        if (normalizedNames.indexOf(key) >= 0 || normalizedNames.indexOf(leaf) >= 0) {
          if (item.value !== undefined && item.value !== null && item.value !== '') return item.value
        }
      }
      return null
    },
    runAiOutcome (run) {
      const result = this.runStatValue(run, ['amazonSearch.aiResult', 'aiResult', 'ai_result'])
      const status = this.runStatValue(run, ['amazonSearch.aiStatus', 'aiStatus', 'ai_status'])
      const items = this.runStatValue(run, ['amazonSearch.aiItems', 'aiItems', 'ai_items'])
      const webCalls = this.runStatValue(run, ['amazonSearch.aiWebSearchCalls', 'aiWebSearchCalls', 'ai_web_search_calls'])
      const preview = this.runStatValue(run, ['amazonSearch.aiOutputPreview', 'aiOutputPreview', 'ai_output_preview'])
      const text = String(result || status || '').trim()
      let tone = 'neutral'
      if (/^items:/i.test(text) || (Number(items) > 0)) tone = 'good'
      else if (/empty/i.test(text)) tone = 'warn'
      else if (/error|fail|quota/i.test(text)) tone = 'bad'
      const bits = []
      if (text) bits.push(`AI outcome: ${text}`)
      if (items !== null) bits.push(`${items} item${Number(items) === 1 ? '' : 's'}`)
      if (webCalls !== null) bits.push(`${webCalls} web search call${Number(webCalls) === 1 ? '' : 's'}`)
      return {
        tone,
        label: bits.join(' - '),
        preview: preview ? this.truncateText(preview, 180) : ''
      }
    },
    stageRunEmptyAmazonSearch (run) {
      if (!run || this.runStatus(run) === 'running' || this.runStatus(run) === 'queued') return false
      const key = String(run.jobKey || run.job_key || '').toLowerCase()
      if (key && key !== 'amazon-search-products') return false
      const outcome = this.runAiOutcome(run)
      const found = this.runStatValue(run, ['amazonSearch.found', 'found'])
      const stored = this.runStatValue(run, ['amazonSearch.stored', 'stored'])
      const hasCounts = found !== null || stored !== null
      return /empty/i.test(outcome.label) || (hasCounts && Number(found || 0) === 0 && Number(stored || 0) === 0)
    },
    async runDiscoverFallback () {
      this.stageBusy = 'discoverFallback'
      try {
        await this.startJobRun('discover-parts', {
          type: this.normalizedType(),
          count: Number(this.stageOptions.count) || 5,
          quantity: Number(this.stageOptions.count) || 5,
          researchNote: this.stageOptions.researchNote || ''
        }, 'Discover new parts', 'stage')
      } finally {
        this.stageBusy = null
      }
    },
    prettyStatus (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
    },
    async refreshSelectedDetail () {
      if (!this.selected || !this.selected.id) return null
      const { data } = await axios.get(
        `${apiDomain}part-candidates/${this.selected.id}`,
        { headers: this.authHeaders() })
      const next = data.candidate || data.partCandidate || data.data || data
      this.selected = next
      this.reviewNotes = this.selected.review_notes || ''
      this.syncPriceEditor(next)
      this.syncFinalSpecForm(next)
      return next
    },
    shouldShowPriceRefresh (candidate) {
      if (!candidate) return false
      return !this.candidatePrice(candidate).ok || this.availabilitySuggestsUnavailable(candidate)
    },
    shouldShowPriceReopen (candidate) {
      return this.shouldShowPriceRefresh(candidate) && ['rejected', 'scrape_failed'].indexOf(candidate.status) >= 0
    },
    syncPriceEditor (candidate) {
      const raw = this.firstValidPriceValue(candidate)
      this.priceEditValue = raw !== null && raw !== undefined && raw !== '' ? this.formatCandidatePrice(raw) : ''
    },
    normalizeManualPrice (value) {
      const text = String(value === null || value === undefined ? '' : value).trim()
      const numericText = text.replace(/[$,\s]/g, '')
      const amount = Number(numericText)
      if (!text || isNaN(amount) || amount <= 0 || !isFinite(amount)) {
        throw new Error('Enter a price greater than 0.')
      }
      return `$${amount.toFixed(2)}`
    },
    priceFinalSpecPayload (price, candidate) {
      const sourceCandidate = candidate || this.selected || {}
      const source = sourceCandidate.final_spec && typeof sourceCandidate.final_spec === 'object'
        ? this.cloneJson(sourceCandidate.final_spec)
        : this.emptyFinalSpec()
      const payload = this.emptyFinalSpec()
      ;['general', 'specs', 'compats'].forEach((group) => {
        const values = source[group] && typeof source[group] === 'object' ? source[group] : {}
        payload[group] = Object.assign({}, values)
      })
      payload.general.amzn_price = price
      payload.general.price = price
      return payload
    },
    async saveManualPrice () {
      if (!this.selected) return
      let price
      try {
        price = this.normalizeManualPrice(this.priceEditValue)
      } catch (e) {
        this.priceEditKind = 'error'
        this.priceEditMessage = e.message
        return
      }

      this.busy = 'priceSave'
      this.priceEditMessage = ''
      this.priceEditKind = 'neutral'
      try {
        const finalSpec = this.priceFinalSpecPayload(price)
        const { data } = await axios.post(`${apiDomain}part-candidates/${this.selected.id}`,
          {
            status: this.selected.status,
            review_notes: this.reviewNotes,
            price,
            amzn_price: price,
            amazon_price: price,
            current_price: price,
            final_spec: finalSpec
          },
          { headers: this.authHeaders() })
        const returned = (data && (data.candidate || data.partCandidate || data.data)) || null
        const nextSource = returned || this.selected
        const fallback = Object.assign({}, nextSource, {
          price,
          amzn_price: price,
          amazon_price: price,
          current_price: price,
          final_spec: this.priceFinalSpecPayload(price, nextSource)
        })
        this.applyReturnedCandidate(fallback)
        this.priceEditValue = price
        this.priceEditKind = 'success'
        this.priceEditMessage = `Price saved: ${price}`
        this.priceRefreshMessage = ''
        this.error = null
      } catch (e) {
        this.priceEditKind = 'error'
        this.priceEditMessage = this.errMsg(e)
        this.error = this.errMsg(e)
      } finally {
        this.busy = null
      }
    },
    availabilitySuggestsUnavailable (candidate) {
      const values = [
        candidate && candidate.price,
        candidate && candidate.amzn_price,
        candidate && candidate.amazon_price,
        candidate && candidate.current_price,
        candidate && candidate.raw_product && candidate.raw_product.price,
        candidate && candidate.raw_product && candidate.raw_product.availabilityRaw,
        candidate && candidate.raw_product && candidate.raw_product.availability,
        candidate && candidate.rawProduct && candidate.rawProduct.price,
        candidate && candidate.rawProduct && candidate.rawProduct.availabilityRaw,
        candidate && candidate.rawProduct && candidate.rawProduct.availability
      ].filter((value) => value !== undefined && value !== null)
      const text = values.map((value) => {
        return typeof value === 'object' ? JSON.stringify(value) : String(value)
      }).join(' ')
      return /currently unavailable|unavailable|out of stock|not available/i.test(text)
    },
    applyPriceRefreshResult (run) {
      const stats = this.runStats(run)
      if (['failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0) {
        this.priceRefreshKind = 'error'
        this.priceRefreshMessage = stats.error || stats.lastError || stats.message || 'Price refresh failed'
        return
      }

      const statusFromStats = stats.newStatus || stats.status || stats.reopenedStatus ||
        (stats.candidate && stats.candidate.status) ||
        (stats.updatedCandidate && stats.updatedCandidate.status)
      const reopened = !!(stats.reopened || stats.reopenedCount || stats.reopenedCandidate || stats.reopenedIds)

      if (reopened && this.selected) {
        this.selected.status = statusFromStats || 'needs_review'
      }

      const price = this.candidatePrice(this.selected)
      const statPrice = stats.price || stats.updatedPrice || stats.amzn_price || stats.amazonPrice
      if (price.ok) {
        this.priceRefreshKind = 'success'
        this.priceRefreshMessage = `Price updated: ${price.label}`
      } else if (this.isUsablePriceValue(statPrice)) {
        this.priceRefreshKind = 'success'
        this.priceRefreshMessage = `Price updated: ${this.formatCandidatePrice(statPrice)}`
      } else if (stats.unavailable || stats.currentlyUnavailable || this.availabilitySuggestsUnavailable(this.selected)) {
        this.priceRefreshKind = 'warning'
        this.priceRefreshMessage = 'Amazon currently unavailable'
      } else {
        this.priceRefreshKind = 'warning'
        this.priceRefreshMessage = 'No usable price found'
      }
    },
    runStats (run) {
      return (run && (run.stats || run.result || run.output)) || {}
    },
    async openDetail (c) {
      // Show the list version immediately, then hydrate with the full record.
      const sameCandidate = this.selected && c && this.selected.id === c.id
      this.selected = c
      this.reviewNotes = c.review_notes || ''
      this.syncPriceEditor(c)
      this.syncFinalSpecForm(c)
      if (!sameCandidate) {
        if (this.runPollTimers.candidate) clearTimeout(this.runPollTimers.candidate)
        this.$delete(this.jobRuns, 'candidate')
        this.priceRefreshMessage = ''
        this.priceRefreshKind = 'neutral'
        this.priceEditMessage = ''
        this.priceEditKind = 'neutral'
        this.issueBusy = {}
        this.issueResearchResults = {}
        this.issuePanelOpen = false
        this.sendStageTarget = this.defaultSendStageTarget(c)
      }
      try {
        await this.refreshSelectedDetail()
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    closeDetail () {
      this.selected = null
    },
    async setStatus (status) {
      if (!this.selected) return
      this.busy = status
      try {
        await axios.post(`${apiDomain}part-candidates/${this.selected.id}`,
          { status, review_notes: this.reviewNotes },
          { headers: this.authHeaders() })
        this.notice = `Marked “${this.candidateName(this.selected)}” as ${this.statusLabel(status)}.`
        this.error = null
        this.afterAction()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.busy = null
      }
    },
    defaultSendStageTarget (candidate) {
      const current = candidate && candidate.status
      const first = this.sendStageOptions.find((target) => target.status !== current)
      return first ? first.status : 'discovered'
    },
    async sendToSelectedStage () {
      if (!this.selected || !this.sendStageTarget || this.selected.status === this.sendStageTarget) return
      await this.setStatus(this.sendStageTarget)
    },
    // Send back to the queue for another enrichment pass.
    async sendBack () {
      await this.setStatus('discovered')
      this.notice = `Sent back to discovered. Run enrichment to reprocess.`
    },
    async importCandidate (allowUnavailable) {
      if (!this.selected) return
      const unavailable = !!allowUnavailable
      if (unavailable && !this.canImportUnavailable) return
      if (!unavailable && !this.canImportCandidate) return
      this.busy = unavailable ? 'importUnavailable' : 'import'
      try {
        const { data } = await axios.post(`${apiDomain}part-candidates/${this.selected.id}/import`,
          { review_notes: this.reviewNotes, allowUnavailable: unavailable },
          { headers: this.authHeaders() })
        if (data && data.importedUnavailable) {
          this.notice = `Imported "${this.candidateName(this.selected)}" as unavailable. It is hidden from the builder and will be monitored until price returns.`
        } else {
          this.notice = `Imported "${this.candidateName(this.selected)}" into the catalog.`
        }
        this.error = null
        this.afterAction()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.busy = null
      }
    },
    async saveNotes () {
      if (!this.selected) return
      this.busy = 'notes'
      try {
        await axios.post(`${apiDomain}part-candidates/${this.selected.id}`,
          { status: this.selected.status, review_notes: this.reviewNotes },
          { headers: this.authHeaders() })
        this.selected.review_notes = this.reviewNotes
        this.notice = 'Notes saved.'
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.busy = null
      }
    },
    async runEnrichment () {
      this.enriching = true
      try {
        await axios.post(`${apiDomain}jobs/enrich-candidates/run`,
          { options: { retryFailed: this.includeFailed } },
          { headers: this.authHeaders() })
        this.notice = 'Enrichment job started — refresh in a moment to see updated candidates.'
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.enriching = false
      }
    },
    async runSpecEnrichment () {
      this.specEnriching = true
      try {
        await axios.post(`${apiDomain}jobs/enrich-candidate-specs/run`,
          { options: { type: 'all', limit: 0, delayMs: 1500 } },
          { headers: this.authHeaders() })
        this.notice = 'Missing-spec research job started. Candidates stay in needs review and source notes will be added to issues.'
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.specEnriching = false
      }
    },
    // Deterministic gate: clean candidates move needs_review -> validated; ones
    // with blockers stay needs_review with "Validation blocker:" lines appended.
    async runValidation () {
      this.validating = true
      try {
        await axios.post(`${apiDomain}jobs/validate-candidates/run`,
          { options: {} },
          { headers: this.authHeaders() })
        this.notice = 'Validation gate started. Clean candidates become "validated"; blockers stay in needs review with reasons added.'
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.validating = false
      }
    },
    // Trace which job run produced/last touched this candidate.
    viewSourceRun (c) {
      if (!c || !c.source_job_run_id) return
      this.$router.push({ path: '/users/jobs', query: { run: String(c.source_job_run_id) } }).catch(() => {})
    },
    afterAction () {
      // The item likely no longer matches the current filter; drop it and close.
      const id = this.selected && this.selected.id
      this.closeDetail()
      this.candidates = this.candidates.filter((c) => c.id !== id)
      this.refreshCandidates()
    },
    candidateName (c) {
      return [c.brand, c.model].filter(Boolean).join(' ') || c.asin || `Candidate #${c.id}`
    },
    candidatePrice (c) {
      const raw = this.firstValidPriceValue(c)
      if (raw !== null && raw !== undefined && raw !== '') {
        return {
          ok: true,
          label: this.formatCandidatePrice(raw),
          title: 'Current Amazon price captured during scrape'
        }
      }
      return {
        ok: false,
        label: 'Price not fetched',
        title: 'GixCore could not fetch a current parseable Amazon price for this candidate'
      }
    },
    primarySpecChips (c) {
      if (!c) return []
      const finalSpec = c.final_spec || {}
      const general = finalSpec.general || {}
      const specs = finalSpec.specs || {}
      const compats = finalSpec.compats || {}
      const fields = [
        { key: 'brand', label: 'Brand', value: general.brand || c.brand },
        { key: 'socket', label: 'Socket', value: this.firstSpecValue([specs.Socket, specs.socket, compats.socket]) },
        { key: 'chipset', label: 'Chipset', value: this.firstSpecValue([specs.Chipset, specs.chipset, compats.chipset]) },
        { key: 'form_factor', label: 'Form factor', value: this.firstSpecValue([general.form_factor, general.formFactor, specs.Form_Factor, specs.form_factor, compats.form_factor]) },
        { key: 'memory', label: 'Memory', value: this.firstSpecValue([specs.Compatible_Memory, specs.compatible_memory, compats.ram, compats.memory]) },
        { key: 'ram_speed', label: 'RAM speed', value: this.firstSpecValue([compats.ram_speed, specs.RAM_Speed, specs.ram_speed]) },
        { key: 'pcie', label: 'PCIe', value: this.firstSpecValue([compats.pci_e, specs.PCI_E, specs.pci_e, specs.Bus_Speed]) },
        { key: 'm2', label: 'M.2', value: this.firstSpecValue([compats.m_2, compats.M_2_Slot, specs.M_2_Slot, specs.m_2]) }
      ]
      return fields
        .filter((field) => field.value !== null && field.value !== undefined && field.value !== '' && String(field.value).toLowerCase() !== 'n/a')
        .slice(0, 6)
    },
    firstSpecValue (values) {
      for (const value of values) {
        if (value !== null && value !== undefined && value !== '') return value
      }
      return ''
    },
    firstValidPriceValue (c) {
      if (!c) return null
      const sources = [
        c.price,
        c.amzn_price,
        c.amazon_price,
        c.current_price,
        c.raw_product && c.raw_product.price,
        c.rawProduct && c.rawProduct.price,
        c.raw_product && c.raw_product.amzn_price,
        c.rawProduct && c.rawProduct.amzn_price,
        c.final_spec && c.final_spec.price,
        c.final_spec && c.final_spec.general && c.final_spec.general.price,
        c.final_spec && c.final_spec.general && c.final_spec.general.amzn_price,
        c.final_spec && c.final_spec.general && c.final_spec.general.amznPrice,
        c.final_spec && c.final_spec.specs && c.final_spec.specs.price,
        c.final_spec && c.final_spec.specs && c.final_spec.specs.amzn_price
      ]
      for (const value of sources) {
        if (this.isUsablePriceValue(value)) return value
      }
      return null
    },
    isUsablePriceValue (value) {
      if (value === null || value === undefined || value === '') return false
      if (typeof value === 'number') return value > 0 && isFinite(value)
      if (typeof value === 'object') {
        const nested = value.amount || value.value || value.price || value.raw || value.display
        return this.isUsablePriceValue(nested)
      }
      const text = String(value).trim()
      if (!text) return false
      if (/^(n\/a|na|null|undefined|none|-|--|\$?0(\.00)?)$/i.test(text)) return false
      if (/could not|not found|unavailable|missing|failed|invalid|parse/i.test(text)) return false
      return /\d/.test(text)
    },
    formatCandidatePrice (value) {
      if (typeof value === 'number') {
        return `$${value.toFixed(2)}`
      }
      if (value && typeof value === 'object') {
        return this.formatCandidatePrice(value.display || value.raw || value.price || value.amount || value.value)
      }
      const text = String(value).trim()
      const numeric = text.replace(/[$,\s]/g, '')
      const amount = Number(numeric)
      if (!isNaN(amount) && amount > 0) return `$${amount.toFixed(2)}`
      return text
    },
    scrapeOutcome (candidate) {
      const status = String((candidate && candidate.status) || '').toLowerCase()
      const error = String((candidate && candidate.error) || '').trim()
      const importedId = candidate && (candidate.imported_part_id || candidate.importedPartId)
      if (status === 'duplicate') {
        return {
          show: true,
          tone: 'neutral',
          label: 'Already in catalog',
          message: error || (importedId ? `This ASIN matches catalog part #${importedId}.` : 'This ASIN appears to already exist in the catalog.'),
          action: importedId ? 'Open the existing catalog part before importing another copy.' : 'Review the matching catalog item before continuing.'
        }
      }
      if (status === 'rejected') {
        return {
          show: true,
          tone: 'bad',
          label: 'Dead or rejected ASIN',
          message: error || 'The scrape concluded this candidate should not be queued again.',
          action: 'Terminal outcome: discard it unless the ASIN/source is corrected.'
        }
      }
      if (status === 'scrape_failed') {
        return {
          show: true,
          tone: 'warn',
          label: 'Scrape failed',
          message: error || 'The scraper could not get enough product data from Amazon.',
          action: 'Retry Stage 2 for transient failures, or refresh price if only availability/price failed.'
        }
      }
      if (error) {
        return {
          show: true,
          tone: /404|dead|reject|invalid/i.test(error) ? 'bad' : 'warn',
          label: 'Scrape note',
          message: error,
          action: 'Use the raw Amazon scrape and issues below to decide whether to retry, research, or import as unavailable.'
        }
      }
      return { show: false, tone: 'neutral', label: '', message: '', action: '' }
    },
    sourceUrl (c) {
      return c && (c.source_url || c.sourceUrl || (c.source && c.source.url)) || ''
    },
    sourceTitle (c) {
      return c && (c.source_title || c.sourceTitle || (c.source && c.source.title)) || this.sourceUrl(c)
    },
    onboardMeta (c) {
      const meta = c && (c.source_meta || c.sourceMeta || (c.source && c.source.meta))
      if (meta && typeof meta === 'object' && meta.onboard && typeof meta.onboard === 'object') {
        return meta.onboard
      }
      return null
    },
    onboardBadge (c) {
      const o = this.onboardMeta(c)
      if (!o) return this.requestBadge(c)
      const dir = (o.directive && typeof o.directive === 'object') ? o.directive : {}
      const tierRaw = String(o.tier || (dir.priority && dir.priority.tier) || 'variety').toLowerCase()
      const tier = ['platform-keystone', 'subpath-keystone', 'variety'].indexOf(tierRaw) >= 0 ? tierRaw : 'variety'
      const partType = o.part_type || dir.part_type || (c && c.part_type) || ''
      const cons = o.constraints || dir.constraints || {}
      const seg = dir.segment || {}
      const labelBits = []
      if (cons.ram_type || seg.ram_gen) labelBits.push(String(cons.ram_type || seg.ram_gen).toUpperCase())
      else if (cons.socket || seg.socket) labelBits.push(cons.socket || seg.socket)
      if (cons.form_factor || seg.form_factor) labelBits.push(cons.form_factor || seg.form_factor)
      const typeLabel = this.onboardTypeLabel(partType)
      const gap = [labelBits.join(' '), typeLabel, 'gap'].filter(Boolean).join(' ')
      const tierLabel = { 'platform-keystone': 'platform-keystone', 'subpath-keystone': 'subpath-keystone', variety: 'variety' }[tier]
      return {
        tier,
        text: `Onboarded to fill: ${gap} (${tierLabel})`,
        intent: o.intent || dir.reason || ''
      }
    },
    /** Candidates that came from the request queue say who asked, and for what. */
    requestBadge (c) {
      const meta = c && (c.source_meta || c.sourceMeta)
      const r = meta && typeof meta === 'object' && meta.request && typeof meta.request === 'object' ? meta.request : null
      if (!r) return null
      const what = r.asin ? `ASIN ${r.asin}` : r.upc ? `UPC ${r.upc}` : (r.raw_text || 'a part')
      const who = { ai_suggest: 'AI pick', site_search: 'Searched on site', search_console: 'Searched on Google' }[r.source] || 'Requested'
      return {
        tier: 'request',
        text: `${who}: ${what}`,
        intent: r.accept_unavailable ? 'Asked for explicitly, so it is onboarded even while Amazon cannot sell it.' : ''
      }
    },
    // ---- Discover -> review -> Onboard ----
    discoverScopeNum (key) {
      return Number((this.discover.scope || {})[key]) || 0
    },
    discoverMetric (key) {
      const ledgerValue = this.discover.ledger && this.discover.ledger.summary
        ? this.discover.ledger.summary[key]
        : null
      if (ledgerValue !== undefined && ledgerValue !== null) return Number(ledgerValue) || 0
      return Number((this.discover.progress || {})[key]) || 0
    },
    discoverProgressText () {
      const progress = this.discover.progress || {}
      const active = progress.activeDirective || null
      const completed = Number(progress.directivesCompleted) || 0
      const total = Number(progress.directivesTotal) || 0
      if (active) {
        const label = [this.onboardTypeLabel(active.partType), active.label].filter(Boolean).join(' · ')
        return `Search ${active.index || completed + 1} of ${total || '?'}${label ? ` — ${label}` : ''}. Partial results are shown and preserved live.`
      }
      return `${completed} of ${total || '?'} searches complete. Partial results are shown and preserved live.`
    },
    previewDecision (c) {
      return c ? (this.discover.decisions[c.previewId] || null) : null
    },
    discoverOptions () {
      const type = this.normalizedType()
      const body = {
        type: type || 'all',
        maxDirectives: Math.max(1, Number(this.stageOptions.count) || 5),
        scope: this.stageOptions.onboardScope || 'active'
      }
      const minCount = Number(this.stageOptions.minCount)
      body.minCount = (Number.isFinite(minCount) && minCount > 0) ? minCount : null
      return body
    },
    async runDiscover () {
      if (this.discover.running || this.discover.committing) return
      this.resetDiscover()
      this.discover.running = true
      this.discover.error = ''
      try {
        const { data } = await axios.post(`${apiDomain}admin/onboard/discover`,
          this.discoverOptions(), { headers: this.authHeaders() })
        const id = (data && (data.discoverId || data.jobRunId ||
          (data.watch && data.watch.resultUrl && String(data.watch.resultUrl).split('/').pop()))) || null
        if (!id) {
          this.discover.running = false
          this.discover.error = 'Discover did not return a run id.'
          return
        }
        this.discover.runId = id
        this.pollDiscover()
      } catch (e) {
        this.discover.running = false
        const status = e && e.response && e.response.status
        this.discover.error = status === 409
          ? 'An onboarding run is already active. Wait for it to finish, then Discover again.'
          : this.errMsg(e)
      }
    },
    pollDiscover () {
      const id = this.discover.runId
      if (!id) return
      if (this.discoverPollTimer) clearTimeout(this.discoverPollTimer)
      this.discoverPollTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get(`${apiDomain}jobs/runs/${id}`, { headers: this.authHeaders() })
          const run = this.unwrapRun(data)
          await this.refreshDiscoverLedger(id)
          this.applyDiscoverProgress(run)
          if (this.isTerminalRun(run)) {
            this.discover.running = false
            if (['failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0) {
              this.discover.error = run.error || 'Discover run failed.'
              return
            }
            this.applyDiscoverResult(run)
            return
          }
          this.pollDiscover()
        } catch (e) {
          this.discover.running = false
          this.discover.error = this.errMsg(e)
        }
      }, 2500)
    },
    async refreshDiscoverLedger (id) {
      try {
        const { data } = await axios.get(`${apiDomain}admin/onboard/discover/${id}/results`, { headers: this.authHeaders() })
        const ledger = data && data.ledger ? data.ledger : null
        if (ledger) this.discover.ledger = {
          summary: ledger.summary || {},
          rounds: Array.isArray(ledger.rounds) ? ledger.rounds : [],
          results: Array.isArray(ledger.results) ? ledger.results : []
        }
      } catch (e) {
        // The job result remains authoritative; a transient ledger read failure
        // should not stop polling or hide candidates already published in stats.
      }
    },
    applyDiscoverProgress (run) {
      const stats = this.runStats(run) || {}
      const d = stats.discover || null
      if (!d) return
      this.discover.scope = d.scope || this.discover.scope
      this.discover.directives = Array.isArray(d.directives) ? d.directives : this.discover.directives
      this.discover.cleanup = Array.isArray(d.cleanup) ? d.cleanup : this.discover.cleanup
      this.discover.candidates = Array.isArray(d.candidates) ? d.candidates : this.discover.candidates
      this.discover.progress = d.progress || this.discover.progress
      this.discover.warning = stats.partialReason || this.discover.warning
    },
    applyDiscoverResult (run) {
      const stats = this.runStats(run) || {}
      const d = stats.discover || {}
      this.discover.scope = d.scope || null
      this.discover.directives = Array.isArray(d.directives) ? d.directives : []
      this.discover.cleanup = Array.isArray(d.cleanup) ? d.cleanup : []
      this.discover.candidates = Array.isArray(d.candidates) ? d.candidates : []
      this.discover.progress = d.progress || {}
      this.discover.warning = stats.partialReason || ''
      this.discover.decisions = {}
      this.discover.index = 0
      this.discover.loaded = true
    },
    decidePreview (decision) {
      const c = this.currentPreview
      if (!c) return
      this.$set(this.discover.decisions, c.previewId, decision)
      this.nextPreview()
    },
    skipPreview () {
      this.nextPreview()
    },
    nextPreview () {
      if (this.discover.index < this.discover.candidates.length - 1) this.discover.index++
    },
    prevPreview () {
      if (this.discover.index > 0) this.discover.index--
    },
    async commitOnboard () {
      const previewIds = this.acceptedPreviewIds
      if (!previewIds.length || this.discover.committing) return
      this.discover.committing = true
      this.discover.error = ''
      try {
        const { data } = await axios.post(`${apiDomain}admin/onboard/commit`,
          { discoverId: this.discover.runId, previewIds, scrape: true },
          { headers: this.authHeaders() })
        const created = (data && data.created) || []
        const dups = (data && data.duplicates) || []
        this.notice = `Onboarded ${created.length} candidate${created.length === 1 ? '' : 's'}` +
          (dups.length ? `, ${dups.length} duplicate${dups.length === 1 ? '' : 's'} skipped` : '') + '.'
        this.error = null
        // Track the auto-triggered scrape so its progress shows in the run card.
        if (data && data.scrape && data.scrape.runId) {
          this.$set(this.jobRuns, 'stage', {
            id: data.scrape.runId, status: 'running', label: 'Scrape onboarded', jobKey: 'enrich-candidates'
          })
          this.pollRun('stage')
        }
        this.resetDiscover()
        await this.refreshCandidates()
      } catch (e) {
        this.discover.error = this.errMsg(e)
      } finally {
        this.discover.committing = false
      }
    },
    resetDiscover () {
      if (this.discoverPollTimer) clearTimeout(this.discoverPollTimer)
      this.discoverPollTimer = null
      Object.assign(this.discover, {
        running: false,
        loaded: false,
        runId: null,
        error: '',
        scope: null,
        directives: [],
        cleanup: [],
        candidates: [],
        progress: {},
        ledger: { summary: {}, rounds: [], results: [] },
        warning: '',
        resultsOpen: false,
        index: 0,
        decisions: {},
        committing: false,
        planOpen: false,
        cleanupOpen: false
      })
    },
    // Map a raw onboarding directive (from the scope preview) into a plan row.
    onboardPlanRow (dir) {
      const d = dir && typeof dir === 'object' ? dir : {}
      const tierRaw = String((d.priority && d.priority.tier) || d.tier || 'variety').toLowerCase()
      const tier = ['platform-keystone', 'subpath-keystone', 'variety'].indexOf(tierRaw) >= 0 ? tierRaw : 'variety'
      const partType = d.part_type || d.type || ''
      const cons = d.constraints || {}
      const seg = d.segment || {}
      const bits = []
      if (cons.ram_type || seg.ram_gen) bits.push(String(cons.ram_type || seg.ram_gen).toUpperCase())
      else if (cons.socket || seg.socket) bits.push(String(cons.socket || seg.socket))
      if (cons.form_factor || seg.form_factor) bits.push(String(cons.form_factor || seg.form_factor))
      const typeLabel = this.onboardTypeLabel(partType)
      const gap = [bits.join(' ').trim(), typeLabel].filter(Boolean).join(' ') || typeLabel || 'catalog'
      const count = [d.count, d.target, d.limit, d.want].find((v) => v !== undefined && v !== null)
      return {
        tier,
        tierLabel: tier,
        type: typeLabel,
        gap,
        reason: d.reason || d.intent || '',
        count: (count !== undefined && count !== null) ? count : null
      }
    },
    // Onboarding-specific progress chips for the run card (only shown when the stats exist).
    onboardRunProgress (run) {
      const key = String((run && (run.jobKey || run.job_key)) || '').toLowerCase()
      if (key === 'onboard-part-requests') {
        // Nested steps write dotted keys ("req12.scrape.enriched"), and several
        // share a leaf name, so read exact keys rather than matching by leaf.
        const stats = this.runStats(run)
        const chips = [
          { label: 'Requests', key: 'requestsSelected' },
          { label: 'Candidates', key: 'candidatesQueued' },
          { label: 'Already have', key: 'alreadyHave' },
          { label: 'Not on Amazon', key: 'notFound' }
        ]
          .filter((spec) => stats[spec.key] !== undefined && stats[spec.key] !== null && stats[spec.key] !== '')
          .map((spec) => ({ label: spec.label, value: stats[spec.key] }))
        const scraped = Object.keys(stats)
          .filter((key) => /\.scrape\.enriched$/.test(key))
          .reduce((sum, key) => sum + (Number(stats[key]) || 0), 0)
        if (scraped) chips.splice(2, 0, { label: 'Scraped', value: scraped })
        return chips
      }
      if (key && key !== 'catalog-auto-onboard') return []
      const specs = [
        { label: 'Dispatched', names: ['dispatched', 'directivesDispatched', 'directives', 'queued'] },
        { label: 'Scraped', names: ['scraped', 'enriched'] },
        { label: 'Candidates', names: ['candidates', 'candidatesCreated', 'created', 'stored'] },
        { label: 'Skipped', names: ['skipped'] }
      ]
      const out = []
      specs.forEach((spec) => {
        const value = this.runStatValue(run, spec.names)
        if (value !== null && value !== undefined && value !== '') out.push({ label: spec.label, value })
      })
      return out
    },
    onboardTypeLabel (type) {
      const map = { cpu: 'CPU', motherboard: 'Motherboard', ram: 'RAM', storage: 'Storage', gpu: 'GPU', psu: 'PSU', case: 'Case' }
      const key = String(type || '').toLowerCase()
      return map[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '')
    },
    sourceMetaText (c) {
      const meta = c && (c.source_meta || c.sourceMeta || (c.source && c.source.meta))
      if (!meta) return ''
      if (typeof meta === 'string') return meta
      if (typeof meta === 'object') {
        const parts = []
        if (meta.provider) parts.push(`Provider: ${meta.provider}`)
        if (meta.model) parts.push(`Model: ${meta.model}`)
        if (meta.verifiedAt || meta.verified_at) parts.push(`Verified: ${this.fmtTime(meta.verifiedAt || meta.verified_at)}`)
        if (parts.length) return parts.join(' - ')
        try { return JSON.stringify(meta) } catch (e) { return '' }
      }
      return String(meta)
    },
    statusLabel (s) {
      const map = {
        needs_review: 'Needs review',
        validated: 'Validated',
        discovered: 'Discovered',
        scrape_failed: 'Scrape failed',
        duplicate: 'Duplicate',
        imported: 'Imported',
        rejected: 'Rejected',
        proposed: 'Proposed',
        checking: 'Checking',
        available: 'Available',
        unavailable: 'Unavailable',
        not_found: 'Not found',
        deferred: 'Deferred',
        invalid: 'Invalid',
        response_incomplete: 'Incomplete',
        invalid_response: 'Invalid response',
        failed: 'Failed',
        completed: 'Completed',
        running: 'Running'
      }
      return map[s] || s
    },
    evaluationFor (candidate) {
      if (!candidate) return null
      const finalSpec = candidate.final_spec || {}
      return candidate.evaluation || candidate.evaluation_data || candidate.evaluationData ||
        candidate.spec_evaluation || candidate.specEvaluation ||
        finalSpec.evaluation || finalSpec.evaluation_data || finalSpec.evaluationData ||
        finalSpec.spec_evaluation || finalSpec.specEvaluation || null
    },
    normalizeEvaluationGroup (group) {
      const text = String(group || '').trim().toLowerCase()
      if (text === 'compat' || text === 'compatibility' || text === 'compatible' || text === 'compats') return 'compats'
      if (text === 'spec' || text === 'specification' || text === 'specifications' || text === 'specs') return 'specs'
      if (text === 'general' || text === 'catalog' || text === 'base') return 'general'
      return text
    },
    normalizeEvaluationPath (field, fallbackPath, fallbackGroup, fallbackKey) {
      const rawPath = field && (field.path || field.field || field.field_path || field.fieldPath || field.key)
      let path = String(rawPath || fallbackPath || '').trim()
      let group = this.normalizeEvaluationGroup(field && (field.group || field.section || field.category || field.scope || fallbackGroup))
      let key = String((field && (field.name || field.field_name || field.fieldName)) || fallbackKey || '').trim()

      if (path && path.indexOf('.') >= 0) {
        const parts = path.split('.')
        group = this.normalizeEvaluationGroup(parts.shift())
        key = parts.join('.')
      } else if (path && !key) {
        key = path
      }

      if (!group && fallbackGroup) group = this.normalizeEvaluationGroup(fallbackGroup)
      if (!group || !key || ['general', 'specs', 'compats'].indexOf(group) < 0) return null
      return {
        group,
        key,
        path: `${group}.${key}`
      }
    },
    normalizeEvaluationStatus (value) {
      const text = String(value || '').trim().toLowerCase()
      if (/block|error|required|invalid|missing/.test(text)) return 'blocker'
      if (/warn|unverified|could_not|needs/.test(text)) return 'warning'
      if (/estimate|estimated|rule|derived/.test(text)) return 'estimated'
      if (/ok|valid|verified|clean|source|pass/.test(text)) return 'ok'
      return text || 'ok'
    },
    evaluationFieldValue (field) {
      if (!field || typeof field !== 'object') return ''
      const keys = ['value', 'finalValue', 'final_value', 'current', 'currentValue', 'current_value', 'resolvedValue', 'resolved_value']
      for (const key of keys) {
        if (field[key] !== undefined && field[key] !== null) return field[key]
      }
      return ''
    },
    normalizeEvaluationProvenance (field) {
      if (!field || typeof field !== 'object') return null
      const provenance = field.provenance || field.source_provenance || field.sourceProvenance || field.field_meta || field.fieldMeta || field.meta || field.metadata
      if (provenance && typeof provenance === 'object') return provenance
      const hasInline = field.tier !== undefined || field.source || field.url || field.confidence !== undefined || field.provider || field.model
      if (!hasInline) return null
      return {
        tier: field.tier,
        source: field.source,
        url: field.url,
        confidence: field.confidence,
        provider: field.provider,
        model: field.model
      }
    },
    normalizeEvaluationField (field, fallbackPath, fallbackGroup, fallbackKey) {
      if (!field || typeof field !== 'object') {
        field = { value: field }
      }
      const pathInfo = this.normalizeEvaluationPath(field, fallbackPath, fallbackGroup, fallbackKey)
      if (!pathInfo) return null
      const value = this.evaluationFieldValue(field)
      const status = this.normalizeEvaluationStatus(field.status || field.severity || field.state || field.level || field.kind)
      return Object.assign({}, pathInfo, {
        value: this.formatSpecInputValue(value),
        status,
        missing: field.missing === true || this.isMissingSpecValue(value),
        description: field.description || field.tooltip || field.help || field.reason || field.message || '',
        expected: this.formatSpecInputValue(field.expected || field.expected_value || field.expectedValue || ''),
        suggested: this.formatSpecInputValue(field.suggested || field.suggested_value || field.suggestedValue || ''),
        provenance: this.normalizeEvaluationProvenance(field)
      })
    },
    evaluationFields (candidate) {
      const evaluation = this.evaluationFor(candidate)
      const fields = evaluation && evaluation.fields
      if (!fields) return []
      const rows = []
      const push = (field, path, group, key) => {
        const row = this.normalizeEvaluationField(field, path, group, key)
        if (row) rows.push(row)
      }

      if (Array.isArray(fields)) {
        fields.forEach((field, index) => push(field, '', '', String(index)))
      } else if (typeof fields === 'object') {
        Object.keys(fields).forEach((groupOrPath) => {
          const value = fields[groupOrPath]
          const normalizedGroup = this.normalizeEvaluationGroup(groupOrPath)
          if (['general', 'specs', 'compats'].indexOf(normalizedGroup) >= 0 && Array.isArray(value)) {
            value.forEach((field, index) => push(field, '', normalizedGroup, field && (field.key || field.name || field.field || index)))
          } else if (['general', 'specs', 'compats'].indexOf(normalizedGroup) >= 0 && value && typeof value === 'object') {
            Object.keys(value).forEach((key) => push(value[key], `${normalizedGroup}.${key}`, normalizedGroup, key))
          } else {
            push(value, groupOrPath, '', '')
          }
        })
      }

      const seen = {}
      return rows.filter((row) => {
        const key = row.path.toLowerCase()
        if (seen[key]) return false
        seen[key] = true
        return true
      })
    },
    evaluationProblemSource (candidate) {
      const evaluation = this.evaluationFor(candidate)
      return evaluation && (evaluation.problems || evaluation.findings || evaluation.issues) || null
    },
    normalizeEvaluationProblem (problem, fallbackPath, index) {
      if (!problem || typeof problem !== 'object') {
        problem = { message: this.formatIssue(problem) }
      }
      const path = this.normalizeIssueFieldPath(problem.field || problem.path || problem.field_path || problem.fieldPath || fallbackPath || '')
      const status = this.normalizeEvaluationStatus(problem.status || problem.severity || problem.state || problem.level || problem.kind)
      const message = problem.message || problem.description || problem.reason || this.formatIssue(problem)
      return {
        key: (problem.id || problem.issueId || problem.issue_id || path || 'problem') + ':' + index,
        id: problem.id || problem.issueId || problem.issue_id || null,
        field: path,
        status,
        message,
        current: this.formatSpecInputValue(problem.current || problem.current_value || problem.currentValue || problem.value || ''),
        expected: this.formatSpecInputValue(problem.expected || problem.expected_value || problem.expectedValue || ''),
        suggested: this.formatSpecInputValue(problem.suggested || problem.suggested_value || problem.suggestedValue || ''),
        raw: Object.assign({}, problem, { field: path, message })
      }
    },
    evaluationProblems (candidate) {
      const problems = this.evaluationProblemSource(candidate)
      if (!problems) return []
      const rows = []
      if (Array.isArray(problems)) {
        problems.forEach((problem, index) => rows.push(this.normalizeEvaluationProblem(problem, '', index)))
      } else if (typeof problems === 'object') {
        Object.keys(problems).forEach((path, index) => rows.push(this.normalizeEvaluationProblem(problems[path], path, index)))
      }
      return rows.filter((row) => row && this.isActionableFindingRow(row))
    },
    findingRows (candidate) {
      const evaluation = this.evaluationFor(candidate)
      const problemSource = this.evaluationProblemSource(candidate)
      const problems = this.evaluationProblems(candidate)
      if (evaluation && problemSource) return problems
      return ((candidate && candidate.issues) || []).map((issue, index) => ({
        key: this.issueKey(issue, index),
        id: this.issueIdentifier(issue),
        field: this.normalizeIssueFieldPath(this.issueFieldPath(issue)),
        status: this.issueSeverity(issue),
        message: this.formatIssue(issue),
        current: '',
        expected: '',
        suggested: '',
        raw: issue
      })).filter((row) => this.isActionableFindingRow(row))
    },
    isActionableFindingRow (row) {
      if (!row || !row.message) return false
      const message = String(row.message || '').toLowerCase()
      if (/research source|official research filled|research complete|research updated|source:/.test(message)) return false
      if (row.status === 'blocker' || row.status === 'warning') return true
      if (/validation blocker|required field|must be populated|missing_required|could not verify|could not|failed|invalid/.test(message)) return true
      return false
    },
    evaluationCounts (candidate) {
      const evaluation = this.evaluationFor(candidate)
      const raw = evaluation && evaluation.counts && typeof evaluation.counts === 'object' ? evaluation.counts : {}
      const problems = this.findingRows(candidate)
      const valueFor = (...keys) => {
        for (const key of keys) {
          if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') return Number(raw[key]) || 0
        }
        return null
      }
      const counts = {
        total: problems.length,
        blockers: valueFor('blockers', 'blocker'),
        warnings: valueFor('warnings', 'warning'),
        estimated: valueFor('estimated', 'estimates'),
        ok: valueFor('ok', 'valid', 'verified'),
        problems: problems.length
      }
      if (counts.blockers === null) counts.blockers = problems.filter((row) => row.status === 'blocker').length
      if (counts.warnings === null) counts.warnings = problems.filter((row) => row.status === 'warning').length
      if (counts.estimated === null) counts.estimated = this.evaluationFields(candidate).filter((row) => row.status === 'estimated').length
      if (counts.ok === null) counts.ok = this.evaluationFields(candidate).filter((row) => row.status === 'ok').length
      return counts
    },
    evaluationHealthBadges (candidate) {
      const evaluation = this.evaluationFor(candidate)
      if (!evaluation) return []
      const counts = this.evaluationCounts(candidate)
      return [
        { key: 'blockers', label: 'blockers', value: counts.blockers, tone: 'blocker' },
        { key: 'warnings', label: 'warnings', value: counts.warnings, tone: 'warning' },
        { key: 'estimated', label: 'estimated', value: counts.estimated, tone: 'estimated' },
        { key: 'ok', label: 'ok', value: counts.ok, tone: 'ok' }
      ].filter((badge) => badge.value !== undefined && badge.value !== null)
    },
    fieldStatusClass (status) {
      const normalized = this.normalizeEvaluationStatus(status)
      if (['blocker', 'warning', 'estimated', 'ok'].indexOf(normalized) >= 0) return normalized
      return 'ok'
    },
    fieldStatusLabel (status) {
      const map = {
        blocker: 'Blocker',
        warning: 'Warning',
        estimated: 'Estimated',
        ok: 'OK'
      }
      const normalized = this.fieldStatusClass(status)
      return map[normalized] || this.prettyStatus(status)
    },
    findingStatusLabel (finding) {
      return this.fieldStatusLabel(finding && finding.status)
    },
    findingResearchKey (finding, index) {
      if (finding && finding.field) return this.issueKeyForFieldPath(finding.field)
      return this.issueKey(finding && finding.raw ? finding.raw : finding, index)
    },
    findingResearchState (finding, index) {
      return this.issueResearchResults[this.findingResearchKey(finding, index)] || {
        label: '',
        tone: 'neutral',
        message: '',
        suggestion: ''
      }
    },
    findingResearchSucceeded (finding, index) {
      const state = this.findingResearchState(finding, index)
      const raw = state && state.raw ? state.raw : {}
      if (state.tone !== 'good') return false
      const remaining = raw.remaining !== undefined ? Number(raw.remaining) : null
      const missing = this.candidateListFromKeys(raw, ['missingFieldsRemaining', 'missing_fields_remaining', 'remainingFields', 'remaining_fields'])
      if (remaining !== null && !isNaN(remaining)) return remaining === 0
      return missing.length === 0
    },
    clearFindingResearchState (finding, index) {
      this.$delete(this.issueResearchResults, this.findingResearchKey(finding, index))
    },
    canResearchFinding (finding) {
      return !!(finding && (finding.field || ['blocker', 'warning'].indexOf(finding.status) >= 0))
    },
    isFindingResearchBusy (finding, index) {
      if (finding === 'all' || index === 'all') return !!this.issueBusy[this.issueKey('all')]
      return !!this.issueBusy[this.findingResearchKey(finding, index)]
    },
    researchFinding (finding, index) {
      if (finding && finding.field) {
        const parts = this.normalizeIssueFieldPath(finding.field).split('.')
        const group = this.normalizeEvaluationGroup(parts.shift())
        const key = parts.join('.')
        if (group && key) return this.researchSpecField(group, key)
      }
      return this.researchCandidateIssue(finding && finding.raw ? finding.raw : finding, index)
    },
    scrollToSpecField (path) {
      this.$nextTick(() => {
        const fieldPath = this.normalizeIssueFieldPath(path)
        if (!fieldPath) return
        const el = this.$el.querySelector(`[data-field-path="${fieldPath.toLowerCase()}"]`)
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    // final_spec = { general, specs, compats } of key/value objects.
    specGroups (finalSpec) {
      const toRows = (obj) => {
        if (!obj || typeof obj !== 'object') return []
        return Object.keys(obj).map((k) => ({
          k: k,
          v: typeof obj[k] === 'object' ? JSON.stringify(obj[k]) : String(obj[k])
        }))
      }
      return [
        { key: 'general', label: 'General', rows: toRows(finalSpec.general) },
        { key: 'specs', label: 'Specs', rows: toRows(finalSpec.specs) },
        { key: 'compats', label: 'Compatibility', rows: toRows(finalSpec.compats) }
      ].filter((g) => g.rows.length)
    },
    specGroupProblemCount (group) {
      return (group && group.rows ? group.rows : []).filter((row) => {
        const status = String(row.status || '').toLowerCase()
        return row.missing || status === 'blocker' || status === 'warning'
      }).length
    },
    emptyFinalSpec () {
      return {
        general: {},
        specs: {},
        compats: {}
      }
    },
    cloneJson (value) {
      if (value === null || value === undefined) return value
      try {
        return JSON.parse(JSON.stringify(value))
      } catch (e) {
        return value
      }
    },
    normalizeFinalSpecForForm (finalSpec, issues) {
      const source = finalSpec && typeof finalSpec === 'object' ? this.cloneJson(finalSpec) : this.emptyFinalSpec()
      const next = this.emptyFinalSpec()
      ;['general', 'specs', 'compats'].forEach((group) => {
        const values = source[group] && typeof source[group] === 'object' ? source[group] : {}
        Object.keys(values).forEach((key) => {
          next[group][key] = this.formatSpecInputValue(values[key])
        })
      })
      this.specIssueFieldPaths(issues).forEach((path) => {
        const parts = path.split('.')
        const group = parts[0] === 'compat' ? 'compats' : parts[0]
        const key = parts.slice(1).join('.')
        if (!next[group] || !key) return
        if (next[group][key] === undefined) next[group][key] = ''
      })
      return next
    },
    syncFinalSpecForm (candidate) {
      const next = this.normalizeFinalSpecForForm(candidate && candidate.final_spec, candidate && candidate.issues)
      this.evaluationFields(candidate).forEach((row) => {
        if (!next[row.group]) return
        if (next[row.group][row.key] === undefined) {
          next[row.group][row.key] = this.formatSpecInputValue(row.value)
        }
      })
      this.editableFinalSpec = next
      this.finalSpecSnapshot = this.stringifyFinalSpec(next)
      this.finalSpecEdited = false
      this.finalSpecMessage = ''
      this.finalSpecMessageKind = 'neutral'
    },
    stringifyFinalSpec (finalSpec) {
      if (!finalSpec) return ''
      const normalize = {}
      ;['general', 'specs', 'compats'].forEach((group) => {
        normalize[group] = {}
        const values = finalSpec[group] || {}
        Object.keys(values).sort((a, b) => a.localeCompare(b)).forEach((key) => {
          normalize[group][key] = values[key]
        })
      })
      return JSON.stringify(normalize)
    },
    formatSpecInputValue (value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') {
        try { return JSON.stringify(value) } catch (e) { return String(value) }
      }
      return String(value)
    },
    isMissingSpecValue (value) {
      if (value === null || value === undefined) return true
      const text = String(value).trim()
      return !text || /^(n\/a|na|null|undefined|none|-|--|unknown)$/i.test(text)
    },
    isLongSpecValue (value) {
      const text = String(value === null || value === undefined ? '' : value)
      return text.length > 92 || /\n/.test(text)
    },
    specCompareValue (value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') {
        try { return JSON.stringify(value) } catch (e) { return String(value) }
      }
      return String(value).trim()
    },
    flattenSpecValues (spec) {
      const rows = {}
      const source = spec && typeof spec === 'object' ? spec : {}
      ;['general', 'specs', 'compats', 'compat'].forEach((group) => {
        const normalizedGroup = group === 'compat' ? 'compats' : group
        const values = source[group] && typeof source[group] === 'object' ? source[group] : {}
        Object.keys(values).forEach((key) => {
          rows[`${normalizedGroup}.${key}`] = this.specCompareValue(values[key])
        })
      })
      return rows
    },
    verifyIssueForPath (candidate, path) {
      const normalizedPath = this.normalizeIssueFieldPath(path)
      const issues = (candidate && candidate.issues) || []
      const exact = issues.find((issue) => this.normalizeIssueFieldPath(this.issueFieldPath(issue)) === normalizedPath)
      if (exact) return this.formatIssue(exact)
      const leaf = normalizedPath.split('.').pop()
      const loose = issues.find((issue) => {
        const text = this.formatIssue(issue).toLowerCase()
        return text.indexOf(normalizedPath.toLowerCase()) >= 0 || (leaf && text.indexOf(String(leaf).toLowerCase()) >= 0)
      })
      return loose ? this.formatIssue(loose) : ''
    },
    verifyDiffRows (candidate) {
      if (!candidate || !candidate.draft_spec || !candidate.final_spec) return []
      const draft = this.flattenSpecValues(candidate.draft_spec)
      const final = this.flattenSpecValues(candidate.final_spec)
      const keys = Object.keys(Object.assign({}, draft, final)).sort((a, b) => a.localeCompare(b))
      return keys
        .map((path) => {
          const draftValue = draft[path] === undefined ? '' : draft[path]
          const finalValue = final[path] === undefined ? '' : final[path]
          if (draftValue === finalValue) return null
          const setToNA = !this.isMissingSpecValue(draftValue) && this.isMissingSpecValue(finalValue)
          return {
            path,
            draftLabel: this.truncateText(draftValue || 'empty', 70),
            finalLabel: this.truncateText(finalValue || 'empty', 70),
            draftTitle: draftValue || 'empty',
            finalTitle: finalValue || 'empty',
            setToNA,
            issue: this.verifyIssueForPath(candidate, path)
          }
        })
        .filter(Boolean)
    },
    specFieldPath (group, key) {
      return `${group}.${key}`
    },
    fieldResearchKey (group, key) {
      return this.issueKeyForFieldPath(this.specFieldPath(group, key))
    },
    issueResearchStateForField (group, key) {
      return this.issueResearchResults[this.fieldResearchKey(group, key)] || {
        label: '',
        tone: 'neutral',
        message: '',
        suggestion: ''
      }
    },
    fieldMetaRoots () {
      const candidate = this.selected || {}
      const finalSpec = candidate.final_spec || {}
      const roots = [
        candidate.field_meta,
        candidate.fieldMeta,
        candidate.field_metadata,
        candidate.fieldMetadata,
        finalSpec.field_meta,
        finalSpec.fieldMeta,
        finalSpec.field_metadata,
        finalSpec.fieldMetadata,
        finalSpec.field_sources,
        finalSpec.fieldSources,
        finalSpec.sources,
        finalSpec._meta && finalSpec._meta.fields,
        finalSpec.meta && finalSpec.meta.fields,
        finalSpec.metadata && finalSpec.metadata.fields
      ]
      return roots.filter((root) => root && typeof root === 'object')
    },
    fieldMetaForPath (path) {
      const normalized = this.normalizeIssueFieldPath(path)
      if (!normalized) return null
      const alternates = [
        normalized,
        normalized.replace(/^compats\./i, 'compat.'),
        normalized.replace(/^compats\./i, 'compatibility.'),
        normalized.replace(/^specs\./i, 'spec.'),
        normalized.replace(/^specs\./i, 'specifications.')
      ].filter((value, index, arr) => value && arr.indexOf(value) === index)

      const roots = this.fieldMetaRoots()
      for (const root of roots) {
        for (const fieldPath of alternates) {
          const meta = this.lookupFieldMeta(root, fieldPath)
          if (meta) return this.normalizeFieldMeta(meta)
        }
      }
      return null
    },
    lookupFieldMeta (root, path) {
      if (!root || typeof root !== 'object' || !path) return null
      if (root[path]) return root[path]

      const directKey = Object.keys(root).find((key) => key.toLowerCase() === path.toLowerCase())
      if (directKey) return root[directKey]

      const parts = path.split('.')
      const group = parts.shift()
      const field = parts.join('.')
      if (!group || !field) return null

      const groupKey = Object.keys(root).find((key) => key.toLowerCase() === group.toLowerCase())
      const nested = groupKey ? root[groupKey] : null
      if (!nested || typeof nested !== 'object') return null
      if (nested[field]) return nested[field]

      const nestedKey = Object.keys(nested).find((key) => key.toLowerCase() === field.toLowerCase())
      return nestedKey ? nested[nestedKey] : null
    },
    normalizeFieldMeta (meta) {
      if (!meta) return null
      if (typeof meta === 'string') return { source: meta }
      if (Array.isArray(meta)) return { source: meta.join(', ') }
      if (typeof meta === 'object') return meta
      return { source: String(meta) }
    },
    fieldMetaSource (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const source = meta.source || meta.source_name || meta.sourceName || meta.source_title || meta.sourceTitle || meta.origin || meta.from
      if (source) {
        if (typeof source === 'object') return source.name || source.title || source.url || ''
        return String(source)
      }
      const provider = meta.provider || meta.ai_provider || meta.aiProvider || meta.model_provider || meta.modelProvider
      const model = meta.model || meta.ai_model || meta.aiModel
      if (provider || model) return [this.prettyProvider(provider), model].filter(Boolean).join(' / ')
      const url = meta.url || meta.source_url || meta.sourceUrl || meta.href
      if (url) return this.hostnameFromUrl(url)
      return ''
    },
    fieldMetaConfidence (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const value = meta.confidence !== undefined ? meta.confidence
        : (meta.score !== undefined ? meta.score
          : (meta.confidence_score !== undefined ? meta.confidence_score : meta.confidenceScore))
      if (value === undefined || value === null || value === '') return ''
      const number = Number(value)
      if (!isNaN(number)) {
        const pct = number <= 1 ? Math.round(number * 100) : Math.round(number)
        return `${pct}%`
      }
      return String(value)
    },
    fieldMetaConfidenceKey (meta) {
      const value = this.fieldMetaConfidence(meta).toLowerCase()
      if (/high|strong|trusted|verified|^9\d%$|^100%$/.test(value)) return 'high'
      if (/medium|moderate|mid|^([5-8]\d)%$/.test(value)) return 'mid'
      if (/low|weak|guess|^([0-4]\d)%$/.test(value)) return 'low'
      return 'neutral'
    },
    fieldMetaTier (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const keys = ['tier', 'source_tier', 'sourceTier', 'evidence_tier', 'evidenceTier', 'confidence_tier', 'confidenceTier']
      for (const key of keys) {
        if (meta[key] !== undefined && meta[key] !== null && meta[key] !== '') return meta[key]
      }
      return ''
    },
    fieldMetaTierLabel (tier) {
      const text = String(tier || '').trim()
      if (!text && tier !== 0) return ''
      const number = Number(text)
      if (!isNaN(number)) {
        if (number === 0) return 'T0 rule'
        if (number === 1) return 'T1 DeepSeek'
        if (number === 2) return 'T2 web-source'
        return `T${number}`
      }
      const lowered = text.toLowerCase()
      if (/^(t0|0|rule|tier 0|tier_0|tier-0)/.test(lowered)) return 'T0 rule'
      if (/^(t1|1|deepseek|ai|tier 1|tier_1|tier-1)/.test(lowered)) return 'T1 DeepSeek'
      if (/^(t2|2|web|web-source|source|tier 2|tier_2|tier-2)/.test(lowered)) return 'T2 web-source'
      return text.replace(/^tier[_\s-]*/i, 'T').replace(/_/g, ' ').slice(0, 14)
    },
    fieldMetaTrustClass (meta) {
      return this.fieldMetaConfidenceKey(meta)
    },
    fieldMetaUrl (meta) {
      if (!meta || typeof meta !== 'object') return ''
      return meta.url || meta.source_url || meta.sourceUrl || meta.href || ''
    },
    fieldMetaSummary (meta) {
      if (!meta) return ''
      const parts = [
        this.fieldMetaSource(meta),
        this.fieldMetaConfidence(meta)
      ].filter(Boolean)
      return parts.join(' · ')
    },
    fieldMetaTooltip (meta) {
      if (!meta) return ''
      const rows = []
      const source = this.fieldMetaSource(meta)
      const confidence = this.fieldMetaConfidence(meta)
      const tier = this.fieldMetaTier(meta)
      const url = this.fieldMetaUrl(meta)
      const note = meta.note || meta.reason || meta.evidence || meta.summary
      const checkedAt = meta.resolved_at || meta.resolvedAt || meta.checked_at || meta.checkedAt || meta.created_at || meta.createdAt
      if (source) rows.push(`Source: ${source}`)
      if (confidence) rows.push(`Confidence: ${confidence}`)
      if (tier || tier === 0) rows.push(`Tier: ${this.fieldMetaTierLabel(tier)}`)
      if (url) rows.push(`URL: ${url}`)
      if (checkedAt) rows.push(`Checked: ${this.fmtTime(checkedAt)}`)
      if (note) rows.push(`Note: ${note}`)
      return rows.join('\n')
    },
    prettyProvider (provider) {
      const text = String(provider || '').trim()
      if (!text) return ''
      const map = {
        openai: 'OpenAI',
        deepseek: 'DeepSeek',
        web_search: 'Web search'
      }
      return map[text.toLowerCase()] || text
    },
    hostnameFromUrl (url) {
      const text = String(url || '')
      try {
        return new URL(text).hostname.replace(/^www\./, '')
      } catch (e) {
        return text
      }
    },
    specIssueFieldPaths (issues) {
      return ((issues || []).map((issue) => this.issueFieldPath(issue)).filter(Boolean))
        .filter((path, index, arr) => arr.indexOf(path) === index)
    },
    markFinalSpecEdited () {
      this.finalSpecEdited = true
      this.finalSpecMessage = ''
    },
    finalSpecPayload () {
      const source = this.editableFinalSpec || this.emptyFinalSpec()
      const payload = this.emptyFinalSpec()
      ;['general', 'specs', 'compats'].forEach((group) => {
        Object.keys(source[group] || {}).forEach((key) => {
          payload[group][key] = this.normalizeSpecSaveValue(source[group][key])
        })
      })
      return payload
    },
    normalizeSpecSaveValue (value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },
    async saveGeneratedSpec (options = {}) {
      if (!this.selected || !this.editableFinalSpec) return false
      const previousBusy = this.busy
      this.busy = 'specSave'
      try {
        const payload = this.finalSpecPayload()
        const { data } = await axios.post(`${apiDomain}part-candidates/${this.selected.id}`,
          {
            status: this.selected.status,
            review_notes: this.reviewNotes,
            final_spec: payload
          },
          { headers: this.authHeaders() })
        const next = (data && (data.candidate || data.partCandidate || data.data)) || Object.assign({}, this.selected, { final_spec: payload })
        this.applyReturnedCandidate(next)
        this.finalSpecMessageKind = 'good'
        this.finalSpecMessage = options.silent ? '' : 'Catalog fields saved.'
        this.error = null
        return true
      } catch (e) {
        this.finalSpecMessageKind = 'bad'
        this.finalSpecMessage = this.errMsg(e)
        this.error = this.errMsg(e)
        return false
      } finally {
        this.busy = previousBusy && previousBusy !== 'specSave' ? previousBusy : null
      }
    },
    async validateGeneratedSpec () {
      if (!this.selected || !this.selected.id) return
      if (this.finalSpecDirty) {
        const saved = await this.saveGeneratedSpec({ silent: true })
        if (!saved) return
      }
      this.finalSpecMessageKind = 'neutral'
      this.finalSpecMessage = 'Validation started. Results will refresh here when the run finishes.'
      await this.runSingleStage('validate')
    },
    formatIssue (iss) {
      if (typeof iss === 'string') return iss
      if (iss && typeof iss === 'object') {
        return iss.message || iss.field || JSON.stringify(iss)
      }
      return String(iss)
    },
    // Validation-gate blockers are appended as "Validation blocker: <field> ..." lines.
    isBlocker (iss) {
      return /^\s*validation blocker:/i.test(this.formatIssue(iss))
    },
    issueSeverity (iss) {
      const text = this.formatIssue(iss)
      if (this.isBlocker(iss)) return 'blocker'
      if (/research source|official research|source:/i.test(text)) return 'source'
      if (/error|failed|could not|invalid|missing|required/i.test(text)) return 'warning'
      return 'note'
    },
    issueSeverityLabel (iss) {
      const severity = this.issueSeverity(iss)
      const labels = {
        blocker: 'Blocker',
        warning: 'Warning',
        source: 'Source',
        note: 'Note'
      }
      return labels[severity] || 'Note'
    },
    canResearchIssue (iss) {
      const severity = this.issueSeverity(iss)
      return severity === 'blocker' || severity === 'warning'
    },
    issueKey (issueOrIndex, fallbackIndex) {
      const candidateId = this.selected && this.selected.id ? this.selected.id : 'candidate'
      if (issueOrIndex === 'all') return `${candidateId}:all`

      const issue = issueOrIndex && typeof issueOrIndex === 'object' ? issueOrIndex : null
      if (issue) {
        const issueId = this.issueIdentifier(issue)
        if (issueId) return `${candidateId}:issue:${issueId}`

        const fieldPath = this.normalizeIssueFieldPath(this.issueFieldPath(issue))
        if (fieldPath) return `${candidateId}:field:${fieldPath.toLowerCase()}`

        const text = this.issueTextKey(this.formatIssue(issue))
        if (text) return `${candidateId}:text:${text}`
      }

      const index = fallbackIndex !== undefined ? fallbackIndex : issueOrIndex
      return `${candidateId}:index:${index}`
    },
    issueTextKey (text) {
      return String(text || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 180)
    },
    issueResearchState (issueOrIndex, fallbackIndex) {
      return this.issueResearchResults[this.issueKey(issueOrIndex, fallbackIndex)] || {
        label: '',
        tone: 'neutral',
        message: '',
        suggestion: ''
      }
    },
    normalizeIssueResearchResult (data) {
      const response = data || {}
      const changedFields = this.candidateListFromKeys(response, ['changedFields', 'changed_fields', 'filledFields', 'filled_fields'])
      const missingFieldsRemaining = this.candidateListFromKeys(response, ['missingFieldsRemaining', 'missing_fields_remaining', 'remainingFields', 'remaining_fields'])
      const resolverMeta = response.resolverMeta || response.resolver_meta || {}
      const resolverTiers = resolverMeta.tiers || {}
      const resolverErrors = this.resolverErrorMessages(resolverMeta.errors)
      const filled = response.filled !== undefined ? response.filled : changedFields.length
      const remaining = response.remaining !== undefined ? response.remaining : missingFieldsRemaining.length
      const helped = response.helped === true || response.suggested === true || Number(filled) > 0 || changedFields.length > 0
      const researched = response.researched === true || response.action === 'research'
      const summary = []
      if (filled !== undefined && remaining !== undefined) {
        const total = Number(filled) + Number(remaining)
        if (!isNaN(total)) summary.push(`filled ${filled}/${total}`)
      }
      if (changedFields.length) summary.push(`Changed: ${changedFields.join(', ')}`)
      if (remaining !== undefined && remaining !== null && remaining !== '') summary.push(`Remaining: ${remaining}`)
      if (resolverTiers.t1 !== undefined || resolverTiers.t2 !== undefined || resolverTiers.t0 !== undefined) {
        summary.push(`rule ${Number(resolverTiers.t0 || 0)}`)
        summary.push(`DeepSeek ${Number(resolverTiers.t1 || 0)}`)
        summary.push(`web ${Number(resolverTiers.t2 || 0)}`)
      }
      if (Array.isArray(resolverMeta.escalated) && resolverMeta.escalated.length) {
        summary.push(`escalated ${resolverMeta.escalated.join(', ')}`)
      }
      if (response.sources && response.sources.length) summary.push(`${response.sources.length} source${response.sources.length === 1 ? '' : 's'}`)
      return {
        label: helped ? 'Research updated candidate' : (researched ? 'Research complete' : 'No fields filled'),
        tone: resolverErrors.length ? 'bad' : (helped ? 'good' : 'warn'),
        message: summary.join(' - ') || response.message || response.reason || response.status || '',
        suggestion: '',
        errors: resolverErrors,
        raw: response
      }
    },
    resolverErrorMessages (errors) {
      if (!Array.isArray(errors)) return []
      return errors.map((error) => {
        if (!error) return ''
        if (typeof error === 'string') return error
        const tier = error.tier !== undefined && error.tier !== null ? `T${error.tier}: ` : ''
        return `${tier}${error.message || error.error || error.reason || JSON.stringify(error)}`
      }).filter(Boolean)
    },
    normalizeIssueFieldPath (path) {
      const text = String(path || '').trim()
      if (!text) return ''
      return text.replace(/^compat\./i, 'compats.')
    },
    issueKeyForFieldPath (path) {
      const candidateId = this.selected && this.selected.id ? this.selected.id : 'candidate'
      const fieldPath = this.normalizeIssueFieldPath(path)
      return fieldPath ? `${candidateId}:field:${fieldPath.toLowerCase()}` : ''
    },
    issueResultFieldPaths (data) {
      const response = data || {}
      const fields = this.candidateListFromKeys(response, ['changedFields', 'changed_fields', 'filledFields', 'filled_fields'])
      return fields
        .map((field) => this.normalizeIssueFieldPath(field))
        .filter((field, index, arr) => field && arr.indexOf(field) === index)
    },
    issueResearchTargetKeys (data, fallbackKey, index) {
      if (index === 'all') return [fallbackKey]
      const fieldKeys = this.issueResultFieldPaths(data)
        .map((field) => this.issueKeyForFieldPath(field))
        .filter((key, keyIndex, arr) => key && arr.indexOf(key) === keyIndex)
      return fieldKeys.length ? fieldKeys : [fallbackKey]
    },
    issueResearchSucceeded (issueOrIndex, fallbackIndex) {
      const state = this.issueResearchState(issueOrIndex, fallbackIndex)
      const raw = state && state.raw ? state.raw : {}
      if (state.tone !== 'good') return false
      const remaining = raw.remaining !== undefined ? Number(raw.remaining) : null
      const missing = this.candidateListFromKeys(raw, ['missingFieldsRemaining', 'missing_fields_remaining', 'remainingFields', 'remaining_fields'])
      if (remaining !== null && !isNaN(remaining)) return remaining === 0
      return missing.length === 0
    },
    clearIssueResearchState (issueOrIndex, fallbackIndex) {
      this.$delete(this.issueResearchResults, this.issueKey(issueOrIndex, fallbackIndex))
    },
    issueIdentifier (iss) {
      if (!iss || typeof iss !== 'object') return null
      return iss.id || iss.issueId || iss.issue_id || iss.findingId || iss.finding_id || null
    },
    issueFieldPath (iss) {
      if (!iss) return ''
      if (typeof iss === 'object') {
        const value = iss.field || iss.path || iss.key || iss.field_path || iss.fieldPath
        if (value) return String(value)
      }
      const text = this.formatIssue(iss)
      const match = text.match(/\b(?:general|specs|compats|compat|price)\.[A-Za-z0-9_]+/i)
      return match ? match[0] : ''
    },
    issueResearchPayload (iss, index) {
      const field = index === 'all' ? '' : this.issueFieldPath(iss)
      if (field) return { field }
      return {}
    },
    applyReturnedCandidate (candidate) {
      if (!candidate) return
      this.selected = candidate
      this.reviewNotes = this.selected.review_notes || ''
      this.syncPriceEditor(candidate)
      this.syncFinalSpecForm(candidate)
      const idx = this.candidates.findIndex((row) => row.id === candidate.id)
      if (idx >= 0) this.$set(this.candidates, idx, candidate)
      this.fetchCounts()
      this.scheduleScopeFetch()
    },
    async postCandidateIssueAction (action, index, payload) {
      const id = this.selected && this.selected.id
      if (!id) throw new Error('No candidate selected')
      const headers = { headers: this.authHeaders() }

      if (action === 'research') {
        const issueId = index === 'all' ? null : this.issueIdentifier(payload && payload.issueObject, index)
        const researchBody = this.issueResearchPayload(payload && payload.issueObject, index)
        const genericUrl = `${apiDomain}part-candidates/${id}/issues/research`

        if (issueId) {
          try {
            const { data } = await axios.post(
              `${apiDomain}part-candidates/${id}/issues/${issueId}/research`,
              researchBody,
              headers
            )
            return data
          } catch (e) {
            const status = e && e.response && e.response.status
            if (status !== 404 && status !== 405) throw e
          }
        }

        const { data } = await axios.post(genericUrl, researchBody, headers)
        return data
      }

      throw new Error(`Unsupported candidate issue action: ${action}`)
    },
    async researchCandidateIssue (iss, index) {
      if (!this.selected) return
      const key = this.issueKey(index === 'all' ? 'all' : iss, index)
      this.$set(this.issueBusy, key, true)
      try {
        const data = await this.postCandidateIssueAction('research', index, {
          issueObject: iss,
          issue: this.formatIssue(iss),
          part_type: this.selected.part_type,
          asin: this.selected.asin
        })
        const result = this.normalizeIssueResearchResult(data)
        const targetKeys = this.issueResearchTargetKeys(data, key, index)
        targetKeys.forEach((targetKey) => {
          this.$set(this.issueResearchResults, targetKey, result)
        })
        if (targetKeys.indexOf(key) === -1) {
          this.$delete(this.issueResearchResults, key)
        }
        if (data && data.candidate) {
          this.applyReturnedCandidate(data.candidate)
        }
        this.notice = result.message ? `${result.label}: ${result.message}` : result.label
      } catch (e) {
        this.$set(this.issueResearchResults, key, {
          label: 'Research failed',
          tone: 'bad',
          message: this.errMsg(e),
          suggestion: ''
        })
      } finally {
        this.$set(this.issueBusy, key, false)
      }
    },
    async researchSpecField (group, fieldKey) {
      if (!this.selected || !this.selected.id) return
      const field = this.specFieldPath(group, fieldKey)
      const key = this.fieldResearchKey(group, fieldKey)
      this.$set(this.issueBusy, key, true)
      try {
        const { data } = await axios.post(
          `${apiDomain}part-candidates/${this.selected.id}/issues/research`,
          { field },
          { headers: this.authHeaders() }
        )
        const result = this.normalizeIssueResearchResult(data)
        const targetKeys = this.issueResearchTargetKeys(data, key, 'field')
        const keys = [key].concat(targetKeys).filter((targetKey, index, arr) => targetKey && arr.indexOf(targetKey) === index)
        keys.forEach((targetKey) => {
          this.$set(this.issueResearchResults, targetKey, result)
        })
        if (data && data.candidate) {
          this.applyReturnedCandidate(data.candidate)
        }
        this.notice = result.message ? `${result.label}: ${result.message}` : result.label
      } catch (e) {
        this.$set(this.issueResearchResults, key, {
          label: 'Research failed',
          tone: 'bad',
          message: this.errMsg(e),
          suggestion: ''
        })
      } finally {
        this.$set(this.issueBusy, key, false)
      }
    },
    enrichmentErrorTitle (error) {
      const text = String(error || '')
      if (/blocker/i.test(text)) return 'Validation blockers need review'
      if (/price|unavailable|amazon/i.test(text)) return 'Amazon data needs attention'
      return 'The enrichment pass could not finish cleanly'
    },
    candidateBullets (c) {
      const bullets = c && c.raw_product && c.raw_product.bullets
      return normalizeBulletList(bullets)
    },
    candidateMissingFieldPaths (candidate) {
      if (!candidate) return []
      const paths = {}
      const add = (field) => {
        const value = field && typeof field === 'object'
          ? field.path || field.field || field.key || field.field_path || field.fieldPath || field.name
          : field
        const path = this.normalizeIssueFieldPath(value)
        if (path) paths[path.toLowerCase()] = path
      }
      const addList = (source) => {
        if (!source || typeof source !== 'object') return
        ;['missingFields', 'missing_fields', 'missingFieldsRemaining', 'missing_fields_remaining', 'remainingFields', 'remaining_fields'].forEach((key) => {
          const values = source[key]
          if (Array.isArray(values)) values.forEach(add)
        })
      }

      const evaluation = this.evaluationFor(candidate)
      const finalValues = this.flattenSpecValues(candidate.final_spec)
      const normalizedFinalValues = {}
      Object.keys(finalValues).forEach((path) => {
        normalizedFinalValues[path.toLowerCase()] = finalValues[path]
      })
      addList(candidate)
      addList(evaluation)
      addList(candidate.final_spec)

      this.evaluationFields(candidate).forEach((row) => {
        const normalizedPath = row.path.toLowerCase()
        const current = normalizedFinalValues[normalizedPath] !== undefined
          ? normalizedFinalValues[normalizedPath]
          : row.value
        if (this.isMissingSpecValue(current)) add(row.path)
      })

      Object.keys(finalValues).forEach((path) => {
        if (this.isMissingSpecValue(finalValues[path])) add(path)
      })

      ;((candidate && candidate.issues) || []).forEach((issue) => {
        const text = this.formatIssue(issue)
        const path = this.issueFieldPath(issue)
        if (path && /missing|required|empty|n\/a|not populated/i.test(text)) add(path)
      })

      return Object.keys(paths).sort().map((key) => paths[key])
    },
    candidateMissingFieldCount (candidate) {
      const paths = this.candidateMissingFieldPaths(candidate)
      const sources = [candidate, this.evaluationFor(candidate), candidate && candidate.final_spec]
      const countKeys = ['missingFieldCount', 'missing_field_count', 'missingFieldsCount', 'missing_fields_count', 'remainingFieldCount', 'remaining_field_count']
      let reported = 0
      sources.forEach((source) => {
        countKeys.forEach((key) => {
          const value = source && source[key]
          if (value === undefined || value === null || value === '') return
          const count = Number(value)
          if (!isNaN(count)) reported = Math.max(reported, count)
        })
      })
      return Math.max(paths.length, reported)
    },
    candidateMissingFieldTitle (candidate) {
      const paths = this.candidateMissingFieldPaths(candidate)
      if (!paths.length) {
        const count = this.candidateMissingFieldCount(candidate)
        return `${count} missing field${count === 1 ? '' : 's'}`
      }
      return `Missing: ${paths.join(', ')}`
    },
    blockerCount (c) {
      return this.evaluationCounts(c).blockers || 0
    },
    issueCounts (c) {
      const evaluated = this.evaluationCounts(c)
      const counts = Object.assign({
        total: 0,
        blockers: 0,
        warnings: 0,
        sources: 0,
        notes: 0,
        estimated: 0,
        ok: 0
      }, evaluated)
      counts.total = counts.problems !== undefined ? counts.problems : counts.total
      return counts
    },
    verificationIssueSummary (c) {
      const counts = this.issueCounts(c)
      if (!counts.total) return 'No issues'
      const parts = []
      if (counts.blockers) parts.push(`${counts.blockers} blocker${counts.blockers === 1 ? '' : 's'}`)
      if (counts.warnings) parts.push(`${counts.warnings} warning${counts.warnings === 1 ? '' : 's'}`)
      if (counts.estimated) parts.push(`${counts.estimated} estimated`)
      if (!parts.length) parts.push(`${counts.total} finding${counts.total === 1 ? '' : 's'}`)
      return parts.join(' / ')
    },
    verificationIssueHint (c) {
      const issues = this.findingRows(c)
      const firstActionable = issues.find((issue) => {
        return issue.status === 'blocker' || issue.status === 'warning'
      }) || issues[0]
      if (!firstActionable) return 'Catalog validation has no current findings.'
      return this.truncateText(firstActionable.message || this.formatIssue(firstActionable.raw || firstActionable), 132)
    },
    truncateText (value, max) {
      const text = String(value || '').replace(/\s+/g, ' ').trim()
      if (text.length <= max) return text
      return text.slice(0, Math.max(0, max - 1)).trim() + '...'
    },
    // imgUrl is usually a JSON-array string of full-res Amazon URLs; parse it
    // defensively so a malformed value can never throw and break the render.
    parseImgUrlArray (val) {
      if (Array.isArray(val)) return val.filter((u) => typeof u === 'string' && u)
      if (typeof val !== 'string' || !val.trim()) return []
      try {
        const arr = JSON.parse(val)
        return Array.isArray(arr) ? arr.filter((u) => typeof u === 'string' && u) : []
      } catch (e) {
        return []
      }
    },
    candidateImages (c) {
      if (!c) return []
      const imgs = []
      if (c.raw_product && Array.isArray(c.raw_product.images)) {
        imgs.push(...c.raw_product.images.filter((u) => typeof u === 'string' && u))
      }
      if (c.final_spec && c.final_spec.general) {
        imgs.push(...this.parseImgUrlArray(c.final_spec.general.imgUrl))
      }
      if (c.raw_product && typeof c.raw_product.mainImg === 'string' && c.raw_product.mainImg) {
        imgs.push(c.raw_product.mainImg)
      }
      return Array.from(new Set(imgs))
    },
    rememberImageResolution (url, event) {
      const img = event && event.target
      if (!url || !img || !img.naturalWidth || !img.naturalHeight) return
      this.$set(this.imageDimensions, url, {
        width: img.naturalWidth,
        height: img.naturalHeight
      })
    },
    rememberImageResolutionError (url) {
      if (!url) return
      this.$set(this.imageDimensions, url, { error: true })
    },
    imageResolution (url) {
      const size = this.imageDimensions[url]
      if (size && size.error) return 'size unknown'
      if (!size || !size.width || !size.height) return 'measuring...'
      return `${size.width} x ${size.height}`
    },
    // When the candidate first entered the queue.
    addedAt (c) {
      return (c && (c.discovered_at || c.created_at)) || null
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleString()
    },
    fmtDateShort (t) {
      if (!t) return ''
      const d = new Date(t)
      return isNaN(d) ? '' : d.toLocaleDateString()
    },
    fmtRelative (t) {
      if (!t) return '—'
      const d = new Date(t)
      if (isNaN(d)) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 0) return this.fmtTime(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtTime(t)
    },
    errMsg (e) {
      return (e.response && e.response.data && e.response.data.message) || e.message || 'Request failed'
    }
  }
}
</script>

<style scoped>
.cand-page { min-width: 0; padding: 4px 8px 40px; color: #cfd2da; }
.label { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 1px solid rgba(67, 72, 87, 0.9); }
.eyebrow { display: block; margin-bottom: 3px; color: #1997c6; font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
.label h4 { margin: 0 0 6px; color: #d8dde8; font-size: 18px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.label-actions { display: flex; align-items: center; gap: 10px; }
.page-views {
  display: inline-flex;
  overflow: hidden;
  gap: 3px;
  padding: 4px;
  background: rgba(8, 11, 17, 0.45);
  border: 1px solid #434857;
  border-radius: 6px;
}
.page-views button {
  padding: 6px 16px;
  border-radius: 5px;
  border: 0;
  background: transparent;
  color: #9198aa;
  font: inherit;
  cursor: pointer;
  transition: 0.15s;
}
.page-views button:hover { color: #cfd2da; }
.page-views button.active { background: rgba(25,151,198,0.14); color: #eef8fc; }
.page-refresh { display: inline-flex; align-items: center; gap: 8px; min-height: 36px; font-size: 12px; }
.page-refresh svg { width: 15px; height: 15px; }
.page-views button:focus { outline: none; }
.page-views button:focus-visible { outline: 1px solid rgba(25,151,198,0.8); outline-offset: -2px; }
.enrich-toggle { display: flex; align-items: center; gap: 5px; font-size: 9.5pt; color: #9198aa; margin: 0; cursor: pointer; }
.intro { max-width: 860px; margin: 0.6rem 0 1.1rem; color: #a4acbc; font-size: 14px; line-height: 1.55; }
.alert { background: #5a2230; border: 1px solid #a8364a; color: #f3c6cf; padding: 8px 12px; border-radius: 6px; margin: 10px 0; }
.notice { background: #1f4d3a; border: 1px solid #2e7d5a; color: #b6e3c8; padding: 8px 12px; border-radius: 6px; margin: 10px 0; }

.btn-ghost { background: transparent; border: 1px solid #434857; color: #9198aa; border-radius: 6px; padding: 4px 12px; cursor: pointer; transition: 0.15s; }
.btn-ghost:hover { border-color: #1997c6; color: #cfd2da; }
.btn-ghost:disabled { opacity: 0.5; cursor: default; }
.btn-ghost-primary { border-color: rgba(25,151,198,0.58); color: #9bdff5; background: rgba(25,151,198,0.08); }
.btn-ghost-primary:hover { border-color: #1997c6; color: #eef8fc; background: rgba(25,151,198,0.14); }

/* Pipeline stages */
.stage-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 10px;
  margin: 16px 0 12px;
}
.stage-tab {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 72px;
  padding: 12px;
  border: 1px solid #434857;
  border-radius: 8px;
  background: rgba(12, 16, 24, 0.72);
  color: #aeb6c8;
  text-align: left;
  cursor: pointer;
  transition: 0.16s;
}
.stage-tab:hover {
  border-color: rgba(25,151,198,0.6);
  background: rgba(18, 25, 36, 0.88);
}
.stage-tab.active {
  border-color: #1997c6;
  background: linear-gradient(135deg, rgba(25,151,198,0.18), rgba(12,16,24,0.82));
  box-shadow: inset 0 0 0 1px rgba(25,151,198,0.12);
}
.stage-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(25,151,198,0.55);
  border-radius: 8px;
  color: #9bdff5;
  font-weight: 800;
}
.stage-tab-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}
.stage-tab-copy strong {
  color: #dfe4ee;
  font-size: 10pt;
  line-height: 1.1;
}
.stage-tab-copy small {
  overflow: hidden;
  color: #7f889a;
  font-size: 8.5pt;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stage-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(8,11,17,0.48);
  color: #e4d836;
  font-size: 9pt;
  font-weight: 800;
}
.stage-workbench {
  display: grid;
  gap: 12px;
  margin: 0 0 14px;
  padding: 14px;
  border: 1px solid rgba(67,72,87,0.9);
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(13, 18, 28, 0.82), rgba(7, 10, 16, 0.62));
}
.stage-workbench-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.stage-workbench-main h5 {
  margin: 3px 0 5px;
  color: #e7e9ee;
  font-size: 14pt;
}
.stage-workbench-main p {
  max-width: 780px;
  margin: 0;
  color: #8f98aa;
  font-size: 9.5pt;
  line-height: 1.45;
}
.stage-kicker {
  color: #1997c6;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.stage-actions-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  min-width: 250px;
}
.stage-options {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}
.stage-search-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(67,72,87,0.62);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.36);
}
.stage-field {
  display: grid;
  gap: 5px;
  min-width: 120px;
  margin: 0;
}
.stage-field span {
  color: #7f889a;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.stage-field input,
.stage-field select {
  height: 34px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #cfd2da;
  padding: 0 10px;
  outline: none;
}
.stage-field input:focus,
.stage-field select:focus {
  border-color: #1997c6;
}
.stage-field-wide {
  flex: 1 1 280px;
}
.stage-field-quantity {
  min-width: 110px;
  max-width: 130px;
}
.stage-run-btn {
  min-height: 34px;
}
.price-refresh-bulk {
  min-height: 34px;
  border-color: rgba(224,192,126,0.48);
  color: #ead08a;
  background: rgba(77,58,31,0.18);
}
.price-refresh-bulk:hover:not(:disabled) {
  border-color: #e0c07e;
  color: #fff2c4;
  background: rgba(77,58,31,0.3);
}
.stage-note {
  color: #e0c07e;
  font-size: 9pt;
}
.stage-note-list {
  display: grid;
  gap: 4px;
}
.stage-scope-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(67,72,87,0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.42);
}
.stage-scope-card.loading {
  opacity: 0.82;
}
.stage-scope-main {
  display: grid;
  gap: 3px;
  min-width: 0;
}
.stage-scope-kicker {
  color: #1997c6;
  font-size: 7.5pt;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.stage-scope-main strong {
  color: #dfe4ee;
  font-size: 9.5pt;
}
.stage-scope-main small,
.stage-scope-stat small {
  color: #8f98aa;
  font-size: 8.3pt;
  line-height: 1.35;
}
.stage-scope-stat {
  display: grid;
  justify-items: end;
  min-width: 86px;
}
.stage-scope-stat span {
  color: #e4d836;
  font-size: 18pt;
  font-weight: 700;
  line-height: 1;
}
.stage-scope-counts {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.stage-scope-counts span {
  padding: 2px 7px;
  border: 1px solid rgba(67,72,87,0.78);
  border-radius: 999px;
  color: #aab2c2;
  font-size: 8pt;
  font-weight: 700;
}
.stage-scope-error {
  grid-column: 1 / -1;
  color: #e0c07e;
  font-size: 8.5pt;
}
/* Onboarding plan preview (the directives a dispatch would queue) */
.onboard-plan {
  display: grid;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(67,72,87,0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.42);
}
.onboard-plan-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.onboard-plan-kicker {
  color: #1997c6;
  font-size: 7.5pt;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.onboard-plan-head small {
  color: #8f98aa;
  font-size: 8.3pt;
}
.onboard-plan-list {
  display: grid;
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.onboard-plan-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 7px 9px;
  border: 1px solid rgba(67,72,87,0.6);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.5);
}
.onboard-plan-tier {
  padding: 2px 8px;
  border: 1px solid rgba(117,124,146,0.36);
  border-radius: 999px;
  font-size: 7.5pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.onboard-plan-tier.tier-platform-keystone { border-color: rgba(27,201,142,0.42); background: rgba(27,201,142,0.09); color: #8fe8c4; }
.onboard-plan-tier.tier-subpath-keystone { border-color: rgba(25,151,198,0.45); background: rgba(25,151,198,0.1); color: #9bdff5; }
.onboard-plan-tier.tier-variety { border-color: rgba(117,124,146,0.36); background: rgba(117,124,146,0.1); color: #b7c2d6; }
.onboard-plan-gap {
  display: grid;
  gap: 1px;
  min-width: 0;
}
.onboard-plan-gap strong {
  color: #dfe4ee;
  font-size: 9pt;
}
.onboard-plan-gap small {
  color: #8f98aa;
  font-size: 8pt;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.onboard-plan-count {
  color: #e4d836;
  font-size: 11pt;
  font-weight: 700;
}
.onboard-plan-empty {
  display: grid;
  gap: 3px;
  padding: 9px 11px;
  border: 1px dashed rgba(27,201,142,0.34);
  border-radius: 6px;
  background: rgba(27,201,142,0.05);
}
.onboard-plan-empty strong { color: #8fe8c4; font-size: 9pt; }
.onboard-plan-empty small { color: #8f98aa; font-size: 8.3pt; line-height: 1.4; }
.onboard-plan-empty em { color: #b7c2d6; font-style: normal; font-weight: 700; }
/* Onboarding run progress chips */
.run-progress {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.run-progress-chip {
  padding: 2px 8px;
  border: 1px solid rgba(25,151,198,0.32);
  border-radius: 999px;
  color: #dfe4ee;
  font-size: 8.5pt;
  font-weight: 700;
}
.run-progress-chip small {
  margin-right: 5px;
  color: #8f98aa !important;
  font-size: 7.5pt;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
/* ---- Discover -> review -> Onboard ---- */
.discover-btn {
  align-self: end;
  justify-self: end;
  min-width: 112px;
  min-height: 34px;
  margin-left: auto;
  white-space: nowrap;
}
/* Stage 1 intake: one panel — type + tabs on top, the active way in below */
.intake {
  border: 1px solid rgba(67,72,87,0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.36);
}
.intake-head {
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(67,72,87,0.6);
}
.intake-type {
  align-self: center;
  height: 30px;
  margin: 8px 0;
  padding: 0 8px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #cfd2da;
  font-size: 9pt;
  outline: none;
}
.intake-type:focus { border-color: #1997c6; }
.intake-type.needs-type { border-color: rgba(224,168,86,0.75); color: #f0c888; }
.intake-tabs { display: flex; gap: 20px; overflow-x: auto; }
.intake-tab {
  margin-bottom: -1px;
  padding: 0 1px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: #8f98aa;
  font: inherit;
  font-size: 9.3pt;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.intake-tab:hover { color: #cfd2da; }
.intake-tab.active { border-bottom-color: #16b4ed; color: #eef8fc; }
/* No box after a mouse click; keyboard focus still gets a visible ring. */
.intake-tab:focus,
.intake-link:focus,
.requests-filter button:focus { outline: none; }
.intake-tab:focus-visible,
.intake-link:focus-visible,
.requests-filter button:focus-visible { outline: 1px solid rgba(25,151,198,0.8); outline-offset: 2px; border-radius: 3px; }
.intake-pane { display: grid; gap: 10px; padding: 12px; }
.intake-row { display: flex; align-items: flex-start; gap: 8px; }
.intake-row-fields { flex-wrap: wrap; align-items: flex-end; }
.intake-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 34px;
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #dfe4ee;
  font-family: inherit;
  font-size: 9pt;
  line-height: 1.4;
  resize: none;
  outline: none;
}
textarea.intake-input { height: auto; }
.intake-input:focus { border-color: #1997c6; }
.intake-input::placeholder { color: #6f7788; }
.intake-count {
  height: 34px;
  padding: 0 8px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #cfd2da;
  font-size: 9pt;
  outline: none;
}
.intake-btn { min-height: 34px; white-space: nowrap; }
.intake-row-fields .discover-btn { margin-left: auto; }
.intake-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #8f98aa;
  font-size: 8.3pt;
  line-height: 1.45;
}
.intake-warn { color: #e0a856 !important; }
.intake-error { margin: 0; color: #f49aaa; font-size: 8.5pt; }
.intake-chip {
  max-width: 100%;
  overflow: hidden;
  padding: 1px 8px;
  border: 1px solid rgba(117,124,146,0.4);
  border-radius: 999px;
  color: #b7c2d6;
  font-size: 7.8pt;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.intake-chip.kind-asin { border-color: rgba(27,201,142,0.45); color: #8fe8c4; }
.intake-chip.kind-upc { border-color: rgba(25,151,198,0.5); color: #9bdff5; }
.intake-chip.kind-bad { border-color: rgba(244,154,170,0.45); color: #f49aaa; }
.intake-skipped {
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: #e0a856;
  font-size: 8.1pt;
}
.intake-skipped em { color: #dfe4ee; font-style: normal; }
.intake-summary { color: #dfe4ee; font-weight: 700; }
.intake-feed {
  padding: 10px 12px;
  border: 1px solid rgba(67,72,87,0.55);
  border-radius: 6px;
  background: rgba(4, 6, 10, 0.35);
}
.intake-bulk { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.intake-bulk .btn-ghost { white-space: nowrap; }
.intake-link {
  padding: 0;
  border: 0;
  background: none;
  color: #7fd0ee;
  font: inherit;
  cursor: pointer;
}
.intake-link:hover { color: #eef8fc; }
@media (max-width: 900px) {
  .intake-head { flex-wrap: wrap; gap: 4px 18px; }
  .intake-tabs { flex: 1 1 100%; min-height: 36px; }
  .intake-row:not(.intake-row-fields) { flex-wrap: wrap; }
  .intake-row:not(.intake-row-fields) .intake-input { flex-basis: 100%; }
}

/* Request queue: a plain list under the intake panel */
.queue { display: grid; gap: 8px; }
.queue-head { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.queue-head strong { color: #dfe4ee; font-size: 10pt; }
.queue-head small { flex: 1 1 auto; color: #8f98aa; font-size: 8.3pt; }
.requests-filter {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid rgba(67,72,87,0.7);
  border-radius: 6px;
}
.requests-filter button {
  padding: 5px 10px;
  border: 0;
  background: rgba(8,10,15,0.48);
  color: #8f98aa;
  font: inherit;
  font-size: 8pt;
  font-weight: 700;
  cursor: pointer;
}
.requests-filter button + button { border-left: 1px solid rgba(67,72,87,0.7); }
.requests-filter button.active { background: rgba(25,151,198,0.14); color: #dfe4ee; }
.requests-list { display: grid; gap: 6px; margin: 0; padding: 0; list-style: none; }

.discover-live,
.discover-ledger-summary,
.discover-error-state { display: grid; gap: 8px; }
.discover-status {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 13px;
  border: 1px solid rgba(25,151,198,0.32);
  border-radius: 8px;
  background: rgba(25,151,198,0.07);
}
.discover-status strong { display: block; color: #dfe4ee; font-size: 9.5pt; }
.discover-status small { color: #8f98aa; font-size: 8.3pt; }
.discover-live-copy { color: #8f98aa; font-size: 8pt; }
.discover-metrics { display: flex; flex-wrap: wrap; gap: 6px; }
.discover-metrics span {
  padding: 4px 7px;
  border: 1px solid rgba(67,72,87,0.62);
  border-radius: 999px;
  background: rgba(8,11,17,0.42);
  color: #8f98aa;
  font-size: 7.7pt;
}
.discover-metrics strong { color: #dfe4ee; }
.discover-warning {
  padding: 8px 10px;
  border: 1px solid rgba(245,183,77,0.42);
  border-radius: 7px;
  background: rgba(245,183,77,0.08);
  color: #f5c66d;
  font-size: 8.3pt;
}
.discover-ledger-list {
  display: grid;
  gap: 4px;
  max-height: 280px;
  margin: 0;
  padding: 0;
  overflow: auto;
  list-style: none;
}
.discover-ledger-row {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 6px 8px;
  border: 1px solid rgba(67,72,87,0.45);
  border-radius: 6px;
  background: rgba(8,11,17,0.32);
}
.discover-ledger-status {
  color: #8f98aa;
  font-size: 7.3pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.discover-ledger-status.is-available { color: #77d9a8; }
.discover-ledger-status.is-unavailable,
.discover-ledger-status.is-scrape_failed,
.discover-ledger-status.is-invalid,
.discover-ledger-status.is-invalid_response,
.discover-ledger-status.is-response_incomplete,
.discover-ledger-status.is-failed { color: #f49aaa; }
.discover-ledger-status.is-checking,
.discover-ledger-status.is-proposed { color: #6fc3ff; }
.discover-ledger-product { display: grid; gap: 2px; min-width: 0; }
.discover-ledger-product strong {
  overflow: hidden;
  color: #dfe4ee;
  font-size: 8.2pt;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.discover-ledger-product small { color: #8f98aa; font-size: 7.5pt; }
.discover-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(25,151,198,0.3);
  border-top-color: #16b4ed;
  border-radius: 50%;
  animation: discover-spin 0.8s linear infinite;
  flex: 0 0 auto;
}
@keyframes discover-spin { to { transform: rotate(360deg); } }
.discover-error {
  padding: 9px 12px;
  border: 1px solid rgba(244,154,170,0.4);
  border-radius: 8px;
  background: rgba(244,154,170,0.08);
  color: #f49aaa;
  font-size: 8.7pt;
}
.discover-idle {
  padding: 12px 13px;
  border: 1px dashed rgba(67,72,87,0.7);
  border-radius: 8px;
  color: #8f98aa;
  font-size: 8.7pt;
}
.discover-idle strong { color: #dfe4ee; }
.discover-scope {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(67,72,87,0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.42);
}
.discover-scope-main { display: grid; gap: 2px; min-width: 0; }
.discover-scope-kicker {
  color: #1997c6;
  font-size: 7.5pt;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.discover-scope-main strong { color: #dfe4ee; font-size: 10pt; }
.discover-scope-main small { color: #8f98aa; font-size: 8.3pt; }
.discover-plan-toggle,
.cleanup-toggle {
  border: 1px solid rgba(67,72,87,0.7);
  border-radius: 6px;
  background: rgba(8,10,15,0.48);
  color: #b8c2d4;
  cursor: pointer;
  font: inherit;
  font-size: 8pt;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}
.discover-plan-toggle:hover, .cleanup-toggle:hover { border-color: rgba(25,151,198,0.6); color: #dfe4ee; }
/* Cleanup (retire) group */
.cleanup-group { display: grid; gap: 6px; }
.cleanup-toggle { justify-self: start; display: inline-flex; align-items: center; gap: 7px; }
.cleanup-caret { transition: transform 0.16s ease; display: inline-block; }
.cleanup-caret.open { transform: rotate(90deg); }
.cleanup-list { display: grid; gap: 4px; margin: 0; padding: 0; list-style: none; }
.cleanup-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 6px 9px;
  border: 1px solid rgba(117,124,146,0.28);
  border-radius: 6px;
  background: rgba(117,124,146,0.06);
}
.cleanup-type {
  padding: 1px 7px;
  border: 1px solid rgba(117,124,146,0.36);
  border-radius: 999px;
  color: #b7c2d6;
  font-size: 7.5pt;
  font-weight: 800;
  text-transform: uppercase;
  white-space: nowrap;
}
.cleanup-reason { color: #9aa3b4; font-size: 8pt; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cleanup-count { color: #8f98aa; font-size: 9pt; font-weight: 700; }
/* Review deck */
.review-deck {
  display: grid;
  gap: 9px;
  padding: 11px 12px;
  border: 1px solid rgba(25,151,198,0.28);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.5);
}
.review-deck-head {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #8f98aa;
  font-size: 8pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.review-progress {
  flex: 1 1 auto;
  height: 4px;
  border-radius: 999px;
  background: rgba(54,62,78,0.6);
  overflow: hidden;
}
.review-progress span { display: block; height: 100%; background: #16b4ed; border-radius: inherit; transition: width 0.2s ease; }
.review-card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 13px;
  align-items: start;
  padding: 11px;
  border: 1px solid rgba(67,72,87,0.66);
  border-radius: 8px;
  background: rgba(8,10,15,0.5);
}
.review-card.is-accept { border-color: rgba(27,201,142,0.5); background: rgba(27,201,142,0.06); }
.review-card.is-reject { border-color: rgba(244,154,170,0.4); opacity: 0.7; }
.review-card.is-duplicate { opacity: 0.6; }
.review-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  background: linear-gradient(180deg, #f4f6f8, #dde4ec) center / cover no-repeat;
  box-shadow: inset 0 0 20px rgba(10,14,22,0.14);
}
.review-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(67,72,87,0.3);
  color: #8f98aa;
}
.review-body { display: grid; gap: 6px; min-width: 0; }
.review-name-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.review-name-row strong { color: #f4f8fb; font-size: 10.5pt; }
.review-dup-badge {
  padding: 1px 7px;
  border: 1px solid rgba(224,192,126,0.4);
  border-radius: 999px;
  color: #e0c07e;
  font-size: 7.5pt;
  font-weight: 800;
  text-transform: uppercase;
}
.review-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; color: #9aa3b4; font-size: 8.5pt; }
.review-gap { color: #9bdff5; }
.review-price { color: #dfe4ee; font-weight: 700; }
.review-price.muted { color: #8f98aa; font-weight: 500; }
.review-source { color: #16b4ed; text-decoration: none; }
.review-source:hover { text-decoration: underline; }
.review-asin { color: #7f889a; font-size: 8pt; }
.review-decision { font-size: 8pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; }
.review-decision.accept { color: #8fe8c4; }
.review-decision.reject { color: #f49aaa; }
.review-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.review-actions .btn-ghost { padding: 6px 11px; font-size: 8.5pt; }
.review-reject, .review-accept {
  border: 1px solid;
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
  font-size: 8.5pt;
  font-weight: 800;
  padding: 6px 13px;
}
.review-reject { border-color: rgba(244,154,170,0.42); background: rgba(244,154,170,0.08); color: #f49aaa; }
.review-reject:hover { background: rgba(244,154,170,0.16); }
.review-accept { border-color: rgba(27,201,142,0.5); background: rgba(27,201,142,0.12); color: #8fe8c4; margin-left: auto; }
.review-accept:hover:not(:disabled) { background: rgba(27,201,142,0.2); }
.review-accept:disabled { opacity: 0.4; cursor: not-allowed; }
.review-commit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid rgba(27,201,142,0.32);
  border-radius: 8px;
  background: rgba(27,201,142,0.06);
}
.review-commit span { color: #dfe4ee; font-size: 9pt; font-weight: 700; }
.run-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid rgba(25,151,198,0.32);
  border-radius: 7px;
  background: rgba(25,151,198,0.08);
}
.run-card div {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.run-card strong {
  color: #dfe4ee;
  font-size: 9.5pt;
}
.run-card small {
  color: #8f98aa;
  font-size: 8.5pt;
}
.run-scope {
  color: #b6deef !important;
}
.run-ai-outcome {
  display: block;
  margin-top: 3px;
  font-weight: 800;
}
.run-ai-outcome.tone-good { color: #8ee5c4; }
.run-ai-outcome.tone-warn { color: #e0c07e; }
.run-ai-outcome.tone-bad { color: #f49aaa; }
.run-ai-preview {
  display: block;
  max-width: 760px;
  margin-top: 2px;
  color: #a3adbf !important;
  line-height: 1.4;
}
.run-fallback-btn {
  margin-left: auto;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(224, 192, 126, 0.5);
  border-radius: 6px;
  background: rgba(224, 192, 126, 0.09);
  color: #e0c07e;
  font-size: 8pt;
  font-weight: 900;
  cursor: pointer;
}
.run-fallback-btn:hover:not(:disabled) {
  background: rgba(224, 192, 126, 0.16);
  color: #f2dfaa;
}
.run-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #1997c6;
  box-shadow: 0 0 0 4px rgba(25,151,198,0.12);
}
.run-success .run-dot,
.run-succeeded .run-dot,
.run-completed .run-dot,
.run-complete .run-dot {
  background: #1bc98e;
  box-shadow: 0 0 0 4px rgba(27,201,142,0.12);
}
.run-failed .run-dot,
.run-error .run-dot {
  background: #d95f7a;
  box-shadow: 0 0 0 4px rgba(217,95,122,0.12);
}
.run-summary {
  margin-left: auto;
  color: #9bdff5;
  font-size: 8.5pt;
}

/* Status tabs */
.status-tabs { display: flex; flex-wrap: wrap; gap: 7px; margin: 14px 0; }
.status-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  background: rgba(38, 43, 55, 0.9);
  border: 1px solid #434857;
  color: #9ba3b7;
  border-radius: 999px;
  padding: 4px 7px 4px 13px;
  cursor: pointer;
  font-size: 9.5pt;
  transition: 0.15s;
}
.status-tab:hover { color: #cfd2da; border-color: #5a6075; background: rgba(48, 54, 69, 0.94); }
.status-tab.active { background: rgba(25,151,198,0.16); border-color: #1997c6; color: #cfeef8; }
.status-tab-label { line-height: 1; }
.status-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
  border: 1px solid rgba(117, 124, 146, 0.35);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.42);
  color: #d1d6e2;
  font-size: 8.5pt;
  font-weight: 800;
  line-height: 1;
}
.status-tab.active .status-tab-count {
  border-color: rgba(25, 151, 198, 0.58);
  background: rgba(25, 151, 198, 0.18);
  color: #eaf7fc;
}

/* List */
.cand-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 14px; }
.cand-card { display: flex; gap: 12px; border: 1px solid #434857; border-radius: 8px; padding: 12px; background: #2b2f3a; cursor: pointer; transition: 0.16s; min-width: 0; }
.cand-card:hover { border-color: #1997c6; box-shadow: 0 8px 24px rgba(0,0,0,0.3); transform: translateY(-2px); }
.cand-thumb { flex: 0 0 64px; width: 64px; height: 64px; border-radius: 6px; background: #fff center / contain no-repeat; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4); }
.cand-thumb--empty { display: flex; align-items: center; justify-content: center; background: rgba(8,11,17,0.55); box-shadow: inset 0 0 0 1px #434857; color: #4c5468; font-size: 24px; }
.cand-main { min-width: 0; flex: 1; }
.cand-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.cand-badges { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-width: 0; }
.cand-title { color: #e7e9ee; font-size: 12.5pt; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cand-reason { color: #9198aa; font-size: 9.5pt; line-height: 1.3; margin-top: 3px; max-height: 34px; overflow: hidden; }
.cand-meta { display: flex; gap: 10px; margin-top: 6px; font-size: 8.5pt; color: #6b7180; }
.cand-issues { color: #e0c07e; }
.cand-source { color: #7edfaf; }
.cand-price {
  flex: 0 0 auto;
  max-width: 130px;
  overflow: hidden;
  padding: 2px 8px;
  border: 1px solid rgba(27,201,142,0.42);
  border-radius: 999px;
  background: rgba(27,201,142,0.1);
  color: #8fe8c4;
  font-size: 8.5pt;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cand-price.price-missing,
.scrape-price.price-missing {
  border-color: rgba(224,192,126,0.38);
  background: rgba(77,58,31,0.22);
  color: #e0c07e;
}
.candidate-table-shell {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(67,72,87,0.82);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.34);
}
.candidate-table-head,
.candidate-table-row {
  display: grid;
  grid-template-columns: minmax(300px, 2.4fr) 118px 150px 150px 112px 82px 132px;
  gap: 12px;
  align-items: center;
}
.candidate-table-head {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67,72,87,0.72);
  color: #8f98aa;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.candidate-table-row {
  padding: 12px;
  border-bottom: 1px solid rgba(67,72,87,0.52);
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;
}
.candidate-table-row:last-of-type {
  border-bottom: 0;
}
.candidate-table-row:hover,
.candidate-table-row:focus {
  outline: none;
  background: rgba(25,151,198,0.07);
}
.candidate-table-row.missing-price {
  box-shadow: inset 2px 0 0 rgba(224,192,126,0.74);
}
.candidate-table-row.missing-source:not(.missing-price):not(.has-blockers) {
  box-shadow: inset 2px 0 0 rgba(25,151,198,0.58);
}
.candidate-table-row.has-blockers {
  box-shadow: inset 2px 0 0 rgba(201,94,113,0.76);
}
.candidate-product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.candidate-product-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}
.candidate-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.candidate-name-row strong {
  min-width: 0;
  overflow: hidden;
  color: #dfe4ee;
  font-size: 11pt;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.candidate-product-copy p {
  overflow: hidden;
  margin: 0;
  color: #8f98aa;
  font-size: 9pt;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.candidate-product-copy small,
.candidate-table-cell small {
  color: #6f788a;
  font-size: 8pt;
}
.candidate-table-cell {
  display: grid;
  gap: 4px;
  min-width: 0;
  color: #cfd2da;
  font-size: 9pt;
}
.candidate-source-cell a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: max-content;
  max-width: 100%;
  padding: 3px 8px;
  border: 1px solid rgba(27,201,142,0.34);
  border-radius: 999px;
  background: rgba(27,201,142,0.08);
  color: #7edfaf;
  font-weight: 700;
  text-decoration: none;
}
.candidate-source-cell a:hover {
  border-color: rgba(27,201,142,0.62);
  background: rgba(27,201,142,0.14);
  color: #a7f6d5;
}
.source-check {
  position: relative;
  display: inline-flex;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(27,201,142,0.65);
  border-radius: 50%;
  background: rgba(27,201,142,0.12);
}
.source-check::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 2px;
  width: 4px;
  height: 7px;
  border-right: 2px solid #7edfaf;
  border-bottom: 2px solid #7edfaf;
  transform: rotate(45deg);
}
.missing-field {
  color: #e0c07e;
  font-weight: 700;
}
.candidate-attempt-cell strong {
  color: #dfe4ee;
  font-size: 13pt;
}
.issue-pill {
  display: inline-flex;
  justify-content: center;
  width: max-content;
  max-width: 100%;
  padding: 3px 8px;
  border: 1px solid rgba(224,192,126,0.44);
  border-radius: 999px;
  color: #e0c07e;
  font-size: 8pt;
  font-weight: 800;
  line-height: 1.15;
}
.issue-pill-blocker {
  border-color: rgba(201,94,113,0.52);
  color: #f3a3b1;
  background: rgba(201,94,113,0.1);
}
.issue-pill-missing {
  border-color: rgba(224,192,126,0.5);
  color: #f0cf86;
  background: rgba(224,192,126,0.1);
}
.candidate-issue-note {
  color: #8f98aa !important;
  font-size: 7.5pt !important;
  font-weight: 700;
}
.candidate-issue-note.note-blocker {
  color: #e894a3 !important;
}

/* Smart-onboarding provenance badge */
.onboard-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  margin-top: 6px;
  padding: 2px 9px;
  border: 1px solid rgba(25,151,198,0.4);
  border-radius: 999px;
  background: rgba(25,151,198,0.1);
  color: #9bdff5;
  font-size: 8.5pt;
  font-weight: 700;
  letter-spacing: 0.2px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.onboard-badge .onboard-spark {
  width: 6px;
  height: 6px;
  overflow: hidden;
  border-radius: 50%;
  background: currentColor;
  color: inherit;
  font-size: 0;
  text-indent: -999px;
}
.onboard-badge.tier-platform-keystone { border-color: rgba(27,201,142,0.42); background: rgba(27,201,142,0.09); color: #8fe8c4; }
.onboard-badge.tier-subpath-keystone { border-color: rgba(25,151,198,0.45); background: rgba(25,151,198,0.1); color: #9bdff5; }
.onboard-badge.tier-variety { border-color: rgba(117,124,146,0.36); background: rgba(117,124,146,0.1); color: #b7c2d6; }
.onboard-badge.tier-request { border-color: rgba(196,167,245,0.4); background: rgba(196,167,245,0.08); color: #d6c4f8; }

.onboard-banner {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 10px;
  padding: 9px 12px;
  border: 1px solid rgba(25,151,198,0.42);
  border-left-width: 3px;
  border-radius: 8px;
  background: rgba(25,151,198,0.08);
}
.onboard-banner.tier-platform-keystone { border-color: rgba(27,201,142,0.34); border-left-color: #1bc98e; background: rgba(27,201,142,0.07); }
.onboard-banner.tier-subpath-keystone { border-color: rgba(25,151,198,0.38); border-left-color: #1997c6; background: rgba(25,151,198,0.08); }
.onboard-banner.tier-variety { border-color: rgba(117,124,146,0.34); border-left-color: #7f889a; background: rgba(117,124,146,0.08); }
.onboard-banner .onboard-spark {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  overflow: hidden;
  border-radius: 50%;
  background: #7bccee;
  color: transparent;
  font-size: 0;
  text-indent: -999px;
}
.onboard-banner.tier-platform-keystone .onboard-spark { background: #1bc98e; }
.onboard-banner.tier-subpath-keystone .onboard-spark { background: #1997c6; }
.onboard-banner.tier-variety .onboard-spark { background: #8f98aa; }
.onboard-banner.tier-request .onboard-spark { background: #c4a7f5; }
.onboard-banner-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.onboard-banner-main strong { color: #e7ebf3; font-size: 10pt; font-weight: 700; }
.onboard-banner-main span { color: #9aa3b4; font-size: 9pt; line-height: 1.4; }
.issue-pill-clean {
  border-color: rgba(27,201,142,0.38);
  color: #8fe8c4;
  background: rgba(27,201,142,0.08);
}

.type-badge { font-size: 8pt; text-transform: uppercase; letter-spacing: 1px; padding: 2px 6px; border-radius: 4px; background: rgba(25,151,198,0.14); color: #7fc4e0; }
.status-pill { font-size: 8.5pt; text-transform: uppercase; letter-spacing: .5px; padding: 2px 8px; border-radius: 10px; background: #3a3f4d; color: #b2b3b4; }
.st-needs_review { background: #4d3a1f; color: #e0c07e; }
.st-validated { background: rgba(27,201,142,0.18); color: #5ff0c0; box-shadow: inset 0 0 0 1px rgba(27,201,142,0.55); }
.st-discovered { background: #1d3a52; color: #6fc3ff; }
.st-scrape_failed { background: #5a2230; color: #f3a3b1; }
.st-duplicate { background: #3a3f4d; color: #b2b3b4; }
.st-imported { background: #1f4d3a; color: #7ee0b0; }
.st-rejected { background: #4a2a30; color: #d99aa6; }

.cand-blockers { color: #f3a3b1; }

.empty { color: #6b7180; text-align: center; grid-column: 1 / -1; padding: 30px 0; }

/* Drawer */
.cand-drawer { position: fixed; inset: 0; background: rgba(0,0,0,.6); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.cand-panel { background: #1b1e25; border: 1px solid #434857; border-radius: 8px; width: 820px; max-width: 94vw; max-height: 88vh; display: flex; flex-direction: column; }
.panel-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #434857; }
.panel-head > div { display: flex; align-items: center; gap: 10px; min-width: 0; }
.panel-title { color: #e7e9ee; font-size: 13pt; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.panel-head .close { cursor: pointer; color: #9198aa; font-size: 14pt; }
.panel-body { flex: 1; overflow: auto; padding: 4px 16px 12px; }

.block { padding: 14px 0; border-bottom: 1px solid #2f333d; }
.block:last-child { border-bottom: none; }
.block h5 { color: #9198aa; font-size: 9pt; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 10px; }
.muted { color: #6b7180; font-size: 9.5pt; }

.scrape { display: flex; gap: 14px; }
.scrape-thumb { flex: 0 0 96px; width: 96px; height: 96px; border-radius: 6px; background: #fff center / contain no-repeat; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4); }
.scrape-info { min-width: 0; flex: 1; }
.scrape-title { color: #cfd2da; font-size: 10.5pt; line-height: 1.35; }
.scrape-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin: 8px 0; font-size: 10pt; color: #cfd2da; }
.scrape-row strong { color: #e4d836; font-size: 12pt; }
.amzn-link { color: #1997c6; font-size: 9.5pt; }
.scrape-price {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 1px 8px;
  border: 1px solid rgba(27,201,142,0.42);
  border-radius: 999px;
  background: rgba(27,201,142,0.1);
  color: #8fe8c4;
}
.scrape-price.price-missing strong {
  color: #e0c07e;
  font-size: 10pt;
}
.price-refresh-btn {
  min-height: 24px;
  padding: 2px 9px;
  border: 1px solid rgba(25,151,198,0.55);
  border-radius: 999px;
  background: rgba(25,151,198,0.08);
  color: #9bdff5;
  font-size: 8.5pt;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s;
}
.price-refresh-btn:hover:not(:disabled) {
  border-color: #1997c6;
  background: rgba(25,151,198,0.16);
  color: #eef8fc;
}
.price-refresh-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.price-refresh-reopen {
  border-color: rgba(224,192,126,0.48);
  color: #ead08a;
  background: rgba(77,58,31,0.2);
}
.price-refresh-message {
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
  padding: 5px 8px;
  border: 1px solid rgba(117,124,146,0.34);
  border-radius: 6px;
  background: rgba(8,11,17,0.42);
  color: #cfd2da;
  font-size: 9pt;
}
.price-refresh-message--success {
  border-color: rgba(27,201,142,0.45);
  background: rgba(27,201,142,0.1);
  color: #8fe8c4;
}
.price-refresh-message--warning {
  border-color: rgba(224,192,126,0.42);
  background: rgba(77,58,31,0.2);
  color: #e0c07e;
}
.price-refresh-message--error {
  border-color: rgba(168,54,74,0.48);
  background: rgba(168,54,74,0.14);
  color: #f3a3b1;
}
.price-refresh-empty {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.bullets { margin: 6px 0 0; padding-left: 18px; color: #9198aa; font-size: 9.5pt; line-height: 1.45; }

.source-card {
  display: grid;
  gap: 6px;
  padding: 12px;
  border: 1px solid rgba(27,201,142,0.34);
  border-radius: 7px;
  background: rgba(27,201,142,0.08);
}

.source-kicker {
  color: #7edfaf;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.source-card a {
  color: #cfeef8;
  font-size: 10pt;
  line-height: 1.35;
  word-break: break-word;
}

.source-card p {
  margin: 0;
  color: #8f98aa;
  font-size: 9pt;
  line-height: 1.4;
}

.reason { color: #cfd2da; font-size: 10pt; line-height: 1.5; margin: 0; }
.issues { margin: 0; padding-left: 18px; color: #e0c07e; font-size: 9.5pt; line-height: 1.5; }
.issues li.issue-blocker {
  color: #f3a3b1;
  font-weight: 600;
  list-style: none;
  margin-left: -18px;
  padding: 3px 8px 3px 22px;
  position: relative;
  border-left: 2px solid #a8364a;
  background: rgba(168,54,74,0.12);
  border-radius: 0 4px 4px 0;
  margin-bottom: 3px;
}
.issues li.issue-blocker::before {
  content: '⛔';
  position: absolute;
  left: 4px;
  font-size: 8.5pt;
}
.count-bad { background: #5a2230; color: #f3a3b1; border-radius: 8px; padding: 1px 7px; font-size: 8pt; }
.count-block { background: #a8364a; color: #fff; border-radius: 8px; padding: 1px 7px; font-size: 8pt; margin-left: 6px; }
.count-warn { background: rgba(224,192,126,0.18); color: #e0c07e; border-radius: 8px; padding: 1px 7px; font-size: 8pt; }
.count-est { background: rgba(25,151,198,0.18); color: #9bdff5; border-radius: 8px; padding: 1px 7px; font-size: 8pt; }

/* Pipeline / trace */
.pipeline-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; }
.pl-item { display: inline-flex; align-items: center; gap: 6px; font-size: 9.5pt; }
.pl-k { color: #6b7180; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.5px; }
.pl-v { color: #cfd2da; }
.pl-runlink { color: #1997c6; font-size: 9.5pt; cursor: pointer; margin-left: auto; }
.pl-runlink:hover { color: #16b4ed; text-decoration: underline; }

.single-stage-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}
.single-stage-btn {
  display: grid;
  gap: 5px;
  min-height: 70px;
  padding: 10px;
  border: 1px solid #434857;
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.56);
  color: #cfd2da;
  text-align: left;
  cursor: pointer;
  transition: 0.15s;
}
.single-stage-btn:hover:not(:disabled) {
  border-color: #1997c6;
  background: rgba(25,151,198,0.1);
}
.single-stage-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.single-stage-btn small {
  color: #7f889a;
  font-size: 8.5pt;
  line-height: 1.25;
}
.single-stage-import {
  border-color: rgba(27,201,142,0.42);
  color: #8fe8c4;
}
.single-stage-unavailable {
  border-color: rgba(224,192,126,0.44);
  background: rgba(77,58,31,0.14);
  color: #ead08a;
}
.run-card-compact {
  margin-top: 10px;
}

/* Image gallery */
.img-count { background: rgba(25,151,198,0.18); color: #7fc4e0; border-radius: 8px; padding: 1px 7px; font-size: 8pt; }
.img-gallery { display: flex; flex-wrap: wrap; gap: 8px; }
.img-cell {
  width: 92px;
  min-height: 104px;
  border-radius: 6px;
  color: #7f889a;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4);
  cursor: zoom-in;
  text-align: center;
  text-decoration: none;
  transition: 0.15s;
}
.img-preview {
  display: block;
  width: 92px;
  height: 78px;
  border-radius: 6px;
  background: #fff center / contain no-repeat;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4);
}
.img-cell small {
  display: block;
  margin-top: 5px;
  color: #737d91;
  font-size: 7.5pt;
  line-height: 1.15;
}
.img-probe {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.img-cell:hover .img-preview { box-shadow: 0 0 0 2px #1997c6; transform: translateY(-2px); }
.err-text { color: #f3a3b1; font-size: 9.5pt; line-height: 1.4; margin: 0; white-space: pre-wrap; }
.enrichment-error-block {
  border-color: rgba(168,54,74,0.38);
  background: linear-gradient(135deg, rgba(168,54,74,0.1), rgba(8,11,17,0.34));
}
.enrichment-error-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 10px 12px;
  border: 1px solid rgba(168,54,74,0.36);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.42);
}
.enrichment-error-card .icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(168,54,74,0.45);
  border-radius: 7px;
  color: #f3a3b1;
  background: rgba(168,54,74,0.16);
}
.enrichment-error-card strong {
  display: block;
  margin-bottom: 4px;
  color: #f3a3b1;
  font-size: 10pt;
}
.enrichment-error-card p {
  margin: 0;
  color: #f0bdc8;
  font-size: 9.3pt;
  line-height: 1.45;
  white-space: pre-wrap;
}

.spec-group { margin-bottom: 12px; }
.spec-group-label { color: #7fc4e0; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 5px; }
.spec-row { display: flex; gap: 12px; padding: 3px 0; border-bottom: 1px solid #23262e; font-size: 9.5pt; }
.spec-k { flex: 0 0 38%; color: #9198aa; overflow: hidden; text-overflow: ellipsis; }
.spec-v { flex: 1; color: #cfd2da; word-break: break-word; }

.notes { width: 100%; background: #1f232c; border: 1px solid #434857; color: #cfd2da; border-radius: 6px; padding: 8px 10px; resize: vertical; font-family: inherit; font-size: 9.5pt; outline: none; }
.notes:focus { border-color: #1997c6; }
.imported-ref { display: inline-block; margin-top: 6px; }

.panel-foot { display: flex; flex-wrap: wrap; gap: 8px; padding: 12px 16px; border-top: 1px solid #434857; }
.act { border: 1px solid #434857; background: #2b2f3a; color: #cfd2da; border-radius: 6px; padding: 7px 16px; cursor: pointer; font-size: 10pt; transition: 0.15s; }
.act:disabled { opacity: 0.5; cursor: default; }
.act-import { background: #1997c6; border-color: #1997c6; color: #fff; font-weight: 600; margin-right: auto; }
.act-import:hover:not(:disabled) { background: #16b4ed; }
.act-unavailable { border-color: rgba(224,192,126,0.52); color: #ead08a; background: rgba(77,58,31,0.16); }
.act-unavailable:hover:not(:disabled) { background: rgba(77,58,31,0.28); }
.act-retry:hover:not(:disabled) { border-color: #6fc3ff; color: #cfeef8; }
.act-dup:hover:not(:disabled) { border-color: #9198aa; }
.act-reject { color: #d99aa6; }
.act-reject:hover:not(:disabled) { border-color: #a8364a; background: rgba(168,54,74,0.15); }
.act-notes { color: #9198aa; }
.stage-send-control {
  display: inline-flex;
  align-items: stretch;
  gap: 6px;
}
.stage-send-control select {
  min-width: 190px;
  border: 1px solid rgba(67,72,87,0.88);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #cfd2da;
  padding: 0 10px;
  font-size: 9pt;
  outline: none;
}
.stage-send-control select:focus {
  border-color: rgba(25,151,198,0.72);
}

/* Candidate detail revision */
.cand-drawer {
  align-items: flex-start;
  padding: 16px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(5px);
}

.cand-panel {
  width: min(1240px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  background:
    linear-gradient(145deg, rgba(22, 27, 38, 0.98), rgba(12, 16, 24, 0.98));
  border-color: rgba(89, 101, 128, 0.72);
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.58);
}

.panel-head {
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px 14px;
  background: rgba(9, 13, 20, 0.42);
}

.panel-head > div.panel-title-wrap {
  display: grid;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.panel-kicker-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.candidate-id {
  color: #7f889a;
  font-size: 8.5pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.panel-title {
  margin: 0;
  color: #e7edf7;
  font-size: 18pt;
  font-weight: 700;
  line-height: 1.15;
  white-space: normal;
}

.panel-head .close {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(117, 124, 146, 0.32);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.54);
  color: #8f98aa;
  text-decoration: none;
  line-height: 1;
}

.panel-head .close:hover {
  border-color: rgba(25, 151, 198, 0.65);
  color: #dff7ff;
}

.panel-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 316px;
  gap: 12px;
  align-items: start;
  padding: 14px 18px 18px;
  background:
    radial-gradient(circle at 18% 0%, rgba(25, 151, 198, 0.08), transparent 28%),
    linear-gradient(180deg, rgba(8, 12, 19, 0.18), rgba(4, 7, 12, 0.1));
}

.panel-body::-webkit-scrollbar,
.issues::-webkit-scrollbar,
.generated-block::-webkit-scrollbar {
  width: 8px;
}

.panel-body::-webkit-scrollbar-thumb,
.issues::-webkit-scrollbar-thumb,
.generated-block::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.42);
}

.panel-body::-webkit-scrollbar-track,
.issues::-webkit-scrollbar-track,
.generated-block::-webkit-scrollbar-track {
  background: rgba(8, 11, 17, 0.42);
}

.block {
  margin: 0 0 10px;
  padding: 14px;
  border: 1px solid rgba(82, 94, 121, 0.82);
  border-radius: 8px;
  background:
    linear-gradient(145deg, rgba(15, 21, 32, 0.72), rgba(7, 10, 16, 0.62));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.035),
    0 12px 34px rgba(0, 0, 0, 0.18);
}

.block h5,
.section-kicker {
  color: #20bff3;
  font-size: 8.5pt;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.block-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.block-title-row h5 {
  margin: 0;
}

.verify-diff-block {
  border-color: rgba(25, 151, 198, 0.34);
}

.diff-count {
  padding: 3px 8px;
  border: 1px solid rgba(25, 151, 198, 0.38);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.1);
  color: #9bdff5;
  font-size: 8pt;
  font-weight: 900;
  white-space: nowrap;
}

.verify-diff-list {
  display: grid;
  gap: 7px;
}

.verify-diff-row {
  display: grid;
  grid-template-columns: minmax(170px, 0.36fr) minmax(0, 1fr);
  gap: 8px 12px;
  align-items: center;
  padding: 9px 10px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.38);
}

.verify-diff-row.nuked {
  border-color: rgba(224, 192, 126, 0.34);
  background: rgba(77, 58, 31, 0.1);
}

.diff-path {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.diff-path span {
  color: #dfe5ef;
  font-size: 9pt;
  font-weight: 900;
  word-break: break-word;
}

.diff-path strong {
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(117, 124, 146, 0.18);
  color: #aeb6c8;
  font-size: 7.4pt;
  font-weight: 900;
  text-transform: uppercase;
}

.verify-diff-row.nuked .diff-path strong {
  background: rgba(224, 192, 126, 0.16);
  color: #e0c07e;
}

.diff-values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.diff-before,
.diff-after {
  display: block;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid rgba(67, 72, 87, 0.6);
  border-radius: 6px;
  color: #aab3c4;
  font-size: 8.8pt;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.diff-after {
  border-color: rgba(25, 151, 198, 0.34);
  color: #dfe5ef;
}

.verify-diff-row.nuked .diff-after {
  border-color: rgba(224, 192, 126, 0.38);
  color: #e0c07e;
}

.diff-arrow {
  color: #20bff3;
  font-size: 14pt;
  line-height: 1;
}

.verify-diff-row p {
  grid-column: 2;
  margin: 0;
  color: #8f98aa;
  font-size: 8.7pt;
  line-height: 1.4;
}

.diff-overflow {
  display: block;
  margin-top: 8px;
  color: #7f889a;
}

.candidate-overview-block {
  grid-column: 1;
  grid-row: 1;
  margin: 0;
  padding: 16px;
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.12), transparent 34%),
    linear-gradient(145deg, rgba(16, 23, 35, 0.82), rgba(7, 10, 16, 0.7));
}

.candidate-overview {
  display: grid;
  grid-template-columns: 108px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.candidate-media {
  min-width: 0;
}

.scrape-thumb {
  width: 108px;
  height: 108px;
  border-radius: 8px;
  background-color: #f3f5f8;
  box-shadow:
    inset 0 0 24px rgba(7, 11, 18, 0.14),
    0 10px 24px rgba(0, 0, 0, 0.24);
}

.scrape-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667289;
  font-size: 26px;
}

.candidate-overview-main {
  min-width: 0;
}

.candidate-overview-main h4 {
  display: -webkit-box;
  margin: 6px 0 7px;
  overflow: hidden;
  color: #eef3fa;
  font-size: 14pt;
  font-weight: 700;
  line-height: 1.28;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.candidate-meta-line {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  color: #8f98aa;
  font-size: 9.5pt;
}

.availability-note {
  color: #e0c07e;
}

.scrape-outcome-card {
  display: grid;
  gap: 4px;
  margin-top: 10px;
  padding: 9px 10px;
  border: 1px solid rgba(117, 124, 146, 0.36);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.34);
}

.scrape-outcome-card summary,
.scrape-details summary,
.evidence-disclosure > summary {
  list-style: none;
}

.scrape-outcome-card summary::-webkit-details-marker,
.scrape-details summary::-webkit-details-marker,
.evidence-disclosure > summary::-webkit-details-marker {
  display: none;
}

.scrape-outcome-card summary {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.scrape-outcome-card summary::after,
.scrape-details summary::after,
.evidence-disclosure > summary::after {
  color: #748095;
  content: '+';
  font-size: 12pt;
  font-weight: 500;
}

.scrape-outcome-card[open] summary::after,
.scrape-details[open] summary::after,
.evidence-disclosure[open] > summary::after {
  content: '\2212';
}

.scrape-outcome-card > p {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(117, 124, 146, 0.2);
}

.scrape-details {
  margin-top: 11px;
}

.scrape-details > summary {
  display: flex;
  gap: 8px;
  align-items: center;
  width: max-content;
  max-width: 100%;
  color: #8f9aac;
  font-size: 8.5pt;
  font-weight: 800;
  cursor: pointer;
}

.scrape-details > summary span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: rgba(117, 124, 146, 0.14);
  color: #aeb7c7;
  font-size: 7.5pt;
}

.scrape-details > summary::after {
  margin-left: 2px;
  font-size: 10pt;
}

.scrape-outcome-card.tone-warn {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(77, 58, 31, 0.14);
}

.scrape-outcome-card.tone-bad {
  border-color: rgba(232, 79, 106, 0.44);
  background: rgba(90, 34, 48, 0.16);
}

.scrape-outcome-label {
  color: #dfe5ef;
  font-size: 8.4pt;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.scrape-outcome-card.tone-warn .scrape-outcome-label { color: #e0c07e; }
.scrape-outcome-card.tone-bad .scrape-outcome-label { color: #f0a1af; }

.scrape-outcome-card p,
.scrape-outcome-card small {
  margin: 0;
  color: #aab3c4;
  font-size: 8.7pt;
  line-height: 1.4;
}

.scrape-outcome-card small {
  color: #7f889a;
}

.candidate-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 13px 0 8px;
}

.candidate-chip {
  min-width: 92px;
  padding: 7px 9px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.44);
}

.candidate-chip small,
.price-card-label {
  display: block;
  color: #8a94a7;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.candidate-chip strong {
  display: block;
  margin-top: 3px;
  color: #dfe5ef;
  font-size: 10pt;
  line-height: 1.15;
}

.candidate-price-card {
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background:
    linear-gradient(145deg, rgba(18, 25, 37, 0.86), rgba(8, 11, 17, 0.78));
}

.price-card-value {
  color: #e4d836;
  font-size: 21pt;
  line-height: 1;
  font-weight: 700;
}

.price-card-value.price-missing {
  color: #e0c07e;
  font-size: 14pt;
  line-height: 1.2;
}

.price-card-note {
  color: #7f889a;
  font-size: 8.8pt;
  line-height: 1.35;
}

.price-card-actions {
  display: grid;
  gap: 8px;
}

.price-manual-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: end;
}

.price-manual-form label {
  display: grid;
  gap: 5px;
  margin: 0;
}

.price-manual-form label span {
  color: #8a94a7;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.price-manual-form input {
  height: 32px;
  min-width: 0;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.72);
  color: #e4d836;
  font-size: 11pt;
  font-weight: 800;
  padding: 0 10px;
  outline: none;
}

.price-manual-form input:focus {
  border-color: #1997c6;
  box-shadow: 0 0 0 2px rgba(25,151,198,0.1);
}

.price-save-btn {
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(27,201,142,0.45);
  border-radius: 6px;
  background: rgba(27,201,142,0.1);
  color: #8fe8c4;
  font-size: 8.5pt;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s;
}

.price-save-btn:hover:not(:disabled) {
  border-color: rgba(27,201,142,0.78);
  background: rgba(27,201,142,0.18);
  color: #e9fff6;
}

.price-save-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.candidate-price-card .price-refresh-btn,
.candidate-price-card .amzn-link {
  min-height: 30px;
  align-content: center;
  justify-content: center;
  border-radius: 6px;
  text-align: center;
  text-decoration: none;
}

.candidate-price-card .amzn-link {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(25, 151, 198, 0.55);
  background: rgba(25, 151, 198, 0.08);
  color: #9bdff5;
  font-weight: 800;
}

.bullets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 22px;
  max-height: none;
  overflow: visible;
  margin-top: 10px;
  padding-left: 18px;
  color: #b9c1cf;
}

.candidate-summary-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.9fr);
  gap: 12px;
  margin-bottom: 12px;
}

.candidate-review-workspace {
  display: contents;
}

.candidate-review-main,
.candidate-review-sidebar {
  min-width: 0;
}

.candidate-review-main {
  grid-column: 1;
  grid-row: 2;
}

.candidate-review-sidebar {
  grid-column: 2;
  grid-row: 1 / span 2;
  position: static;
  align-self: start;
  height: max-content;
  display: grid;
  gap: 10px;
}

.candidate-review-sidebar .candidate-price-card {
  margin: 0;
}

.candidate-review-sidebar .candidate-summary-grid {
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 0;
}

.candidate-review-sidebar .block {
  margin: 0;
  padding: 12px;
}

.candidate-review-sidebar .block h5 {
  margin-bottom: 8px;
  color: #8792a5;
}

.candidate-review-sidebar .source-card {
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
}

.candidate-review-sidebar .pipeline-meta {
  display: grid;
  gap: 0;
}

.candidate-review-sidebar .pl-item {
  justify-content: space-between;
  padding: 7px 0;
  border: 0;
  border-bottom: 1px solid rgba(67, 72, 87, 0.4);
  border-radius: 0;
  background: transparent;
}

.candidate-review-sidebar .pl-runlink {
  padding-top: 8px;
  font-size: 8.5pt;
}

.candidate-review-sidebar .single-stage-actions {
  grid-template-columns: 1fr;
  gap: 6px;
}

.candidate-review-sidebar .single-stage-btn {
  min-height: 48px;
}

.candidate-review-sidebar .run-card-compact {
  grid-template-columns: auto minmax(0, 1fr);
}

.candidate-review-sidebar .run-summary {
  grid-column: 2;
  justify-self: start;
}

.candidate-summary-grid .block {
  margin: 0;
}

.image-block,
.reason-block,
.evaluation-health-block,
.generated-block {
  background:
    radial-gradient(circle at 10% 0%, rgba(25, 151, 198, 0.06), transparent 32%),
    linear-gradient(145deg, rgba(14, 20, 31, 0.78), rgba(7, 10, 16, 0.66));
}

.evaluation-health-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.evaluation-health-head h5,
.evaluation-health-head p {
  margin: 0;
}

.evaluation-health-head p {
  margin-top: 4px;
  color: #8f98aa;
  font-size: 9pt;
  line-height: 1.35;
}

.evaluation-health-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 7px;
}

.evaluation-health-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-height: 26px;
  padding: 0 9px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 999px;
  color: #aeb6c8;
  background: rgba(8, 11, 17, 0.38);
  font-size: 8.1pt;
  font-weight: 800;
  text-transform: uppercase;
}

.evaluation-health-badge strong {
  color: #dfe5ef;
  font-size: 10pt;
}

.evaluation-health-badge.health-blocker {
  border-color: rgba(168,54,74,0.5);
  color: #f3a3b1;
  background: rgba(168,54,74,0.12);
}

.evaluation-health-badge.health-warning {
  border-color: rgba(224,192,126,0.46);
  color: #e0c07e;
  background: rgba(77,58,31,0.16);
}

.evaluation-health-badge.health-estimated {
  border-color: rgba(25,151,198,0.46);
  color: #9bdff5;
  background: rgba(25,151,198,0.1);
}

.evaluation-health-badge.health-ok {
  border-color: rgba(117,124,146,0.34);
  color: #aeb6c8;
}

.reason-block .reason {
  padding: 10px 12px;
  border: 1px solid rgba(67, 72, 87, 0.58);
  border-radius: 7px;
  background: rgba(4, 7, 12, 0.28);
}

.source-card {
  border-color: rgba(27, 201, 142, 0.3);
  background: rgba(27, 201, 142, 0.07);
}

.pipeline-meta {
  gap: 10px 16px;
}

.pl-item {
  padding: 6px 8px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.3);
}

.pl-runlink {
  align-self: center;
  margin-left: 0;
}

.action-block {
  padding: 12px 14px;
  background: rgba(8, 11, 17, 0.3);
}

.single-stage-actions {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.single-stage-btn {
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 9px;
  align-items: center;
  min-height: 54px;
  padding: 8px 10px;
}

.single-stage-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(117, 124, 146, 0.34);
  border-radius: 7px;
  background: rgba(117, 124, 146, 0.08);
  color: #7f8a9e;
  font-size: 7.5pt;
  font-weight: 900;
}

.single-stage-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.single-stage-copy strong {
  color: #dfe5ef;
  font-size: 9.5pt;
}

.single-stage-import .single-stage-index {
  border-color: rgba(27, 201, 142, 0.4);
  color: #8fe8c4;
  background: rgba(27, 201, 142, 0.08);
}

.single-stage-import {
  background: rgba(27, 201, 142, 0.08);
}

.single-stage-unavailable {
  background: rgba(77, 58, 31, 0.12);
}

.img-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 9px;
}

.img-cell {
  width: 100%;
  min-width: 0;
  min-height: 98px;
  background-color: transparent;
  box-shadow:
    0 8px 18px rgba(0, 0, 0, 0.2);
}

.img-preview {
  width: 100%;
  height: 72px;
}

.candidate-evidence-grid {
  display: grid;
  grid-column: 1 / -1;
  grid-row: 3;
  grid-template-columns: minmax(0, 1.65fr) minmax(260px, 0.75fr);
  gap: 10px;
  margin-bottom: 10px;
}

.review-notes-block {
  grid-column: 1 / -1;
  grid-row: 4;
  width: 100%;
}

.candidate-evidence-grid .block {
  min-width: 0;
  height: 100%;
  margin: 0;
}

.candidate-evidence-grid .block:only-child {
  grid-column: 1 / -1;
}

.evidence-disclosure {
  padding: 0 !important;
  overflow: hidden;
}

.evidence-disclosure > summary {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  color: #aab4c5;
  font-size: 8.5pt;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
}

.evidence-disclosure > summary:hover {
  background: rgba(25, 151, 198, 0.05);
  color: #dce5f1;
}

.evidence-disclosure > summary > small {
  margin-left: auto;
  color: #6f7a8e;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.evidence-disclosure > .img-gallery,
.evidence-disclosure > .reason {
  margin: 0 14px 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(67, 72, 87, 0.42);
}

.issues {
  display: grid;
  gap: 8px;
  max-height: 330px;
  overflow: auto;
  padding: 1px 4px 1px 0;
  color: inherit;
}

.issue-accordion {
  padding: 0;
  overflow: hidden;
  border-color: rgba(92, 81, 61, 0.82);
  background:
    linear-gradient(145deg, rgba(17, 16, 20, 0.8), rgba(7, 10, 16, 0.66));
}

.issue-summary-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  background:
    linear-gradient(135deg, rgba(77, 58, 31, 0.2), rgba(8, 11, 17, 0.34));
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.issue-summary-line:hover {
  background:
    linear-gradient(135deg, rgba(77, 58, 31, 0.2), rgba(25, 151, 198, 0.08));
}

.issue-summary-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.issue-summary-kicker {
  color: #20bff3;
  font-size: 8pt;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.issue-summary-copy strong {
  color: #e2e7f0;
  font-size: 10.5pt;
}

.issue-summary-copy small {
  overflow: hidden;
  color: #8f98aa;
  font-size: 8.7pt;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.issue-summary-badges {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.issue-summary-badges .count-block {
  margin-left: 0;
}

.issue-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid rgba(67,72,87,0.75);
  border-radius: 6px;
  color: #9bdff5;
  font-size: 16px;
  transform: rotate(90deg);
  transition: 0.16s;
}

.issue-chevron.open {
  transform: rotate(-90deg);
}

.issue-accordion-body {
  padding: 12px 14px 14px;
  border-top: 1px solid rgba(67, 72, 87, 0.54);
}

.issue-detail-toolbar {
  margin-bottom: 10px;
}

.issue-row {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  padding: 9px 10px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.34);
}
.issue-content {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.issue-kind {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  border-radius: 999px;
  font-size: 8pt;
  font-weight: 900;
  text-transform: uppercase;
}

.issue-row p {
  margin: 0;
  color: #cfd6e3;
  font-size: 9.2pt;
  line-height: 1.45;
  word-break: break-word;
}

.issue-field-link {
  width: max-content;
  max-width: 100%;
  padding: 3px 7px;
  border: 1px solid rgba(25,151,198,0.38);
  border-radius: 999px;
  background: rgba(25,151,198,0.08);
  color: #9bdff5;
  font-size: 8pt;
  font-weight: 900;
  text-align: left;
  cursor: pointer;
}

.issue-field-link:hover {
  border-color: rgba(25,151,198,0.7);
  color: #dff7ff;
}

.finding-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 6px;
}

.finding-facts span {
  display: grid;
  gap: 2px;
  padding: 7px 8px;
  border: 1px solid rgba(67,72,87,0.58);
  border-radius: 7px;
  background: rgba(4,7,12,0.28);
  color: #cfd6e3;
  font-size: 8.8pt;
  line-height: 1.28;
  word-break: break-word;
}

.finding-facts small {
  color: #7f889a;
  font-size: 7.3pt;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.issue-action {
  min-height: 28px;
  padding: 5px 9px;
  border: 1px solid rgba(67, 72, 87, 0.85);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.48);
  color: #aeb6c8;
  font-size: 8.2pt;
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.15s;
}
.issue-action:hover:not(:disabled) {
  border-color: rgba(25,151,198,0.72);
  color: #dff7ff;
  background: rgba(25,151,198,0.12);
}
.issue-action:disabled {
  opacity: 0.55;
  cursor: default;
}
.issue-action.research {
  align-self: start;
  color: #9bdff5;
}
.issue-action.apply {
  border-color: rgba(27,201,142,0.52);
  color: #8fe8c4;
}
.issue-action.ok {
  border-color: rgba(27,201,142,0.5);
  color: #9df0cc;
  background: rgba(27,201,142,0.1);
}
.issue-research-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.issue-research-state {
  display: grid;
  gap: 4px;
  padding: 8px 10px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.42);
}
.issue-research-state strong {
  color: #dfe4ee;
  font-size: 8.5pt;
}
.issue-research-state span {
  color: #8f98aa;
  font-size: 8.3pt;
}
.issue-research-state code {
  width: max-content;
  max-width: 100%;
  padding: 3px 7px;
  border-radius: 5px;
  background: rgba(25,151,198,0.12);
  color: #aeeaff;
  font-size: 8.3pt;
  white-space: normal;
}
.issue-research-state.state-good {
  border-color: rgba(27,201,142,0.42);
  background: rgba(27,201,142,0.07);
}
.issue-research-state.state-warn {
  border-color: rgba(224,192,126,0.38);
  background: rgba(77,58,31,0.13);
}
.issue-research-state.state-bad {
  border-color: rgba(168,54,74,0.52);
  background: rgba(168,54,74,0.12);
}

.resolver-error-list {
  display: grid;
  gap: 4px;
  margin-top: 5px;
}

.resolver-error-list span {
  display: block;
  padding: 5px 7px;
  border: 1px solid rgba(232, 79, 106, 0.34);
  border-radius: 5px;
  background: rgba(90, 34, 48, 0.28);
  color: #f3b0bc;
}

.issue-blocker {
  border-color: rgba(168, 54, 74, 0.58);
  background: rgba(168, 54, 74, 0.11);
}

.issue-blocker .issue-kind {
  background: rgba(168, 54, 74, 0.22);
  color: #f3a3b1;
}

.issue-warning {
  border-color: rgba(224, 192, 126, 0.4);
  background: rgba(77, 58, 31, 0.13);
}

.issue-warning .issue-kind {
  background: rgba(224, 192, 126, 0.14);
  color: #e0c07e;
}

.issue-estimated {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.08);
}

.issue-estimated .issue-kind {
  background: rgba(25, 151, 198, 0.14);
  color: #9bdff5;
}

.issue-ok .issue-kind {
  background: rgba(117, 124, 146, 0.16);
  color: #aeb6c8;
}

.issue-source .issue-kind {
  background: rgba(25, 151, 198, 0.14);
  color: #87d7f4;
}

.issue-note .issue-kind {
  background: rgba(117, 124, 146, 0.16);
  color: #aeb6c8;
}

.generated-block {
  max-height: none;
  overflow: visible;
  padding: 18px;
  border-color: rgba(25, 151, 198, 0.42);
  background:
    radial-gradient(circle at 4% 0%, rgba(25, 151, 198, 0.1), transparent 28%),
    linear-gradient(145deg, rgba(14, 21, 32, 0.88), rgba(6, 10, 17, 0.76));
}

.generated-title-row {
  align-items: flex-start;
  margin-bottom: 12px;
}

.generated-title-row p {
  margin: 4px 0 0;
  color: #8f98aa;
  font-size: 9pt;
  line-height: 1.35;
}

.dirty-pill {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 9px;
  border: 1px solid rgba(224, 192, 126, 0.42);
  border-radius: 999px;
  background: rgba(77, 58, 31, 0.14);
  color: #e0c07e;
  font-size: 8pt;
  font-weight: 900;
  text-transform: uppercase;
}

.spec-groups,
.spec-form-groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.spec-form-group-general {
  grid-column: 1 / -1;
}

.spec-field-list {
  display: grid;
  gap: 9px 12px;
}

.spec-form-group-general .spec-field-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.spec-form-group-general .spec-field.is-multiline {
  grid-column: 1 / -1;
}

.spec-group,
.spec-form-group {
  margin: 0;
  min-width: 0;
  padding: 12px;
  border: 1px solid rgba(82, 94, 121, 0.78);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(11, 16, 25, 0.88), rgba(5, 8, 14, 0.76));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.035),
    0 10px 24px rgba(0, 0, 0, 0.14);
}

.spec-group-label {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 11px;
  color: #20bff3;
  font-weight: 900;
}

.spec-group-label small {
  color: #738096;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.spec-row {
  display: grid;
  grid-template-columns: minmax(94px, 0.55fr) minmax(0, 1fr);
  gap: 12px;
  padding: 6px 0;
}

.spec-k {
  flex: none;
}

.spec-v {
  min-width: 0;
}

.spec-field {
  display: grid;
  gap: 5px;
  align-content: start;
  min-width: 0;
  margin: 0;
}

.spec-field-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.spec-field-name {
  min-width: 0;
  color: #99a4b8;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-meta-bar {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 0;
}

.field-status-chip {
  padding: 2px 6px;
  border: 1px solid rgba(67,72,87,0.62);
  border-radius: 999px;
  background: rgba(8,11,17,0.38);
  color: #aeb6c8;
  font-size: 7.1pt;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

.field-status-chip-blocker {
  border-color: rgba(168,54,74,0.58);
  color: #f3a3b1;
  background: rgba(168,54,74,0.14);
}

.field-status-chip-warning {
  border-color: rgba(224,192,126,0.5);
  color: #e0c07e;
  background: rgba(77,58,31,0.16);
}

.field-status-chip-estimated {
  border-color: rgba(25,151,198,0.48);
  color: #9bdff5;
  background: rgba(25,151,198,0.12);
}

.field-status-chip-ok {
  border-color: rgba(117,124,146,0.28);
  color: #8f98aa;
}

.field-meta-summary {
  max-width: 180px;
  color: #7f889a;
  font-size: 7.4pt;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-meta-link {
  color: #1fb2e8;
  font-size: 7.2pt;
  font-weight: 900;
  text-decoration: none;
  text-transform: uppercase;
}

.field-meta-link:hover {
  color: #dff7ff;
}

.field-tier-badge {
  display: inline-flex;
  align-items: center;
  min-height: 18px;
  padding: 0 6px;
  border: 1px solid rgba(117, 124, 146, 0.36);
  border-radius: 999px;
  background: rgba(117, 124, 146, 0.12);
  color: #aeb6c8;
  font-size: 7.2pt;
  font-weight: 900;
  text-transform: uppercase;
}

.field-tier-high {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #8fe8c4;
}

.field-tier-mid {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(77, 58, 31, 0.16);
  color: #e0c07e;
}

.field-tier-low {
  border-color: rgba(232, 79, 106, 0.42);
  background: rgba(90, 34, 48, 0.2);
  color: #f3aab6;
}

.field-tier-neutral {
  border-color: rgba(117, 124, 146, 0.36);
  background: rgba(117, 124, 146, 0.12);
  color: #aeb6c8;
}

.field-research-action {
  min-height: 20px;
  padding: 2px 6px;
  border: 1px solid rgba(25, 151, 198, 0.42);
  border-radius: 5px;
  background: rgba(25, 151, 198, 0.08);
  color: #9bdff5;
  font-size: 7.2pt;
  font-weight: 900;
  line-height: 1.1;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.14s;
}

.field-research-action:hover:not(:disabled) {
  border-color: rgba(32, 191, 243, 0.74);
  background: rgba(25, 151, 198, 0.16);
  color: #dff7ff;
}

.field-research-action:disabled {
  opacity: 0.55;
  cursor: default;
}

.spec-field:not(.missing):not(.field-status-blocker):not(.field-status-warning) .field-research-action {
  opacity: 0;
  transform: translateY(2px);
}

.spec-field:hover .field-research-action,
.spec-field:focus-within .field-research-action,
.spec-field .field-research-action:focus {
  opacity: 1;
  transform: translateY(0);
}

.spec-field input,
.spec-field textarea {
  width: 100%;
  border: 1px solid rgba(67, 72, 87, 0.88);
  border-radius: 6px;
  background: rgba(4, 7, 12, 0.72);
  color: #dfe5ef;
  font-family: inherit;
  font-size: 9pt;
  line-height: 1.35;
  outline: none;
  transition: 0.14s;
}

.spec-field input {
  height: 34px;
  padding: 0 9px;
}

.spec-field textarea {
  min-height: 76px;
  padding: 8px 9px;
  resize: vertical;
}

.spec-field input:focus,
.spec-field textarea:focus {
  border-color: rgba(25,151,198,0.74);
  box-shadow: 0 0 0 1px rgba(25,151,198,0.12);
}

.spec-field.missing input,
.spec-field.missing textarea {
  border-color: rgba(224,192,126,0.48);
  background: rgba(77,58,31,0.12);
}

.spec-field.field-status-blocker input,
.spec-field.field-status-blocker textarea {
  border-color: rgba(168,54,74,0.56);
  background: rgba(168,54,74,0.1);
}

.spec-field.field-status-warning input,
.spec-field.field-status-warning textarea {
  border-color: rgba(224,192,126,0.48);
  background: rgba(77,58,31,0.12);
}

.spec-field.field-status-estimated input,
.spec-field.field-status-estimated textarea {
  border-color: rgba(25,151,198,0.44);
  background: rgba(25,151,198,0.07);
}

.spec-field.missing .spec-field-name::after {
  content: " missing";
  margin-left: 6px;
  color: #e0c07e;
  font-weight: 900;
}

.field-research-state {
  margin-top: 2px;
  padding: 6px 8px;
}

.generated-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  position: sticky;
  bottom: -18px;
  z-index: 3;
  margin: 14px -18px -18px;
  padding: 12px 18px;
  border-top: 1px solid rgba(67, 72, 87, 0.54);
  border-radius: 0 0 8px 8px;
  background: rgba(8, 12, 19, 0.96);
  box-shadow: 0 -12px 28px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
}

.spec-save-state {
  margin-right: auto;
  padding: 7px 10px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.38);
  color: #aeb6c8;
  font-size: 8.8pt;
}

.spec-save-state.state-good {
  border-color: rgba(27,201,142,0.42);
  background: rgba(27,201,142,0.08);
  color: #9df0cc;
}

.spec-save-state.state-bad {
  border-color: rgba(168,54,74,0.5);
  background: rgba(168,54,74,0.12);
  color: #f3a3b1;
}

.panel-foot {
  padding: 14px 22px;
  background: rgba(9, 13, 20, 0.54);
}

@media screen and (max-width: 640px) {
  .stage-workbench-head,
  .stage-actions-row {
    display: grid;
    width: 100%;
    min-width: 0;
    justify-content: stretch;
  }
  .stage-scope-card {
    grid-template-columns: 1fr;
  }
  .stage-scope-stat {
    justify-items: start;
  }
  .candidate-table-head {
    display: none;
  }
  .candidate-table-row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }
  .candidate-table-cell {
    grid-template-columns: 98px minmax(0, 1fr);
    align-items: center;
  }
  .candidate-table-cell::before {
    color: #6f788a;
    font-size: 8pt;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  .candidate-status-cell::before { content: "Status"; }
  .candidate-price-cell::before { content: "Price"; }
  .candidate-source-cell::before { content: "Source"; }
  .candidate-time-cell::before { content: "Added"; }
  .candidate-attempt-cell::before { content: "Attempts"; }
  .candidate-issue-cell::before { content: "Missing / Issues"; }
  .cand-drawer {
    padding: 10px;
  }
  .cand-panel {
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
  }
  .panel-head,
  .panel-body,
  .panel-foot {
    padding-left: 12px;
    padding-right: 12px;
  }
  .panel-body {
    display: block;
  }
  .candidate-review-workspace {
    display: flex;
    flex-direction: column;
  }
  .candidate-overview,
  .candidate-summary-grid,
  .candidate-evidence-grid,
  .spec-groups,
  .spec-form-groups {
    grid-template-columns: 1fr;
  }
  .candidate-review-main,
  .candidate-review-sidebar {
    grid-column: auto;
    grid-row: auto;
  }
  .candidate-review-main {
    order: 1;
  }
  .candidate-review-sidebar {
    position: static;
    order: 2;
  }
  .spec-form-group-general {
    grid-column: auto;
  }
  .spec-form-group-general .spec-field-list,
  .bullets {
    grid-template-columns: 1fr;
  }
  .verify-diff-row,
  .diff-values {
    grid-template-columns: 1fr;
  }
  .verify-diff-row p {
    grid-column: 1;
  }
  .diff-arrow {
    transform: rotate(90deg);
    justify-self: start;
  }
  .issue-row {
    grid-template-columns: 1fr;
  }
  .stage-tabs {
    grid-template-columns: 1fr;
  }
  .stage-options {
    align-items: stretch;
  }
  .stage-field,
  .stage-search-controls,
  .stage-run-btn,
  .discover-btn {
    width: 100%;
    margin-left: 0;
  }
  .single-stage-actions {
    grid-template-columns: 1fr;
  }
  .generated-actions {
    position: static;
    margin-bottom: -18px;
  }
  .scrape { flex-direction: column; }
  .panel-foot .act-import { margin-right: 0; }
}

@media screen and (min-width: 641px) and (max-width: 1180px) {
  .candidate-table-shell {
    overflow-x: auto;
  }
  .candidate-table-head,
  .candidate-table-row {
    min-width: 980px;
    grid-template-columns: minmax(280px, 2.2fr) 112px 140px 142px 104px 76px 128px;
  }
  .candidate-overview {
    grid-template-columns: 110px minmax(0, 1fr);
  }
  .panel-body {
    display: block;
  }
  .candidate-review-workspace {
    display: flex;
    flex-direction: column;
  }
  .candidate-review-main,
  .candidate-review-sidebar {
    grid-column: auto;
    grid-row: auto;
  }
  .candidate-review-main {
    order: 1;
  }
  .candidate-review-sidebar {
    position: static;
    order: 2;
  }
  .candidate-price-card {
    grid-column: 1 / -1;
  }
  .candidate-summary-grid,
  .candidate-evidence-grid,
  .spec-groups {
    grid-template-columns: 1fr;
  }
  .verify-diff-row {
    grid-template-columns: 1fr;
  }
  .verify-diff-row p {
    grid-column: 1;
  }
  .stage-tabs {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
  .single-stage-actions {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}
</style>
